import { createFileRoute } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import {
  Utensils, Landmark, Palette, Leaf, Moon, Sparkles, Music, Building2, Heart, ShoppingBag,
  Sunrise, Sun, Sunset, MapPin, GripVertical, Clock, ArrowUpRight, Info, Users, Globe,
  Coffee, Store, Eye, Compass, CheckCircle2, Timer, Star, DollarSign, ChevronDown, ChevronUp,
} from "lucide-react";
import { useRheaLoading } from "@/lib/ui/RheaLoadingContext";
import { RheaPrototypeHeader } from "@/components/RheaPrototypeHeader";
import { LazyPanelBoundary } from "@/components/LazyPanelBoundary";
import { track, buildProviderPageLoadedEvents } from "@/lib/analytics";
import { logMarketplaceEvent, initPageEngagementTracking } from "@/lib/marketplaceEventLog";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useRef, useEffect, useMemo, lazy, Suspense, type ReactNode } from "react";
import { generateExperienceSequence, fetchFallbackCandidates, getProviderDestinations, notifyWaitlist, wakeServer, parseIntent, clearColdStart, searchExperiences } from "@/lib/api";
import type { LLMParsedIntent } from "@/lib/api";
import type { ExperienceSearchParams, ExperienceSearchFacets, ExperienceSearchResponse } from "@/lib/types";
import { buildMarketplaceSearchParamsFromSequence, marketplaceFiltersKey } from "@/lib/experiences/marketplaceSearchParams";
import {
  appendUniqueExperienceItems, createLoadMoreActionContext, decideNextLoadMoreStep,
  hasWarmupBudgetRemaining, computeWarmupPollDelayMs,
  type LoadMoreActionContext, type ManualExitReason,
} from "@/lib/experiences/marketplaceContinuity";
import { isShowLocalReferencesEnabled } from "@/lib/experiences/launchFlags";
import { usePreferences, languageToMarketLocale } from "@/lib/hooks/usePreferences";
// Sprint 14 — IntentClarificationCard
import { IntentClarificationCard } from "@/components/experiences/IntentClarificationCard";
// Sprint 3C-A — Itinerary workspace components
// Launch Performance & Bundle Trim V1 — lazy: only rendered once the user
// manually opens "Ver mapa aproximado" (mapOpen defaults to false on both
// breakpoints), never part of first paint.
const ItineraryGeoMap = lazy(() =>
  import("@/components/experiences/itinerary/ItineraryGeoMap").then((m) => ({
    default: m.ItineraryGeoMap,
  })),
);
// Sprint 3C-B — Interactive itinerary editing (still used by the multi-city storytelling render)
import { SortableExperienceShell } from "@/components/experiences/itinerary/SortableExperienceShell";
import { makeCompositeId, makeDayDropId } from "@/components/experiences/itinerary/itineraryDraft";
import { useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
// Marketplace Inventory Layout V1
import { MarketplaceToolbar } from "@/components/experiences/marketplace/MarketplaceToolbar";
import { SelectionTray, MAX_COMPARISON_ITEMS, type TrayItem } from "@/components/experiences/marketplace/SelectionTray";
import { ExperienceMarketplaceRow } from "@/components/experiences/marketplace/ExperienceMarketplaceRow";
import { buildMarketplaceInventory, groupMarketplaceByIntent, pickThemedTeaser, getInventoryDepthCopy, getMarketplaceSource, getMarketplaceCounts, getCoverageTierBanner } from "@/components/experiences/marketplace/marketplaceInventory";
import { SelectionAction } from "@/components/experiences/marketplace/SelectionAction";
import { getRheaRecommendationRank, hasActivityIntentConflict } from "@/components/experiences/marketplace/rheaRecommendation";
import { ResultsHero } from "@/components/experiences/marketplace/ResultsHero";
import { FeaturedShelf } from "@/components/experiences/marketplace/FeaturedShelf";
// Launch Performance & Bundle Trim V1 — lazy, same established pattern as
// ProviderProductDetailDrawer below: both are Portal-rendered (Sheet/Radix
// Dialog) and invisible until their own `open`/`item` prop goes true, so
// there's no reserved layout space to preserve while the chunk loads.
const ExperienceDetailDrawer = lazy(() =>
  import("@/components/experiences/marketplace/ExperienceDetailDrawer").then((m) => ({
    default: m.ExperienceDetailDrawer,
  })),
);
const ComparisonDrawer = lazy(() =>
  import("@/components/experiences/marketplace/ComparisonDrawer").then((m) => ({
    default: m.ComparisonDrawer,
  })),
);
import { getAddToComparisonDecision } from "@/components/experiences/marketplace/comparisonDecision";
import { isMarketplaceEligibleProvider } from "@/components/experiences/marketplace/providerLinkState";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import type { MarketplaceDetailSurface } from "@/lib/analytics";
import type { MarketplaceItem } from "@/components/experiences/marketplace/marketplaceInventory";
import {
  computeDynamicMaxPrice, buildPriceChips, hasEnoughPricedItems,
  roundUpToCleanStep, PRICE_FILTER_UI_CAP,
  type PriceChip,
} from "@/components/experiences/marketplace/priceFilter";
import {
  applyFilters, sortExperiences, hasActiveFilters, countActiveFilters,
  EMPTY_FILTERS, buildFilterAppliedProps, buildSortChangedProps,
  type ActiveFilters, type DurationFilter, type SortBy,
} from "@/components/experiences/marketplace/marketplaceFilters";
import { buildCompareItemAddedProps } from "@/components/experiences/marketplace/detailAnalytics";
import { isUserFacingValidUrl } from "@/lib/experiences/linkIntegrity";
import { normalizeCityDisplayName } from "@/lib/experiences/cityDisplayNames";
import { DateFlexPicker } from "@/components/experiences/DateFlexPicker";
import type { DateSelection } from "@/components/experiences/DateFlexPicker";
import { useAuth } from "@/lib/auth/AuthContext";
// ── Type-only imports — erased at build time, zero bundle cost ────────────────
import type {
  ExperienceSequence,
  ExperienceDay,
  ExperienceItem,
  ExperienceSupplyOption,
  EvidenceContextItem,
  CityExperienceSegment,
  TransitionNarrative,
  FallbackResult,
  FallbackCandidate,
  IntentSnapshot,
  ProviderDestinationCandidate,
  ProviderDestinationDiscoveryResult,
} from "@/lib/types";
import type { MoodStyle } from "@/lib/experiences/moodMeta";
import type { ParsedQuery } from "@/lib/experiences/queryParser";
import type { DiscoveryState } from "@/lib/experiences/discovery";
import type { CatalogCity } from "@/lib/cities/city_catalog";
import type { BuilderResult } from "@/components/experiences/GuidedExperienceBuilder";
import type { DiscoveryResult } from "@/components/experiences/discovery/DiscoveryPanel";
import type { ProfileInsight } from "@/lib/profile/profileInsights";
// ── Sprint 5E: nuevo search bar ───────────────────────────────────────────────
import { TravelSearchBar } from "@/components/experiences/TravelSearchBar";
import type { TravelSearchContext } from "@/components/experiences/TravelSearchBar";
// ── Sprint 12B: Intent Refinement Panel ───────────────────────────────────────
import type { IntentDraft } from "@/components/experiences/IntentRefinementPanel";
import { FlagImg } from "@/components/FlagImg";
// ── Eagerly-needed data (used during initial render / skeleton) ───────────────
import { MOOD_META, deriveDayMood } from "@/lib/experiences/moodMeta";
import { CITY_HERO_META } from "@/lib/experiences/cityMeta";
import { CATEGORY_META, PLACEHOLDER_PALETTE, TIME_META } from "@/lib/experiences/categoryMeta";
import { CITY_VISUALS, CATEGORY_VISUALS } from "@/lib/experiences/visuals";

// ── Category and time-of-day Lucide icon maps — replaces color emojis ────────
const CATEGORY_LUCIDE_ICONS: Readonly<Record<string, LucideIcon>> = {
  food:         Utensils,
  anime:        Sparkles,
  nightlife:    Moon,
  culture:      Landmark,
  art:          Palette,
  nature:       Leaf,
  music:        Music,
  architecture: Building2,
  wellness:     Heart,
  shopping:     ShoppingBag,
};

const TIME_LUCIDE_ICONS: Readonly<Record<string, LucideIcon>> = {
  morning:   Sunrise,
  afternoon: Sun,
  evening:   Sunset,
  night:     Moon,
  any:       MapPin,
};
const KNOWLEDGE_LUCIDE_ICONS: Readonly<Record<string, LucideIcon>> = {
  hidden_gem:       Sparkles,
  neighborhood:     MapPin,
  cafe:             Coffee,
  market:           Store,
  viewpoint:        Eye,
  park:             Leaf,
  local_experience: Compass,
};
const MOOD_LUCIDE_ICONS: Readonly<Record<string, LucideIcon>> = {
  foodie:       Utensils,
  nightlife:    Moon,
  culture:      Landmark,
  creative:     Palette,
  nature:       Leaf,
  slow:         Sunset,
  anime:        Sparkles,
  music:        Music,
  architecture: Building2,
  default:      Globe,
};

// ─── Day display helpers (display-only, never mutates backend data) ───────────

// Category → display title
const _CAT_DISPLAY_TITLE: Readonly<Record<string, string>> = {
  food:         "Sabores locales",
  culture:      "Cultura local",
  art:          "Arte y museos",
  architecture: "Arquitectura y ciudad",
  nightlife:    "Noche local",
  nature:       "Pausa verde",
  music:        "Música y ambiente",
  shopping:     "Diseño y compras",
  wellness:     "Ritmo suave",
  anime:        "Cultura popular",
};

// Mood key → display title (fallback when raw title is internal/missing)
const _MOOD_DISPLAY_TITLE: Readonly<Record<string, string>> = {
  foodie:       "Sabores locales",
  nightlife:    "Noche local",
  culture:      "Cultura local",
  creative:     "Arte y creatividad",
  nature:       "Al aire libre",
  slow:         "Ritmo suave",
  anime:        "Cultura popular",
  music:        "Música y ambiente",
  architecture: "Arquitectura y ciudad",
  default:      "Exploración libre",
};

// Category → mood key (mirrors moodMeta CATEGORY_TO_MOOD)
const _CAT_TO_MOOD: Readonly<Record<string, string>> = {
  food:         "foodie",
  nightlife:    "nightlife",
  culture:      "culture",
  art:          "creative",
  nature:       "nature",
  wellness:     "slow",
  anime:        "anime",
  music:        "music",
  architecture: "architecture",
};

// Known English→Spanish title map
const _TITLE_NORMALIZE_MAP: Readonly<Record<string, string>> = {
  "food discovery":    "Sabores locales",
  "cultural layer":    "Cultura local",
  "art pulse":         "Arte y museos",
  "night pulse":       "Noche local",
  "nature reset":      "Pausa verde",
  "architecture walk": "Arquitectura y ciudad",
  "shopping layer":    "Diseño y compras",
  "creative day":      "Arte y creatividad",
  "slow escape":       "Ritmo suave",
  "open air":          "Al aire libre",
  "culture pop":       "Cultura popular",
  "sound & soul":      "Música y ambiente",
  "built form":        "Arquitectura y ciudad",
  "enfoque cultural":  "Cultura local",
  "exploración":       "Exploración libre",
};

/**
 * Infer dominant category from day experiences, down-weighting provider
 * activities so a single provider item doesn't skew the day identity.
 */
function getDominantCategory(day: ExperienceDay): string {
  if (!day.experiences.length) return "culture";

  const counts: Record<string, number> = {};
  for (const exp of day.experiences) {
    const isProvider = exp.source_type === "provider";
    // Provider gets 0.5 weight when there are multiple local/editorial items
    const weight = isProvider && day.experiences.length > 2 ? 0.5 : 1.0;
    const cat = exp.category ?? "culture";
    counts[cat] = (counts[cat] ?? 0) + weight;
  }

  let dominant = "culture";
  let max = 0;
  for (const [cat, count] of Object.entries(counts)) {
    if (count > max) { max = count; dominant = cat; }
  }
  return dominant;
}

/**
 * Display mood key — uses dominant content rather than energy overrides that
 * can be skewed by a single provider activity.
 */
function getDisplayMoodKey(day: ExperienceDay): string {
  if (!day.experiences.length) return "default";

  const dominant = getDominantCategory(day);
  const moodKey  = _CAT_TO_MOOD[dominant] ?? "default";

  // Keep energy-low override only when it genuinely matches dominant content
  if (day.energy === "low" && (dominant === "wellness" || dominant === "nature")) {
    return "slow";
  }

  return moodKey;
}

function normalizeDayTitle(day: ExperienceDay, displayMoodKey: string): string {
  const raw = (day.title ?? "").trim();
  if (!raw) return _MOOD_DISPLAY_TITLE[displayMoodKey] ?? "Exploración libre";

  const key = raw.toLowerCase();
  if (_TITLE_NORMALIZE_MAP[key]) return _TITLE_NORMALIZE_MAP[key];

  // Catch any ASCII-only internal labels
  if (/^[a-z0-9 &_' -]+$/.test(key)) {
    return _MOOD_DISPLAY_TITLE[displayMoodKey] ?? "Exploración libre";
  }

  return raw;
}

// ─── Narrative arrival/farewell reframing (display-only) ──────────────────────
// The backend Narrative Engine's arrival ("primer día") and farewell ("último
// día") template pools use ordinal-day/schedule framing that reads as Planner
// output rather than a marketplace listing. Backend templates are out of scope
// here, so this detects that specific framing and substitutes marketplace-
// appropriate copy at render time. Narrow pattern — leaves unrelated content
// (including legitimate "itinerario"/"ruta" usage, e.g. "ruta del vino") untouched.
const _SCHEDULE_DAY_PATTERN = /\b(primer|último)\s+d[ií]a\b|\bd[ií]a\s+siguiente\b/i;

function stripScheduleFraming(
  text: string,
  kind: "title" | "subtitle" | "intro" | "moment",
  cityDisplay: string,
): string {
  if (!_SCHEDULE_DAY_PATTERN.test(text)) return text;
  const isFarewell = /último/i.test(text);
  const city = cityDisplay.trim() || "La ciudad";

  if (isFarewell) {
    switch (kind) {
      case "title":    return "Cierre y últimas horas";
      case "subtitle": return "Para terminar bien, sin prisa";
      case "intro":    return `${city} se ve distinta cuando ya conoces sus calles. Ideal para cerrar sin prisa.`;
      case "moment":   return "Vuelve a un lugar que ya visitaste — se ve distinto con el viaje encima.";
    }
  }
  switch (kind) {
    case "title":    return "Primeras impresiones";
    case "subtitle": return "Barrios, sabores y referencias locales";
    case "intro":    return `${city} empieza por sus primeras impresiones: barrios, sabores y referencias locales.`;
    case "moment":   return "Habrá un momento — una calle, un café, una conversación — en que esta selección empiece a tener sentido.";
  }
}

const TAG_LABELS_ES: Readonly<Record<string, string>> = {
  architecture: "Arquitectura",
  culture: "Cultura",
  art: "Arte",
  food: "Gastronomía",
  gastronomy: "Gastronomía",
  nightlife: "Vida nocturna",
  nature: "Naturaleza",
  music: "Música",
  shopping: "Compras",
  wellness: "Bienestar",
  anime: "Anime",
  history: "Historia",
  local_life: "Vida local",
  hidden_gems: "Rincones locales",
  viewpoints: "Miradores",
  cafes: "Cafés",
  family: "Familia",
  design: "Diseño",
};

function formatTagLabel(tag: string): string {
  const key = tag.trim().toLowerCase();
  const category = CATEGORY_META[key];
  return category?.label ?? TAG_LABELS_ES[key] ?? tag.replace(/_/g, " ");
}
import { resolveKnowledgeVisual } from "@/lib/experiences/knowledgeVisuals";
import { parseQuery, buildInterpretationText, parseDatesFromQuery, parseTravelersFromQuery } from "@/lib/experiences/queryParser";
import { POPULAR_DESTINATION_TEASERS } from "@/lib/experiences/popularDestinations";
import { getCityFlag } from "@/lib/geo/flagMeta";
// ── Lazy-loaded components — not needed at initial render ─────────────────────
// Each becomes a separate split chunk; only fetched when user triggers the flow.
const GuidedExperienceBuilder = lazy(
  () => import("@/components/experiences/GuidedExperienceBuilder"),
);
const DiscoveryPanel = lazy(
  () => import("@/components/experiences/discovery/DiscoveryPanel"),
);
// Sprint 12C — lazy-loaded: not needed until after intent parsing
const IntentRefinementPanel = lazy(() =>
  import("@/components/experiences/IntentRefinementPanel").then((m) => ({
    default: m.IntentRefinementPanel,
  })),
);
// Sprint 12C — lazy-loaded: only rendered when user opens a product detail
const ProviderProductDetailDrawer = lazy(() =>
  import("@/components/ProviderProductDetailDrawer").then((m) => ({
    default: m.ProviderProductDetailDrawer,
  })),
);

export const Route = createFileRoute("/experiences")({
  component: ExperiencesPage,
  validateSearch: (search: Record<string, unknown>) => {
    // Accept string OR number — TanStack Router may pass either depending on
    // whether the value comes from navigate() (number) or URL parsing (string).
    const safeInt = (v: unknown, min: number, max: number): number | undefined => {
      if (typeof v !== "string" && typeof v !== "number") return undefined;
      if (typeof v === "string" && v.trim() === "") return undefined;
      const n = Number(v);
      if (!Number.isFinite(n)) return undefined;
      const r = Math.round(n);
      return r >= min && r <= max ? r : undefined;
    };
    // children_ages is a comma-separated string ("7,10") but a single child
    // may arrive as a bare number after URL parsing (e.g. children_ages=7).
    const safeChildrenAges = (v: unknown): string | undefined => {
      if (typeof v === "string") return v;
      if (typeof v === "number" && Number.isFinite(v)) return String(Math.round(v));
      return undefined;
    };
    const safeDestinationIata = (v: unknown): string | undefined => {
      if (typeof v !== "string") return undefined;
      const normalized = v.trim().toUpperCase();
      return /^[A-Z]{3}$/.test(normalized) ? normalized : undefined;
    };
    const safeDestinationName = (v: unknown): string | undefined => {
      if (typeof v !== "string") return undefined;
      const normalized = v.trim();
      return normalized.length > 0 && normalized.length <= 100
        ? normalized
        : undefined;
    };
    return {
      q:                  typeof search.q                === "string" ? search.q             : undefined,
      destination_iata:   safeDestinationIata(search.destination_iata),
      destination_name:   safeDestinationName(search.destination_name),
      departure_date:     typeof search.departure_date   === "string" ? search.departure_date : undefined,
      return_date:        typeof search.return_date      === "string" ? search.return_date    : undefined,
      travel_month:       typeof search.travel_month     === "string" ? search.travel_month   : undefined,
      flexible_days:      safeInt(search.flexible_days,       1,  90),
      flexibility_window: safeInt(search.flexibility_window,  0,  30),
      adults:             safeInt(search.adults,              1,  10),
      children_ages:      safeChildrenAges(search.children_ages),
    };
  },
  head: () => ({
    meta: [
      { title: "Explorar Experiencias — Sift Trip" },
      {
        name: "description",
        content:
          "Descubre actividades organizadas inteligentemente según tu estilo de viaje.",
      },
    ],
  }),
});

// ─── (metadata moved to lib/experiences/) ────────────────────────────────────
// CATEGORY_META    → lib/experiences/categoryMeta.ts
// TIME_META        → lib/experiences/categoryMeta.ts
// MOOD_META        → lib/experiences/moodMeta.ts
// CITY_HERO_META   → lib/experiences/cityMeta.ts
// PLACEHOLDER_PALETTE → lib/experiences/categoryMeta.ts
// MoodStyle (type)  → lib/experiences/moodMeta.ts
// Imported at top of file.

// ─── Loading messages ─────────────────────────────────────────────────────────
// Marketplace Loading & Launch Polish V1 — Experiences is a marketplace
// search, not an itinerary generator: this rotation deliberately avoids
// "secuencia"/"narrativa"/"equilibrando" language (that reads as an AI
// writing a trip narrative) in favor of plain discovery/search verbs
// (buscando, consultando, organizando, filtrando, comparando).

// Provider-Only Marketplace Hotfix V1, Part B — the second message used to
// mention consulting local/reference content, which was accurate before the
// launch marketplace went provider-only. That content is no longer
// surfaced in this mode, so the rotation never claims to consult it.
const LOADING_MESSAGES = [
  "Buscando experiencias disponibles...",
  "Consultando proveedores disponibles...",
  "Organizando resultados por relevancia...",
  "Filtrando por categoría y disponibilidad...",
  "Comparando opciones por proveedor...",
  "Analizando barrios y zonas de la ciudad...",
];

// ─── Suggestion chips ─────────────────────────────────────────────────────────

const SUGGESTION_CHIPS = [
  { label: "Gastronomía",   tag: "foodie"     },
  { label: "Anime",         tag: "anime"      },
  { label: "Vida nocturna", tag: "nightlife"  },
  { label: "Arte",          tag: "arte"       },
  { label: "Música",        tag: "música"     },
  { label: "Naturaleza",    tag: "naturaleza" },
];

// ─── Teaser queries ───────────────────────────────────────────────────────────
// Experiences Popular Destinations Marketplace Alignment V1 — moved to
// lib/experiences/popularDestinations.ts, the tested single source of truth
// (launch_promoted-only, city-level, marketplace-framed, no "N días" copy).

const TEASER_QUERIES = POPULAR_DESTINATION_TEASERS;

// ─── (parser + mood moved to lib/experiences/) ───────────────────────────────
// ParsedQuery, CITY_ALIASES, INTEREST_KEYWORDS, parseQuery → lib/experiences/queryParser.ts
// deriveDayMood, MoodStyle (interface)                      → lib/experiences/moodMeta.ts
// Imported at top of file.

// ─── Budget style mapper ──────────────────────────────────────────────────────
// Maps a max USD amount to a named budget style for the backend.
function budgetToStyle(maxUsd: number): "economico" | "equilibrado" | "premium" | "lujo" | null {
  if (maxUsd <= 0) return null;
  if (maxUsd <= 800)  return "economico";
  if (maxUsd <= 2000) return "equilibrado";
  if (maxUsd <= 5000) return "premium";
  return "lujo";
}

// ─── Canonical traveler state ─────────────────────────────────────────────────
// Single source of truth for adults / children / type across sidebar and search bar.

type TravelerType = TravelSearchContext["travelersType"];

interface TravelerState {
  adults:       number;
  childrenAges: number[];
  hasChildren:  boolean;
  travelerType: TravelerType;
}

function deriveTravelerType(adults: number, childrenAges: number[]): TravelerType {
  if (childrenAges.length > 0) return "familia";
  if (adults === 1) return "solo";
  if (adults === 2) return "pareja";
  return "amigos";
}

function inclusiveDayCount(startIso: string, endIso: string): number | null {
  const start = new Date(`${startIso}T00:00:00`);
  const end   = new Date(`${endIso}T00:00:00`);
  const diff  = end.getTime() - start.getTime();
  if (!Number.isFinite(diff) || diff < 0) return null;
  return Math.min(Math.max(Math.round(diff / 86_400_000) + 1, 1), 14);
}

// ─── TravelerType mapper ──────────────────────────────────────────────────────
// TravelSearchBar uses Spanish keys ("pareja", "familia"…),
// IntentRefinementPanel uses English keys ("couple", "family"…).
// This function bridges both.
function mapSearchBarTravelerType(t: string | undefined): IntentDraft["travelersType"] {
  const m: Record<string, IntentDraft["travelersType"]> = {
    solo:    "solo",
    pareja:  "couple",
    amigos:  "friends",
    familia: "family",
    mayores: "seniors",
    // pass-through for already-mapped values
    couple:  "couple",
    friends: "friends",
    family:  "family",
    seniors: "seniors",
  };
  return m[t ?? "pareja"] ?? "couple";
}

// ─── Animation helpers ────────────────────────────────────────────────────────

const fadeUp = (delay = 0, duration = 0.55) => ({
  initial:    { opacity: 0, y: 14 },
  animate:    { opacity: 1, y: 0 },
  transition: { duration, delay, ease: [0.22, 1, 0.36, 1] as const },
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Returns true when the query has enough intent to generate directly.
 *
 * Rules:
 *   - Always needs: city (guaranteed by parseQuery returning non-null) + explicit days
 *   - 2+ interests → complete regardless of pace (defaults to "equilibrado")
 *   - 1 interest   → complete only if pace is also explicitly provided
 *   - 0 interests  → never complete (interests step is required)
 */
function isQueryComplete(parsed: ParsedQuery): boolean {
  if (!parsed.hasDays || parsed.interests.length === 0) return false;
  if (parsed.interests.length >= 2) return true;          // 2+ interests → skip pace step
  return parsed.pace !== null;                             // 1 interest  → need explicit pace
}

// ─── Phase 7 Smoke Test (manual validation steps) ────────────────────────────
// 1. Search "milan viñedos romance"
//    Expected: FallbackPanel appears with wine-country candidates (VRN, LNG, VOR, LCO)
//    NOT: builder opens with "Milán" / MXP
// 2. Click "Verona" candidate in FallbackPanel
//    Expected: GuidedExperienceBuilder opens with city="Verona", cityIata="VRN"
//    NOT: builder re-opens with Milán
// 3. Complete the builder (select any pace / days / interests)
//    Expected: API call logs "[RHEA Sequence Request] city_iata=VRN"
//    Backend logs "[SequenceRequest] city=VRN ... raw_query='milan viñedos romance'"
// 4. Result shows Verona wine-country itinerary
//    Expected: day titles contain wine vocabulary (vino, copa, viñedos, bodega…)
//    NOT: "Mesa, calle y curiosidad" or "Lo que come la gente del barrio"
// 5. Backend [SequenceWarning] must NOT fire for VRN (only fires for MXP with wine keywords)
// 6. Search "milan" alone (no wine keywords)
//    Expected: builder opens with Milán normally (bypass NOT triggered)
// ─────────────────────────────────────────────────────────────────────────────

// ─── Catalog-unavailable error detection ─────────────────────────────────────
// Returns true when the backend error signals a missing catalog — not a real
// network/server error. These should trigger Fallback Intelligence instead of
// showing a raw error message.

function _isCatalogUnavailableError(msg: string): boolean {
  if (!msg) return false;
  const lower = msg.toLowerCase();
  return (
    lower.includes("catálogo no disponible") ||
    lower.includes("catalogo no disponible") ||
    lower.includes("catalog not available") ||
    lower.includes("unsupported city") ||
    lower.includes("ciudad no disponible") ||
    lower.includes("no catalog")
  );
}


// ─── Helper: extract a readable city name from free-form query ───────────────
// Used when parseQuery() returns null (city not in catalog).
// Very lightweight: takes first "word" that looks like a proper noun.
const SKIP_WORDS = new Set([
  "dias", "días", "day", "days", "noche", "noches",
  "quiero", "busco", "ir", "a", "en", "de", "con",
  "foodie", "arte", "musica", "música", "cultura", "naturaleza",
  "noche", "tranquilo", "intenso", "rapido", "rápido", "lento",
  "viaje", "trip", "travel", "visitar",
]);

function _extractCityHint(raw: string): string | null {
  const cleaned = raw
    .replace(/\d+\s*(d[ií]as?|days?|noches?|nights?)/gi, "")
    .replace(/[+,]/g, " ")
    .trim();
  const words = cleaned
    .split(/\s+/)
    .filter(w => w.length >= 3 && !/^\d+$/.test(w) && !SKIP_WORDS.has(w.toLowerCase()));
  if (words.length === 0) return null;
  const w = words[0];
  const capitalized = w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
  return normalizeCityDisplayName(capitalized);
}

// ─── Recent searches — localStorage helpers ───────────────────────────────────

const RECENT_SEARCHES_KEY = "rhea_recent_searches";

function _loadRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_SEARCHES_KEY);
    return raw ? (JSON.parse(raw) as string[]).slice(0, 5) : [];
  } catch { return []; }
}

function saveRecentSearch(query: string): string[] {
  try {
    const next = [query, ..._loadRecent().filter(q => q !== query)].slice(0, 5);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(next));
    return next;
  } catch { return _loadRecent(); }
}

function removeRecentSearch(query: string): string[] {
  try {
    const next = _loadRecent().filter(q => q !== query);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(next));
    return next;
  } catch { return []; }
}

// ─── Main page ────────────────────────────────────────────────────────────────

function ExperiencesPage() {
  const { user } = useAuth();
  const { startLoading, clearLoading } = useRheaLoading();
  const [query,          setQuery]          = useState("");
  const [loading,        setLoading]        = useState(false);
  // Sprint 13B — shows while awaiting LLM intent parser (before refinement panel)
  const [parsingIntent,  setParsingIntent]  = useState(false);
  // Sprint 14A — mini clarification card (year / days ambiguity detected by LLM)
  const [clarification,  setClarification]  = useState<LLMParsedIntent | null>(null);
  const [pendingCtx,     setPendingCtx]     = useState<TravelSearchContext | undefined>(undefined);
  // URL params — read before state so lazy initializers can use them synchronously
  const {
    q:                  qParam,
    destination_iata:   destinationIataParam,
    destination_name:   destinationNameParam,
    departure_date,
    return_date,
    travel_month,
    flexible_days,
    flexibility_window,
    adults:             adultsParam,
    children_ages:      childrenAgesParam,
  } = Route.useSearch();

  // Sprint 14B — left sidebar filters (travelers). sidebarBudgetMax feeds the
  // backend generation request's budget_usd/budget_style — it has no visible
  // UI anymore since Marketplace Filters V2 replaced the trip-budget slider
  // with the marketplace's own instant "Precio máximo" filter (ActiveFilters
  // .maxPrice, applied client-side to marketplace_inventory). Fixed at its
  // old default (never user-adjustable now) so the request payload shape is
  // unchanged.
  const sidebarBudgetMax = 5000;
  const [sidebarDirty, setSidebarDirty] = useState(false);
  const [itineraryFilters, setItineraryFilters] = useState<ActiveFilters>({ ...EMPTY_FILTERS, categories: new Set() });
  // Canonical traveler state — seeded from URL, updated on submit or sidebar change
  const [travelerState, setTravelerState] = useState<TravelerState>(() => {
    const ages = childrenAgesParam
      ? childrenAgesParam.split(",").map(Number).filter((n: number) => Number.isFinite(n) && n >= 0 && n <= 17)
      : [];
    const a = (adultsParam !== undefined && Number.isFinite(adultsParam) && adultsParam >= 1 && adultsParam <= 10)
      ? adultsParam
      : 2;
    return { adults: a, childrenAges: ages, hasChildren: ages.length > 0, travelerType: deriveTravelerType(a, ages) };
  });
  // Last extra params used in a generation — so the sidebar "Actualizar" can re-apply them
  const [lastExtraParams, setLastExtraParams] = useState<{
    travelers_type?: string;
    travelers?:      number;
    pace?:           string;
    has_children?:   boolean;
    children_ages?:  number[];
    mobility_level?: "normal" | "low" | "minimal" | null;
  }>({});
  const [error,       setError]       = useState<string | null>(null);
  const [sequence,    setSequence]    = useState<ExperienceSequence | null>(null);
  const [inspireMode, setInspireMode] = useState(false);
  const [parsed,      setParsed]      = useState<ParsedQuery | null>(null);
  // Guided Builder state
  const [showBuilder,   setShowBuilder]   = useState(false);
  const [builderCity,   setBuilderCity]   = useState<{ city: string; iata: string } | null>(null);
  const [builderInitial, setBuilderInitial] = useState<{
    days:        number | null;
    interests:   string[];
    pace:        string | null;
    budgetStyle: string | null;
  } | null>(null);
  // Agentic Discovery state
  const [showDiscovery,   setShowDiscovery]   = useState(false);
  const [discoveryState,  setDiscoveryState]  = useState<DiscoveryState | null>(null);
  // Interpretation feedback ("Sift Trip entendió 'rumba' como vida nocturna.")
  const [interpretation, setInterpretation] = useState<string | null>(null);
  // Traveler profile
  const [insight,          setInsight]          = useState<ProfileInsight | null>(null);
  const [insightDismissed, setInsightDismissed] = useState(false);
  /** Non-null when user has accepted profile preferences — merged into next query. */
  const [profilePrefs,     setProfilePrefs]     = useState<{ interests: string[]; pace: string | null; budgetStyle: string | null } | null>(null);
  // Fallback Intelligence V1 — populated when parseQuery returns null
  const [fallbackState,    setFallbackState]    = useState<FallbackResult | null>(null);
  // Sprint 8B — family mode (has_children was true in submission)
  const [familyMode,       setFamilyMode]       = useState(false);
  // Coming Soon — city detected but not yet in catalog
  const [comingSoonCity,   setComingSoonCity]   = useState<string | null>(null);
  const [comingSoonRaw,    setComingSoonRaw]     = useState<string>("");
  const [waitlistSent,     setWaitlistSent]     = useState(false);
  // Intent Persistence Layer V1 — snapshot captured when user clicks a fallback candidate.
  // Held until handleBuilderComplete / handleDiscoveryComplete clears it.
  const [pendingSnapshot,  setPendingSnapshot]  = useState<IntentSnapshot | null>(null);
  // Provider Destination Discovery V2
  const [providerDiscoveryResult,  setProviderDiscoveryResult]  = useState<ProviderDestinationDiscoveryResult | null>(null);
  const [providerDiscoveryLoading, setProviderDiscoveryLoading] = useState(false);
  // Synchronous date init from URL — guarantees the first handleSubmit sees real dates
  const [dateSelection, setDateSelection] = useState<DateSelection>(() => {
    if (departure_date) {
      return { mode: "exact",    travelDate: departure_date, departure_date, return_date: return_date ?? null };
    }
    if (travel_month) {
      return { mode: "flexible", travelDate: travel_month, travel_month, flexible_days: flexible_days ?? null, flexibility_window: flexibility_window ?? null };
    }
    return { mode: "none", travelDate: null };
  });
  // Recent searches — persisted to localStorage
  const [recentSearches, setRecentSearches] = useState<string[]>(() => _loadRecent().slice(0, 3));
  // Sprint 12B — Intent Refinement Panel (shows parsed intent before generating)
  const [showRefinement,   setShowRefinement]   = useState(false);
  const [refinementParsed, setRefinementParsed] = useState<ReturnType<typeof parseQuery> | null>(null);
  const [refinementCtx,    setRefinementCtx]    = useState<TravelSearchContext | undefined>(undefined);

  const resultsRef = useRef<HTMLDivElement>(null);

  // Launch Stability & Error Recovery V1 — generation request-id guard +
  // last-attempt replay. sequenceRequestIdRef mirrors the existing
  // searchRequestIdRef pattern already used for /experiences/search
  // pagination below: a slow, superseded /experiences/sequence response
  // (double-submit, or a stale retry racing a newer search) must never
  // overwrite state a NEWER request already set. lastAttemptRef stores a
  // closure over the exact (p, extra) that led to the current error, so
  // "Intentar de nuevo" replays the identical request instead of
  // re-parsing the query (which could drift to a different result).
  const sequenceRequestIdRef = useRef(0);
  const lastAttemptRef = useRef<(() => void) | null>(null);

  // Mobile Results Disappearing Hotfix V1 — sequenceRequestIdRef (above)
  // only stops a STALE response from overwriting a NEWER one; it does
  // nothing to stop a genuinely duplicate handleSubmit call from ever
  // starting in the first place. generateSequence() unconditionally resets
  // sequence to null and loading to true at its very start (by design, for
  // a real new search) — so a second, redundant handleSubmit call for the
  // SAME query while the first is still resolving (LLM parse, then
  // generateSequence) blanks out an already-displayed valid result and
  // re-shows the loading spinner, then repopulates once the duplicate
  // resolves: exactly "results appear, then disappear, then reappear".
  // TravelSearchBar's own disabled-button state (loading prop) usually
  // prevents this, but there is a real window between the tap and React
  // re-rendering with that disabled state — most reachable on mobile,
  // where a single physical tap can dispatch both a touch-derived and a
  // native click event for the same gesture. This ref is a synchronous
  // (not React-state-timing-dependent) reentrancy lock: identical query
  // text submitted again within the debounce window is ignored outright;
  // a genuinely new/different query is never blocked, and the exact same
  // query is never blocked once the window has passed.
  const lastSubmitRef = useRef<{ query: string; at: number } | null>(null);
  const DUPLICATE_SUBMIT_WINDOW_MS = 600;

  // Admin Metrics UX V1 — marketplace_page_engagement. Starts a fresh
  // duration timer on mount; fires once (tab hidden, page unload, or this
  // route unmounting) with a bucketed duration — never a raw millisecond
  // value. See lib/marketplaceEventLog.ts's own module-level tracking for
  // why had_results/provider_clicked/comparison_opened don't need to be
  // threaded through as props from the far-apart components that set them.
  useEffect(() => {
    return initPageEngagementTracking();
  }, []);

  // Reset itinerary display filters when a new sequence is generated
  useEffect(() => {
    setItineraryFilters({ ...EMPTY_FILTERS, categories: new Set() });
  }, [sequence]);

  // TravelSearchContext derived from URL adults/children_ages — default ctx for auto-submit
  const urlCtx = useMemo<TravelSearchContext | undefined>(() => {
    if (adultsParam === undefined && childrenAgesParam === undefined) return undefined;
    const ages = childrenAgesParam
      ? childrenAgesParam.split(",").map(Number).filter((n: number) => Number.isFinite(n) && n >= 0 && n <= 17)
      : [];
    // adultsParam already validated by safeInt in validateSearch (1-10); fall back to 2
    const a = (adultsParam !== undefined && Number.isFinite(adultsParam) && adultsParam >= 1 && adultsParam <= 10)
      ? adultsParam
      : 2;
    const travelersType =
      ages.length > 0 ? "familia" as const :
      a === 1         ? "solo"    as const :
      a === 2         ? "pareja"  as const :
                        "amigos"  as const;
    return { travelers: a, travelersType, searchMode: "know" as const, childrenAges: ages, hasChildren: ages.length > 0 };
  }, [adultsParam, childrenAgesParam]);

  // Load profile insight on mount — profile modules loaded dynamically (split chunk)
  useEffect(() => {
    // Wake Render backend as soon as the page loads — avoids cold-start delay on first search
    wakeServer();
    Promise.all([
      import("@/lib/profile/profileStore"),
      import("@/lib/profile/profileInsights"),
    ]).then(([{ loadProfile }, { generateInsight }]) => {
      const profile = loadProfile();
      setInsight(generateInsight(profile));
    });
  }, []);

  const structuredUrlQuery = useMemo(() => {
    const intent = qParam?.trim() ?? "";
    if (destinationIataParam && destinationNameParam) {
      return [destinationNameParam, intent].filter(Boolean).join(" ");
    }
    return intent;
  }, [destinationIataParam, destinationNameParam, qParam]);

  // Auto-trigger from the structured destination URL or a legacy ?q= URL.
  useEffect(() => {
    if (structuredUrlQuery && !query && !showRefinement && !sequence && !loading) {
      setQuery(structuredUrlQuery);
      // Fire after mount to ensure state is stable; urlCtx carries URL adults/children
      const timer = setTimeout(() => {
        void handleSubmit(structuredUrlQuery, urlCtx, "url_param");
      }, 80);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structuredUrlQuery]);

  // ── Direct generate (power-user or post-builder) ───────────────────────
  const generateSequence = async (
    p: ParsedQuery,
    extra?: {
      travelers?: number;
      travelers_type?: string;
      has_children?: boolean;
      children_ages?: number[];
      pace?: string;
      budget_style?: string;
      rawQuery?: string;
      intent_snapshot?: IntentSnapshot | null;
      /** Sprint 12B — mobility level forwarded to backend. */
      mobility_level?: "normal" | "low" | "minimal" | null;
      /** Phase 5: true when the call comes from a supply-first candidate —
       *  suppresses the catalog-error → fallback-refetch loop. */
      skip_fallback_on_error?: boolean;
      /** Sprint 14: LLM-detected travel date — overrides DateFlexPicker when picker is unset. */
      travel_date?: string | null;
      /** Sprint 14B: numeric budget upper bound in USD, complements budget_style. */
      budget_usd?: number;
    },
  ) => {
    // Launch Stability & Error Recovery V1 — see sequenceRequestIdRef's own
    // doc above: this request "owns" the shared loading/error/sequence state
    // only until a newer generateSequence call starts. lastAttemptRef is
    // updated unconditionally (even a superseded call is still a valid
    // "last thing the user tried" for retry purposes).
    const requestId = ++sequenceRequestIdRef.current;
    lastAttemptRef.current = () => { void generateSequence(p, extra); };

    setParsed(p);
    setLoading(true);
    // Marketplace Loading & Launch Polish V1 — matches index.tsx's identical
    // overlay copy for the same action (starting a search); "Diseñando tu
    // experiencia" read as itinerary-design language, not marketplace search.
    startLoading("Buscando experiencias reales...");
    setSequence(null);
    setError(null);
    setFallbackState(null);
    setComingSoonCity(null);
    setWaitlistSent(false);
    // Sprint 14B — store params so sidebar "Actualizar" can re-run with new budget/travelers
    setLastExtraParams({
      travelers_type: extra?.travelers_type,
      travelers:      extra?.travelers,
      pace:           extra?.pace,
      has_children:   extra?.has_children,
      children_ages:  extra?.children_ages,
      mobility_level: extra?.mobility_level,
    });
    setSidebarDirty(false);
    setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);

    // ── Pre-warm: wait for backend to be ready before firing the heavy request ──
    // wakeServer() is a cached Promise — __root.tsx fires it on page load so by the
    // time the user submits their first search it's already in-flight or resolved.
    // Awaiting it here ensures /experiences/sequence only lands on a warm backend,
    // eliminating the Render free-tier cold-start race condition.
    await wakeServer();

    // Strip rawQuery / skip_fallback_on_error — frontend-only, never sent to backend.
    //   rawQuery        : used only for the fallback re-fetch query string (not an API field)
    //   skip_fallback_on_error : loop-guard, not a backend field
    //   intent_snapshot : extracted separately so it can be forwarded explicitly
    const { rawQuery: _rq, intent_snapshot: _snap, skip_fallback_on_error: _skipFb, mobility_level: _mobLevel, ...apiExtra } = extra ?? {};

    // ── Phase 2+3+4: Build exact API payload ─────────────────────────────────
    // Assembled here (not inline) so the DEV log reflects exactly what JSON.stringify
    // will serialize and send to /experiences/sequence.
    //
    // Contract:
    //   travelers_type    — snake_case (backend field). Mapped from BuilderResult.travelersType.
    //   intent_snapshot.original_query — carries the user's raw search query through the
    //                                    builder so the backend uses it for archetype
    //                                    matching instead of just the destination name.
    //   debug             — DEV-only; triggers supplier_intelligence_debug in response.
    //                       Never sent in production builds.
    const _apiPayload = {
      city:             p.city,
      city_iata:        p.iata,
      days:             p.days,
      interests:        p.interests,
      region:           p.region,       // Region Discovery V1
      intent_snapshot:  _snap ?? null,  // Intent Persistence Layer V1
      // Sprint 5B — pass travel_date: user DateFlexPicker wins; LLM-detected date as fallback
      travel_date:      extra?.travel_date ?? dateSelection.travelDate ?? undefined,
      ...apiExtra,                      // travelers_type, pace, budget_style, travelers, has_children, children_ages
      // Sprint 12B — mobility level (undefined strips the key from payload)
      ...(_mobLevel != null ? { mobility_level: _mobLevel } : {}),
      // DEV-only: request supplier_intelligence_debug from backend
      ...(import.meta.env.DEV ? { debug: true } : {}),
    };

    // ── Phase 4: DEV-only exact payload log ──────────────────────────────────
    // Shows precisely what is serialized and sent — not a pre-API summary.
    if (import.meta.env.DEV) {
      console.info("[RHEA Sequence Payload]", {
        ..._apiPayload,
        // Annotate derived context (not sent, for readability)
        _meta: {
          source:          _snap ? "fallbackCandidate" : "directSearch",
          skip_fallback:   _skipFb ?? false,
          original_query:  _snap?.original_query ?? _rq ?? p.city,
        },
      });
    }

    try {
      const result = await generateExperienceSequence(_apiPayload);
      // Launch Stability & Error Recovery V1 — a newer generateSequence call
      // already started (double-submit, or a retry racing a fresh search);
      // this response is stale and must never overwrite what the user is
      // now looking at.
      if (requestId !== sequenceRequestIdRef.current) return;
      if (!result.success) {
        // ── Fallback Intelligence V1: catalog errors trigger fallback panel ──
        // When the backend recognizes the IATA (e.g. MXP for Milán) but has no
        // catalog content for it, redirect to the fallback candidate panel
        // instead of showing the "Catálogo no disponible" error message.
        //
        // Phase 5: skip_fallback_on_error suppresses this re-fetch when the call
        // originated from a supply-first candidate (can_generate_sequence=true).
        // Without this guard, a failed supply-first call would re-surface the same
        // fallback panel → user clicks the same candidate → infinite loop.
        const errMsg = result.error ?? "";
        if (_isCatalogUnavailableError(errMsg) && !_skipFb) {
          // City was in catalog but backend couldn't serve it → show coming soon
          // instead of the fallback panel that may suggest unrelated cities.
          const hint = _extractCityHint(extra?.rawQuery ?? p.city);
          setComingSoonCity(hint);
          setComingSoonRaw(extra?.rawQuery ?? p.city);
          setWaitlistSent(false);
          setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
          return;
        }
        const displayErr = errMsg || "No pudimos cargar experiencias en este momento. Intenta de nuevo o cambia el destino.";
        track("sequence_error", {
          city:       p.city,
          error_type: _isCatalogUnavailableError(errMsg) ? "catalog_unavailable" : "unknown",
          error_msg:  displayErr.slice(0, 120),
        });
        setError(displayErr);
      } else {
        setSequence(result);
        // ── Analytics: conversion key event ──────────────────────────────
        track("sequence_loaded", {
          city:             result.city_display,
          iata:             p.iata,
          days:             result.total_days,
          source_class:     (result.days?.[0]?.experiences?.[0]?.source_class as string) ?? "unknown",
          has_provider:     result.days?.some((d) => d.experiences?.some((e) => !!e.booking_url)) ?? false,
          has_availability: result.days?.some((d) => d.experiences?.some((e) => e.availability_confirmed)) ?? false,
          travel_date_mode: dateSelection.mode,
          family_mode:      extra?.has_children === true,
          coverage_tier:    result.coverage_tier ?? null,
        });
        // Launch Analytics & Funnel Instrumentation V1 — canonical funnel-step-2
        // event, fired alongside the legacy sequence_loaded above. Uses the
        // same marketplace-source helpers ResultsHero reads from, so
        // total_results/provider_count always match what the user actually
        // sees, never a raw days[] count that could include local references
        // the marketplace never renders.
        {
          const _mpSource = getMarketplaceSource(result);
          const _mpCounts = getMarketplaceCounts(result, _mpSource.items, _mpSource.source);
          track("marketplace_results_loaded", {
            destination_iata:          p.iata || undefined,
            total_results:             _mpSource.items.length,
            provider_count:            _mpCounts.providerCount,
            coverage_tier:             result.coverage_tier ?? null,
            has_marketplace_inventory: Array.isArray(result.marketplace_inventory) && result.marketplace_inventory.length > 0,
            query_category_count:      result.interests?.length ?? 0,
          });
          // Admin Metrics UX V1 — same event, alongside PostHog, persisted
          // to the backend so /admin/metrics can compute real funnel counts.
          logMarketplaceEvent("marketplace_results_loaded", {
            destination_iata: p.iata || undefined,
            had_results:      _mpSource.items.length > 0,
          });
        }
        // Launch Destination Portfolio V1 — distinct signal from
        // coming_soon_shown (that's a non-generating case; this is a real,
        // successful generation that happens to have zero provider items).
        if (result.coverage_tier === "reference_only") {
          track("reference_only_result_shown", { city: result.city_display, iata: p.iata });
        }
        // ── Record this generation into the traveler profile ──────────────
        // Profile modules are split chunks — already cached from the mount
        // useEffect, so this await is effectively free on repeat calls.
        const [{ recordGeneration }, { generateInsight }] = await Promise.all([
          import("@/lib/profile/profileStore"),
          import("@/lib/profile/profileInsights"),
        ]);
        const updated = recordGeneration({
          interests:    p.interests,
          pace:         extra?.pace ?? p.pace ?? "equilibrado",
          cityIata:     p.iata,
          travelersType: extra?.travelers_type ?? "pareja",
          budgetStyle:  extra?.budget_style ?? "equilibrado",
        });
        const newInsight = generateInsight(updated);
        // Surface updated insight only when the text actually changes
        if (newInsight && newInsight.text !== insight?.text) {
          setInsight(newInsight);
          setInsightDismissed(false);
        } else if (newInsight) {
          setInsight(newInsight);
        }

        // ── Traveler Accounts V1: persist journey + sync generation ────────
        // Fire-and-forget — never block the UI or throw visible errors.
        if (user) {
          const userId = user.id;
          import("@/lib/auth/profileSync").then(
            ({ saveJourneyToHistory, syncGenerationToSupabase }) => {
              // Build journey title
              const mc = result.multi_city_sequence;
              let journeyTitle: string;
              if (mc) {
                const colonIdx = mc.summary.indexOf(":");
                journeyTitle =
                  colonIdx > 0 && colonIdx < 60
                    ? mc.summary.slice(0, colonIdx)
                    : result.city_display;
                journeyTitle += ` · ${result.total_days} días`;
              } else {
                journeyTitle = `${result.city_display} · ${result.total_days} días`;
              }

              // Build cities + countries arrays
              const cities: string[] = mc
                ? mc.cities.map((c) => c.city_name)
                : [result.city_display];
              const countries: string[] = mc
                ? [...new Set(mc.cities.map((c) => c.country))]
                : [result.country];

              // Save to journey_history
              saveJourneyToHistory(userId, {
                journey_title: journeyTitle,
                journey_tone:  result.journey_tone?.tone_id ?? null,
                cities,
                countries,
                total_days:    result.total_days,
                interests:     p.interests,
              }).catch(() => {/* silent */});

              // Bump remote profile counters
              syncGenerationToSupabase(
                userId,
                p.interests,
                extra?.pace ?? p.pace ?? "equilibrado",
                p.iata,
                extra?.travelers_type ?? "pareja",
                extra?.budget_style ?? "equilibrado",
              ).catch(() => {/* silent */});
            },
          ).catch(() => {/* silent */});
        }
      }
    } catch (err) {
      if (requestId !== sequenceRequestIdRef.current) return; // superseded — see above
      const isTimeout = String(err).toLowerCase().includes("timeout");
      track("sequence_error", {
        city:       p.city,
        error_type: isTimeout ? "timeout" : "network",
        error_msg:  String(err).slice(0, 120),
      });
      // Marketplace Loading & Launch Polish V1 — calm, non-technical copy,
      // no "conexión"/blame language; same message as the sequence-error
      // fallback above so users see one consistent voice for "something
      // didn't load", regardless of which code path failed.
      setError("No pudimos cargar experiencias en este momento. Intenta de nuevo o cambia el destino.");
    } finally {
      // Only the request that currently "owns" the shared loading/cold-start
      // state may clear it — a superseded request's cleanup must never hide
      // the spinner for the newer request still in flight.
      if (requestId === sequenceRequestIdRef.current) {
        clearLoading();     // immediate hide — don't wait MIN_VISIBLE_MS
        clearColdStart();   // clear cold-start banner regardless of request outcome
        setLoading(false);
      }
    }
  };

  // Launch Stability & Error Recovery V1 — shared "back to a clean slate"
  // reset, extracted from the toolbar's own onNewSearch inline closure
  // below (same exact reset) so the search-error state's "Nueva búsqueda"
  // action stays identical, not a second hand-written copy that could drift.
  const resetToNewSearch = () => {
    setSequence(null);
    setQuery("");
    setError(null);
    setInterpretation(null);
    setFallbackState(null);
  };

  // Replays the exact last generateSequence(p, extra) call that produced
  // the current error — never re-parses the query, so a retry can't drift
  // to a different destination/interest set than what the user saw fail.
  const retryLastSearch = () => { lastAttemptRef.current?.(); };

  // Launch Empty-State & Coverage UX V1 — jumps straight to a known-good
  // destination from the zero-provider empty state's suggestion chips.
  // Calls generateSequence directly with the exact iata/display already
  // known from the shared Home allowlist, instead of routing a constructed
  // string back through the free-text parser (which could, in principle,
  // resolve ambiguously) — the destination is never in doubt here.
  const tryDestination = (iata: string, cityDisplay: string) => {
    setQuery(cityDisplay);
    void generateSequence(
      { city: cityDisplay, iata, days: 5, hasDays: false, interests: [], pace: null, interpretations: [] },
      { rawQuery: cityDisplay },
    );
  };

  // ── Search-bar submit ──────────────────────────────────────────────────
  // Launch Analytics & Funnel Instrumentation V1 — sourceSurface defaults to
  // "search_bar" (the real TravelSearchBar onSubmit binding calls this with
  // only 2 args); the teaser/recent-search/URL-param call sites below pass
  // their own explicit surface so experiences_search_submitted always
  // reports where the search actually came from.
  const handleSubmit = async (
    rawQuery = query,
    ctx?: TravelSearchContext,
    sourceSurface: "search_bar" | "teaser" | "recent_search" | "url_param" = "search_bar",
  ) => {
    const trimmed = rawQuery.trim();
    if (!trimmed) return;

    // Mobile Results Disappearing Hotfix V1 — see lastSubmitRef's own doc
    // above. A synchronous check, immune to React state-update timing:
    // catches a duplicate call even when it happens in the exact same
    // tick as the first (e.g. a mobile "ghost click" firing twice for one
    // tap), which a loading/parsingIntent state check alone cannot.
    const _now = Date.now();
    if (
      lastSubmitRef.current
      && lastSubmitRef.current.query === trimmed
      && _now - lastSubmitRef.current.at < DUPLICATE_SUBMIT_WINDOW_MS
    ) {
      return;
    }
    lastSubmitRef.current = { query: trimmed, at: _now };

    // Reset any previous panel state on a new submission
    setShowBuilder(false);
    setShowDiscovery(false);
    setDiscoveryState(null);
    setBuilderCity(null);
    setBuilderInitial(null);
    setInterpretation(null);
    setFallbackState(null);
    setComingSoonCity(null);
    setWaitlistSent(false);
    setProviderDiscoveryResult(null);
    setProviderDiscoveryLoading(false);
    setShowRefinement(false);
    setRefinementParsed(null);
    // Sprint 14A — clear any open clarification card
    setClarification(null);
    setPendingCtx(undefined);

    // Sync canonical traveler state from the submitted search context
    if (ctx !== undefined) {
      setTravelerState({
        adults:       ctx.travelers,
        childrenAges: ctx.childrenAges,
        hasChildren:  ctx.hasChildren,
        travelerType: ctx.travelersType,
      });
    }

    // Sprint 4B: extract traveler counts from free-text (infants, children, adults)
    const { adults: _queryAdults, childrenAges: _queryChildren } = parseTravelersFromQuery(trimmed);

    if (_queryAdults !== null || _queryChildren.length > 0) {
      const _baseAdults   = _queryAdults ?? (ctx?.travelers ?? travelerState.adults);
      const _baseChildren = _queryChildren.length > 0
        ? _queryChildren
        : (ctx?.childrenAges ?? travelerState.childrenAges);
      setTravelerState({
        adults:       _baseAdults,
        childrenAges: _baseChildren,
        hasChildren:  _baseChildren.length > 0,
        travelerType: deriveTravelerType(_baseAdults, _baseChildren),
      });
    }
    const effectiveAdults   = _queryAdults ?? ctx?.travelers ?? travelerState.adults;
    const effectiveChildren = _queryChildren.length > 0
      ? _queryChildren
      : (ctx?.childrenAges ?? travelerState.childrenAges);
    const effectiveHasChildren = effectiveChildren.length > 0 || (ctx?.hasChildren ?? false);

    // Sprint 4B: early date inference — computed here (before LLM) so it can bypass
    // the "missing year" clarification card when a date range is deterministic.
    const _earlyDaysM = trimmed.match(/(\d+)\s*d[ií]as?/i);
    const _earlyDays  = _earlyDaysM ? Math.min(Math.max(parseInt(_earlyDaysM[1], 10), 1), 14) : 5;
    const _earlyInferredDates = parseDatesFromQuery(trimmed, _earlyDays);
    const deterministicParsed = parseQuery(trimmed);
    const forcedDestination = destinationIataParam && destinationNameParam
      ? { iata: destinationIataParam, city: destinationNameParam }
      : null;

    // ── Analytics: search intent ──────────────────────────────────────────
    const _hasDates = !!(_earlyInferredDates || dateSelection.travelDate);
    track("search_submitted", {
      has_date:       _hasDates,
      date_mode:      _earlyInferredDates ? "exact" : dateSelection.mode,
      has_children:   effectiveHasChildren,
      children_count: effectiveChildren.length,
      travelers:      effectiveAdults,
      source:         sourceSurface === "teaser" ? "teaser" : "manual",
    });
    // Launch Analytics & Funnel Instrumentation V1 — canonical funnel-step-1
    // event, fired alongside the legacy search_submitted above.
    // destination_iata/destination_name come from the same deterministic
    // parser already run above (deterministicParsed) — never the raw query.
    track("experiences_search_submitted", {
      query_present:     trimmed.length > 0,
      destination_iata:  forcedDestination?.iata || deterministicParsed?.iata || undefined,
      destination_name:  forcedDestination?.city || deterministicParsed?.city || undefined,
      adults:            effectiveAdults,
      has_dates:         _hasDates,
      source_surface:    sourceSurface,
    });
    // Admin Metrics UX V1 — same event, alongside PostHog.
    logMarketplaceEvent("experiences_search_submitted", {
      destination_iata: forcedDestination?.iata || deterministicParsed?.iata || undefined,
    });
    setRecentSearches(saveRecentSearch(trimmed).slice(0, 3));

    // ── Sprint 13B: LLM intent parser (Claude Haiku) ─────────────────────
    // Try the backend LLM parser first; fall back to the regex parser on any
    // network / timeout / API error. The LLM call is capped at 5 s internally.
    setParsingIntent(true);
    let llmResult: LLMParsedIntent | null = null;
    try {
      llmResult = await parseIntent(trimmed);
    } catch {
      // Ignore — will fall through to regex
    }
    setParsingIntent(false);

    // Sprint 14A (client fallback): If LLM failed/timed-out AND the query
    // contains a date with a month but no year, synthesise a year clarification
    // card so the user still gets asked instead of silently generating with a wrong date.
    if (!llmResult) {
      // Observe the fallback — only query_length is sent, never the raw query.
      track("intent_parser_fallback", { surface: "experiences", query_length: trimmed.length });
      // Admin Metrics UX V1 — same event, alongside PostHog (count only).
      logMarketplaceEvent("intent_parser_fallback", {});

      const HAS_MONTH_NO_YEAR =
        /\b\d{1,2}\s+de\s+(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre)\b/i.test(trimmed) &&
        !/\b20\d{2}\b/.test(trimmed);

      if (HAS_MONTH_NO_YEAR && !_earlyInferredDates) {
        const regexBase = deterministicParsed;
        if (regexBase && (regexBase.iata || regexBase.region)) {
          // Extract month+day so we can pre-fill the date after user picks a year
          const MONTH_MAP: Record<string, string> = {
            enero:"01", febrero:"02", marzo:"03", abril:"04", mayo:"05", junio:"06",
            julio:"07", agosto:"08", septiembre:"09", octubre:"10", noviembre:"11", diciembre:"12",
          };
          const mMatch = trimmed.match(/(\d{1,2})\s+de\s+(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre)/i);
          const partialDate: string | null = mMatch
            ? `-${MONTH_MAP[mMatch[2].toLowerCase()]}-${mMatch[1].padStart(2, "0")}`
            : null;

          // Construct a synthetic LLMParsedIntent from the regex result
          const synthetic = {
            city:            regexBase.city,
            iata:            regexBase.iata,
            days:            regexBase.days,
            hasDays:         regexBase.hasDays,
            interests:       regexBase.interests,
            pace:            (regexBase.pace ?? null) as "suave" | "equilibrado" | "intenso" | null,
            budget:          regexBase.budget ?? null,
            mobilityLevel:   (regexBase.mobilityLevel ?? "normal") as "normal" | "low" | "minimal",
            travelersType:   null as null,
            travel_date:     partialDate,
            region:          regexBase.region ?? null,
            regionLabel:     regexBase.regionLabel ?? null,
            interpretations: regexBase.interpretations,
            ambiguities:     ["año"],
            follow_up:       "¿En qué año planeas viajar?",
            parsed_by:       "llm" as const,
          };
          setClarification(synthetic);
          setPendingCtx(ctx);
          setError(null);
          setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
          return;
        }
      }
    }

    // Sprint 14A: LLM found genuine ambiguity → show mini clarification card.
    // Sprint 4B: skip year-only clarification when parseDatesFromQuery resolved the range.
    if (llmResult?.follow_up) {
      const _yearOnlyAmbiguity = llmResult.ambiguities.length > 0
        && llmResult.ambiguities.every(a => a === "año")
        && !!_earlyInferredDates;
      if (!_yearOnlyAmbiguity) {
        setClarification(llmResult);
        setPendingCtx(ctx);
        setError(null);
        setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
        return;
      }
    }

    // Convert LLM result → ParsedQuery shape.
    // Note: travel_date is NOT part of ParsedQuery — it is forwarded to generateSequence
    // via the extra.travel_date param so it can override dateSelection when user hasn't
    // picked a date via the DateFlexPicker.
    const pFromLLM: ParsedQuery | null =
      llmResult && (llmResult.iata || llmResult.region)
        ? {
            city:             llmResult.city,
            iata:             llmResult.iata,
            days:             llmResult.days,
            hasDays:          llmResult.hasDays,
            interests:        llmResult.interests,
            pace:             llmResult.pace ?? null,
            budget:           llmResult.budget ?? null,
            mobilityLevel:    llmResult.mobilityLevel,
            region:           llmResult.region ?? undefined,
            regionLabel:      llmResult.regionLabel ?? undefined,
            interpretations:  llmResult.interpretations,
          }
        : null;

    // Regex parser as fallback when LLM failed or returned no city/region.
    // If the deterministic parser found an explicit city, do not allow the LLM
    // to override it with a different destination.
    const p: ParsedQuery | null = (() => {
      const parsedCandidate = (() => {
        if (!pFromLLM) return deterministicParsed;
        if (
          deterministicParsed?.iata
          && (pFromLLM.region || (pFromLLM.iata && deterministicParsed.iata !== pFromLLM.iata))
        ) {
          return {
            ...pFromLLM,
            city:            deterministicParsed.city,
            iata:            deterministicParsed.iata,
            region:          deterministicParsed.region,
            regionLabel:     deterministicParsed.regionLabel,
            interests:       pFromLLM.interests.length > 0 ? pFromLLM.interests : deterministicParsed.interests,
            interpretations: [
              ...deterministicParsed.interpretations,
              ...pFromLLM.interpretations,
            ],
          };
        }
        return pFromLLM;
      })();

      if (!forcedDestination) return parsedCandidate;

      const base = parsedCandidate ?? {
        city: forcedDestination.city,
        iata: forcedDestination.iata,
        days: 5,
        hasDays: false,
        interests: [],
        pace: null,
        interpretations: [],
      };
      return {
        ...base,
        city: forcedDestination.city,
        iata: forcedDestination.iata,
        region: undefined,
        regionLabel: undefined,
      };
    })();

    // ── Phase 4: Wine-intent bypass — suppress MXP when wine/vineyard signals ──
    // "milan viñedos romance" → parseQuery resolves "milan" to MXP (Milán),
    // which has a gastronomy catalog but NO wine-country supply.  When the query
    // contains vineyard/wine keywords paired with MXP, the user's intent is
    // better served by the fallback engine's wine-country candidates (VRN/LNG/VOR/LCO).
    // Treating the query as unresolved redirects them to the FallbackPanel.
    const _WINE_BYPASS_RE = /vi[nñ]edos?|vineyard|bodegas?\b/i;
    const p_effective = (p && p.iata === "MXP" && _WINE_BYPASS_RE.test(trimmed)) ? null : p;

    if (!p_effective) {
      // ── Coming Soon — city not in catalog ──────────────────────────────
      // Instead of the old fallback flow (which could show unrelated cities),
      // we show a friendly "we're working on it" message.
      setLoading(false);
      setSequence(null);
      setError(null);
      setFallbackState(null);
      setProviderDiscoveryResult(null);
      const hint = _extractCityHint(trimmed);
      setComingSoonCity(hint);
      setComingSoonRaw(trimmed);
      setWaitlistSent(false);
      track("coming_soon_shown", { city_hint: hint, query_length: trimmed.length });
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
      return;
    }

    // Use the resolved (and bypass-filtered) parsed query from here on.
    const pResolved = p_effective;

    // Surface colloquial-term interpretation ("Sift Trip entendió 'rumba' como vida nocturna.")
    setInterpretation(buildInterpretationText(pResolved.interpretations));

    // ── Merge profile prefs into parsed query ────────────────────────────
    // Only fills gaps — query-provided values always win.
    let merged: ParsedQuery = profilePrefs
      ? {
          ...pResolved,
          interests: pResolved.interests.length > 0 ? pResolved.interests : profilePrefs.interests,
          pace:      pResolved.pace ?? profilePrefs.pace,
        }
      : pResolved;

    // Sprint 4B: update calendar chip UI with inferred dates (chip reads state, not the
    // explicit travel_date passed to generateSequence, so both updates are needed).
    // Recompute with merged.days for accuracy when it differs from _earlyDays.
    const _finalInferredDates = _earlyInferredDates
      ? (merged.days !== _earlyDays
          ? (parseDatesFromQuery(trimmed, merged.days) ?? _earlyInferredDates)
          : _earlyInferredDates)
      : null;
    if (_finalInferredDates) {
      if (_finalInferredDates.source === "explicit_range") {
        const rangeDays = inclusiveDayCount(_finalInferredDates.departure_date, _finalInferredDates.return_date);
        if (rangeDays !== null && rangeDays !== merged.days) {
          merged = { ...merged, days: rangeDays, hasDays: true };
        }
      }
      setDateSelection({
        mode:           "exact",
        travelDate:     _finalInferredDates.departure_date,
        departure_date: _finalInferredDates.departure_date,
        return_date:    _finalInferredDates.return_date,
      });
    }

    // Sprint 14: LLM gave a clean result (no follow_up) → generate directly.
    // Skip the IntentRefinementPanel — budget + adults are now in the left sidebar.
    if (pFromLLM) {
      setFamilyMode(effectiveHasChildren);
      setError(null);
      await generateSequence(merged, {
        travelers_type:  llmResult?.travelersType ?? undefined,
        travelers:       effectiveAdults,
        has_children:    effectiveHasChildren,
        children_ages:   effectiveChildren,
        pace:            merged.pace ?? undefined,
        budget_style:    merged.budget ?? budgetToStyle(sidebarBudgetMax) ?? undefined,
        budget_usd:      sidebarBudgetMax > 0 ? sidebarBudgetMax : undefined,
        mobility_level:  (merged.mobilityLevel as "normal" | "low" | "minimal") ?? undefined,
        travel_date:     _finalInferredDates?.departure_date ?? llmResult?.travel_date ?? undefined,
        rawQuery:        trimmed,
      });
      return;
    }

    // Regex fallback path — generate directly (skip refinement panel).
    // Budget + travelers live in the left sidebar; the heavy refinement panel is gone.
    setFamilyMode(effectiveHasChildren);
    setError(null);
    await generateSequence(merged, {
      travelers_type:  mapSearchBarTravelerType(ctx?.travelersType) ?? undefined,
      travelers:       effectiveAdults,
      has_children:    effectiveHasChildren,
      children_ages:   effectiveChildren,
      pace:            merged.pace ?? undefined,
      budget_style:    merged.budget ?? budgetToStyle(sidebarBudgetMax) ?? undefined,
      budget_usd:      sidebarBudgetMax > 0 ? sidebarBudgetMax : undefined,
      mobility_level:  (merged.mobilityLevel as "normal" | "low" | "minimal") ?? undefined,
      travel_date:     _finalInferredDates?.departure_date ?? undefined,
      rawQuery:        trimmed,
    });
  };

  // ── Sprint 12B: Confirm intent from refinement panel ──────────────────
  const handleConfirmIntent = async (draft: IntentDraft) => {
    setShowRefinement(false);
    setRefinementParsed(null);

    const rawQuery = query;

    // Rebuild ParsedQuery from draft (IntentDraft is the enriched form)
    const pFromDraft: ParsedQuery = {
      city:            draft.city,
      iata:            draft.iata,
      days:            draft.days,
      hasDays:         true,
      interests:       draft.interests,
      pace:            draft.pace ?? null,
      region:          draft.isRegion ? draft.city : undefined,
      interpretations: [],
      budget:          draft.budget,
      mobilityLevel:   draft.mobilityLevel,
    };

    // Region queries go direct to multi-city engine (no discovery panel)
    if (draft.isRegion) {
      await generateSequence(pFromDraft, {
        pace:           draft.pace ?? "equilibrado",
        budget_style:   draft.budget ?? undefined,
        mobility_level: draft.mobilityLevel ?? undefined,
        rawQuery,
        travelers:      draft.adults,
        travelers_type: draft.travelersType,
        has_children:   draft.hasChildren,
        children_ages:  draft.childrenAges,
      });
      return;
    }

    // Agentic Discovery — still active for NL queries with missing dims
    const { buildDiscoveryState } = await import("@/lib/experiences/discovery");
    const ds = buildDiscoveryState(rawQuery, pFromDraft, {
      pace:        profilePrefs?.pace,
      budgetStyle: profilePrefs?.budgetStyle,
    });

    if (ds.hasSignals && ds.questions.length > 0) {
      setError(null);
      setDiscoveryState(ds);
      setBuilderCity({ city: pFromDraft.city, iata: pFromDraft.iata });
      setBuilderInitial({
        days:        pFromDraft.hasDays ? pFromDraft.days : null,
        interests:   pFromDraft.interests,
        pace:        pFromDraft.pace,
        budgetStyle: profilePrefs?.budgetStyle ?? null,
      });
      setShowDiscovery(true);
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
      return;
    }

    // Generate with full draft enrichment
    setFamilyMode(draft.hasChildren);
    await generateSequence(pFromDraft, {
      pace:           draft.pace ?? "equilibrado",
      budget_style:   draft.budget ?? undefined,
      mobility_level: draft.mobilityLevel ?? undefined,
      rawQuery,
      travelers:      draft.adults,
      travelers_type: draft.travelersType,
      has_children:   draft.hasChildren,
      children_ages:  draft.childrenAges,
    });
  };

  // ── Sprint 14A: Clarification card callbacks ──────────────────────────

  /** User answered the year or days question → enrich and generate. */
  const handleClarificationResolve = async (enriched: LLMParsedIntent) => {
    const ctx = pendingCtx;
    setClarification(null);
    setPendingCtx(undefined);

    const p: ParsedQuery = {
      city:             enriched.city,
      iata:             enriched.iata,
      days:             enriched.days,
      hasDays:          enriched.hasDays,
      interests:        enriched.interests,
      pace:             enriched.pace ?? null,
      budget:           enriched.budget ?? null,
      mobilityLevel:    enriched.mobilityLevel,
      region:           enriched.region ?? undefined,
      regionLabel:      enriched.regionLabel ?? undefined,
      interpretations:  enriched.interpretations,
    };

    const merged: ParsedQuery = profilePrefs
      ? {
          ...p,
          interests: p.interests.length > 0 ? p.interests : profilePrefs.interests,
          pace:      p.pace ?? profilePrefs.pace,
        }
      : p;

    setInterpretation(buildInterpretationText(merged.interpretations));
    setFamilyMode(ctx?.hasChildren ?? false);
    setError(null);

    await generateSequence(merged, {
      travelers_type:  enriched.travelersType ?? undefined,
      travelers:       ctx?.travelers ?? 2,
      has_children:    ctx?.hasChildren ?? false,
      children_ages:   ctx?.childrenAges ?? [],
      pace:            merged.pace ?? undefined,
      budget_style:    merged.budget ?? budgetToStyle(sidebarBudgetMax) ?? undefined,
      budget_usd:      sidebarBudgetMax > 0 ? sidebarBudgetMax : undefined,
      mobility_level:  (merged.mobilityLevel as "normal" | "low" | "minimal") ?? undefined,
      travel_date:     enriched.travel_date ?? undefined,
      rawQuery:        query,
    });
  };

  /** User dismissed the clarification card → generate with LLM best-guess. */
  const handleClarificationDismiss = async () => {
    const llmRes = clarification;
    const ctx    = pendingCtx;
    setClarification(null);
    setPendingCtx(undefined);
    if (!llmRes) return;

    const p: ParsedQuery = {
      city:             llmRes.city,
      iata:             llmRes.iata,
      days:             llmRes.days,
      hasDays:          llmRes.hasDays,
      interests:        llmRes.interests,
      pace:             llmRes.pace ?? null,
      budget:           llmRes.budget ?? null,
      mobilityLevel:    llmRes.mobilityLevel,
      region:           llmRes.region ?? undefined,
      regionLabel:      llmRes.regionLabel ?? undefined,
      interpretations:  llmRes.interpretations,
    };

    if (!p.iata && !p.region) return; // no valid destination

    setInterpretation(buildInterpretationText(p.interpretations));
    setFamilyMode(ctx?.hasChildren ?? false);
    setError(null);

    await generateSequence(p, {
      travelers_type:  llmRes.travelersType ?? undefined,
      travelers:       ctx?.travelers ?? 2,
      has_children:    ctx?.hasChildren ?? false,
      children_ages:   ctx?.childrenAges ?? [],
      travel_date:     llmRes.travel_date ?? undefined,
      rawQuery:        query,
    });
  };

  // ── Sprint 14B: Sidebar "Actualizar búsqueda" ─────────────────────────

  const handleApplyFilters = async () => {
    if (!parsed) return;
    setSidebarDirty(false);
    await generateSequence(parsed, {
      ...lastExtraParams,
      travelers_type: travelerState.travelerType,
      travelers:      travelerState.adults,
      has_children:   travelerState.hasChildren,
      children_ages:  travelerState.childrenAges,
      budget_style:   budgetToStyle(sidebarBudgetMax) ?? undefined,
      budget_usd:     sidebarBudgetMax > 0 ? sidebarBudgetMax : undefined,
      rawQuery:       query,
    });
  };

  // ── Builder callbacks ──────────────────────────────────────────────────
  const handleBuilderComplete = async (result: BuilderResult) => {
    setShowBuilder(false);
    setBuilderCity(null);

    // Intent Persistence Layer V1 — consume and clear the pending snapshot.
    // Use snapshot.original_query as rawQuery so the archetype detector on the
    // backend receives the user's original search (e.g. "Milan vinedos romance")
    // rather than just the selected candidate city name (e.g. "Val d'Orcia").
    const snap = pendingSnapshot;
    setPendingSnapshot(null);

    const p: ParsedQuery = {
      city:            result.city,
      iata:            result.cityIata,
      days:            result.days,
      hasDays:         true,  // builder always produces a valid day count
      interests:       result.interests,
      pace:            result.pace,
      interpretations: [],    // builder values are already canonical
    };
    setFamilyMode(false); // builder flow has no children context
    await generateSequence(p, {
      travelers:       result.travelers,
      travelers_type:  result.travelersType,
      pace:            result.pace,
      budget_style:    result.budgetStyle,
      // Intent Persistence Layer V1 — original_query takes priority so archetype
      // matching uses the full user intent (e.g. "Milan romantico vinedos").
      // Falls back to the builder-assembled string when no snapshot is available.
      rawQuery:        snap?.original_query
                         || query
                         || `${result.city} ${result.interests.join(" ")}`.trim(),
      intent_snapshot: snap ?? null,
      // Phase 5: when a snapshot exists, we came from a supply-first fallback
      // candidate — suppress the catalog-error fallback re-fetch to prevent loops.
      skip_fallback_on_error: snap !== null,
    });
  };

  const handleBuilderClose = () => {
    setShowBuilder(false);
    setBuilderCity(null);
  };

  // ── Discovery panel callbacks ──────────────────────────────────────────
  const handleDiscoveryComplete = async (result: DiscoveryResult) => {
    setShowDiscovery(false);
    setDiscoveryState(null);

    // Intent Persistence Layer V1 — consume and clear the pending snapshot.
    const snap = pendingSnapshot;
    setPendingSnapshot(null);

    const hasDays      = builderInitial?.days != null;
    const hasInterests = (builderInitial?.interests.length ?? 0) > 0;

    if (hasDays && hasInterests) {
      // All structural fields known — call API directly
      const p: ParsedQuery = {
        city:            result.city,
        iata:            result.cityIata,
        days:            builderInitial!.days!,
        hasDays:         true,
        interests:       builderInitial!.interests,
        pace:            result.pace,
        interpretations: [],
      };
      await generateSequence(p, {
        travelers_type:  result.travelersType,
        pace:            result.pace,
        budget_style:    result.budgetStyle,
        rawQuery:        snap?.original_query
                           || query
                           || `${result.city} ${builderInitial!.interests.join(" ")}`.trim(),
        intent_snapshot: snap ?? null,
      });
    } else {
      // Days or interests missing — open GuidedBuilder with intent pre-fills
      setBuilderInitial({
        days:        builderInitial?.days ?? null,
        interests:   builderInitial?.interests ?? [],
        pace:        result.pace,
        budgetStyle: result.budgetStyle,
      });
      setShowBuilder(true);
    }
  };

  const handleDiscoveryClose = () => {
    setShowDiscovery(false);
    setDiscoveryState(null);
    setBuilderCity(null);
  };

  // Provider Destination Discovery V2 -- candidate selection
  const handleProviderCandidateSelect = (candidate: ProviderDestinationCandidate) => {
    const snap: IntentSnapshot = {
      original_query:          query,
      detected_city:           candidate.destination_name,
      archetype_id:            null,
      archetype_label:         null,
      confidence:              null,
      intent_tags:             candidate.affordances ?? [],
      preferred_dimensions:    {},
      avoid_dimensions:        [],
      mood_hints:              [],
      source:                  "provider_destination_discovery",
      provider_name:           candidate.provider_name,
      provider_destination_id: candidate.provider_destination_id,
      candidate_source:        candidate.candidate_source,
    };
    setPendingSnapshot(snap);
    setProviderDiscoveryResult(null);
    setBuilderCity({ city: candidate.destination_name, iata: "" });
    setBuilderInitial({
      days:        null,
      interests:   candidate.affordances ?? [],
      pace:        null,
      budgetStyle: null,
    });
    setShowBuilder(true);
    setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const runTeaser = (tq: string) => { setQuery(tq); handleSubmit(tq, undefined, "teaser"); };
  const showTeasers = !sequence && !loading && !showBuilder && !showDiscovery && !fallbackState && !comingSoonCity && !providerDiscoveryResult && !providerDiscoveryLoading && !inspireMode && !showRefinement && !clarification;

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: "var(--color-background)" }}>
      <RheaPrototypeHeader mode="workspace" />

      <div className="pt-16">
      {/* ── Search bar ──────────────────────────────────────────────────── */}
      <TravelSearchBar
        query={query}
        onQueryChange={setQuery}
        onSubmit={handleSubmit}
        loading={loading || parsingIntent}
        dateSelection={dateSelection}
        onDateChange={setDateSelection}
        profilePrefs={profilePrefs ?? undefined}
        onClearProfilePrefs={() => setProfilePrefs(null)}
        interpretation={interpretation}
        error={error}
        onRetry={retryLastSearch}
        onNewSearch={resetToNewSearch}
        compact={Boolean(
          sequence || loading || showBuilder || showDiscovery ||
          fallbackState || comingSoonCity || providerDiscoveryResult || providerDiscoveryLoading || showRefinement
        )}
        onModeChange={(mode) => setInspireMode(mode === "inspire")}
        initialAdults={travelerState.adults}
        initialChildrenAges={travelerState.childrenAges}
        initialTravelerType={travelerState.travelerType}
        externalAdults={travelerState.adults}
        externalChildrenAges={travelerState.childrenAges}
        externalTravelerType={travelerState.travelerType}
        onTravelersChange={(ctx) => setTravelerState({
          adults:       ctx.travelers,
          childrenAges: ctx.childrenAges,
          hasChildren:  ctx.hasChildren,
          travelerType: ctx.travelersType,
        })}
      />

      {/* ── Content area ──────────────────────────────────────────────────── */}
      <div ref={resultsRef} className={`relative z-10 px-3 sm:px-5 pb-28 mx-auto ${sequence && !loading ? 'max-w-[1440px]' : 'max-w-3xl'}`} style={{ scrollMarginTop: "88px" }}>

        {/* ── Sprint 14A: IntentClarificationCard (year / days ambiguity) ─── */}
        <AnimatePresence>
          {clarification && !loading && (
            <IntentClarificationCard
              llmResult={clarification}
              currentYear={new Date().getFullYear()}
              onResolve={(enriched) => { void handleClarificationResolve(enriched); }}
              onDismiss={() => { void handleClarificationDismiss(); }}
            />
          )}
        </AnimatePresence>

        {/* ── Sprint 12B: Intent Refinement Panel (lazy — Sprint 12C) ─────── */}
        <AnimatePresence>
          {showRefinement && refinementParsed && (
            <motion.div
              key="refinement"
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
              className="mt-2"
            >
              <Suspense fallback={
                <div className="h-48 rounded-2xl animate-pulse" style={{ background: "oklch(0.972 0.010 75)" }} />
              }>
              <IntentRefinementPanel
                parsed={refinementParsed}
                loading={loading}
                initialAdults={refinementCtx?.travelers ?? travelerState.adults}
                initialTravelersType={mapSearchBarTravelerType(refinementCtx?.travelersType ?? urlCtx?.travelersType)}
                initialHasChildren={refinementCtx?.hasChildren ?? false}
                initialChildrenAges={refinementCtx?.childrenAges ?? []}
                onConfirm={handleConfirmIntent}
                onEdit={() => {
                  setShowRefinement(false);
                  setRefinementParsed(null);
                }}
              />
              </Suspense>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Agentic Discovery Panel — split chunk, loaded on first NL query */}
        <AnimatePresence>
          {showDiscovery && discoveryState && builderCity && (
            <motion.div
              key="discovery"
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
            >
              <Suspense fallback={
                <div
                  className="rounded-3xl"
                  style={{
                    height:     "220px",
                    background: "oklch(0.994 0.008 72 / 0.97)",
                    border:     "1px solid oklch(0.880 0.024 72 / 0.55)",
                  }}
                />
              }>
                <DiscoveryPanel
                  city={builderCity.city}
                  cityIata={builderCity.iata}
                  discoveryState={discoveryState}
                  onComplete={handleDiscoveryComplete}
                  onClose={handleDiscoveryClose}
                />
              </Suspense>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Guided Builder — split chunk, loaded on first partial query */}
        <AnimatePresence>
          {showBuilder && builderCity && !showDiscovery && (
            <motion.div
              key="builder"
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
            >
              <Suspense fallback={
                <div
                  className="rounded-3xl"
                  style={{
                    height:     "320px",
                    background: "oklch(0.994 0.008 72 / 0.97)",
                    border:     "1px solid oklch(0.880 0.024 72 / 0.55)",
                  }}
                />
              }>
                <GuidedExperienceBuilder
                  city={builderCity.city}
                  cityIata={builderCity.iata}
                  initialDays={builderInitial?.days ?? null}
                  initialInterests={builderInitial?.interests ?? []}
                  initialPace={builderInitial?.pace ?? null}
                  initialBudgetStyle={builderInitial?.budgetStyle ?? null}
                  onComplete={handleBuilderComplete}
                  onClose={handleBuilderClose}
                />
              </Suspense>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Fallback Intelligence V1 — unknown city panel */}
        <AnimatePresence>
          {fallbackState && !loading && (
            <motion.div
              key="fallback"
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
            >
              <FallbackPanel
                result={fallbackState}
                onExplore={(candidate, intentTags) => {
                  // Intent Persistence Layer V1 — capture the snapshot before
                  // clearing fallbackState so we can forward it to the sequence generator.
                  const snap = fallbackState?.intent_snapshot ?? null;
                  setPendingSnapshot(snap);
                  setFallbackState(null);
                  setBuilderCity({ city: candidate.display, iata: candidate.iata });
                  setBuilderInitial({
                    days:        null,
                    // Use rich archetype intent_tags from snapshot when available;
                    // fall back to the generic dim-based intentTags otherwise.
                    interests:   (snap?.intent_tags && snap.intent_tags.length > 0)
                                   ? snap.intent_tags
                                   : intentTags,
                    pace:        null,
                    budgetStyle: null,
                  });
                  setShowBuilder(true);
                  setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
                }}
                onDismiss={() => setFallbackState(null)}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Provider Discovery Loading */}
        <AnimatePresence>
          {providerDiscoveryLoading && (
            <motion.div
              key="prov-disc-loading"
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }} transition={{ duration: 0.30 }}
            >
              <div
                className="rounded-3xl p-5 mt-2 flex items-center gap-4"
                style={{
                  background: "oklch(0.994 0.008 72 / 0.97)",
                  border:     "1px solid oklch(0.880 0.024 72 / 0.55)",
                }}
              >
                <PulsingDot />
                <p className="text-sm" style={{ color: "oklch(0.545 0.020 60 / 0.70)" }}>
                  Buscando destinos reales que encajen con tu intención…
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Coming Soon — ciudad no en catálogo */}
        <AnimatePresence>
          {comingSoonCity !== null && !loading && (
            <motion.div
              key="coming-soon"
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
            >
              <CityComingSoonPanel
                cityName={comingSoonCity}
                rawQuery={comingSoonRaw}
                waitlistSent={waitlistSent}
                onWaitlist={async () => {
                  try {
                    await notifyWaitlist({ destination: comingSoonCity ?? comingSoonRaw });
                    setWaitlistSent(true);
                    track("waitlist_joined", { city_hint: comingSoonCity });
                  } catch {
                    setWaitlistSent(true); // optimistic — don't block UX on network error
                  }
                }}
                onDismiss={() => { setComingSoonCity(null); setWaitlistSent(false); }}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Provider Destination Discovery V2 */}
        <AnimatePresence>
          {providerDiscoveryResult && !loading && !providerDiscoveryLoading && (
            <motion.div
              key="prov-discovery"
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
            >
              <ProviderDiscoveryPanel
                result={providerDiscoveryResult}
                onSelect={handleProviderCandidateSelect}
                onDismiss={() => setProviderDiscoveryResult(null)}
              />
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {loading && parsed && (
            <motion.div
              key="loading" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              <LoadingState parsed={parsed} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Results — 3-column workspace managed inside ExperienceTimeline ── */}
        <AnimatePresence>
          {sequence && !loading && (
            <motion.div
              key="results" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              exit={{ opacity: 0 }} transition={{ duration: 0.30 }}
            >
              <ExperienceTimeline
                sequence={sequence}
                rawQuery={query}
                travelDate={dateSelection.travelDate}
                familyMode={familyMode}
                adults={travelerState.adults}
                children={travelerState.childrenAges.length}
                childrenAges={travelerState.childrenAges}
                onNewSearch={resetToNewSearch}
                onTryDestination={tryDestination}
                sidebarDirty={sidebarDirty}
                sidebarLoading={loading}
                onSidebarAdultsChange={(n) => {
                  setTravelerState(prev => ({
                    ...prev,
                    adults:       n,
                    hasChildren:  prev.childrenAges.length > 0,
                    travelerType: deriveTravelerType(n, prev.childrenAges),
                  }));
                  setSidebarDirty(true);
                }}
                onSidebarApply={() => { void handleApplyFilters(); }}
                filters={itineraryFilters}
                onFiltersChange={setItineraryFilters}
              />
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showTeasers && (
            <motion.div
              key="teasers" initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.50, delay: 0.28, ease: [0.22, 1, 0.36, 1] }}
            >
              {recentSearches.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-2.5">
                    <div className="flex-1 h-px" style={{ background: "oklch(0.880 0.024 72 / 0.30)" }} />
                    <p className="text-[10px] uppercase tracking-[0.18em] font-bold shrink-0"
                      style={{ color: "oklch(0.545 0.020 60 / 0.35)" }}>
                      Búsquedas recientes
                    </p>
                    <div className="flex-1 h-px" style={{ background: "oklch(0.880 0.024 72 / 0.30)" }} />
                  </div>
                  <RecentSearchStrip
                    searches={recentSearches}
                    onSelect={(q) => {
                      track("recent_search_clicked", { query_length: q.length });
                      setQuery(q);
                      handleSubmit(q, undefined, "recent_search");
                    }}
                    onRemove={(q) => {
                      track("recent_search_removed", { query_length: q.length });
                      setRecentSearches(removeRecentSearch(q).slice(0, 3));
                    }}
                  />
                </div>
              )}
              <TeaserSection onSelect={runTeaser} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      </div>
    </div>
  );
}

// ─── Fallback Intelligence V1 — FallbackPanel ────────────────────────────────

interface FallbackPanelProps {
  result:     FallbackResult;
  onExplore:  (candidate: FallbackCandidate, intentTags: string[]) => void;
  onDismiss:  () => void;
}

function FallbackPanel({ result, onExplore, onDismiss }: FallbackPanelProps) {
  return (
    <div
      className="rounded-3xl p-6 sm:p-8 mt-2"
      style={{
        background: "oklch(0.994 0.008 72 / 0.97)",
        border:     "1px solid oklch(0.880 0.024 72 / 0.55)",
        boxShadow:  "0 2px 18px #E8194B14",
      }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          {/* Small eyebrow */}
          <p
            className="text-[10px] uppercase tracking-[0.22em] font-bold mb-1.5"
            style={{ color: "#E8194BB2" }}
          >
            {result.detected_city ? `${result.detected_city} no está en el catálogo aún` : "Destino fuera del catálogo actual"}
          </p>
          <h3
            className="font-display text-lg sm:text-xl font-bold leading-tight"
            style={{ color: "var(--color-heading-navy)" }}
          >
            {result.message}
          </h3>
          {result.detected_intent.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {result.detected_intent.map(label => (
                <span
                  key={label}
                  className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full"
                  style={{
                    background: "#E8194B1A",
                    color:      "#E8194B",
                    border:     "1px solid #E8194B38",
                  }}
                >
                  {label}
                </span>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 w-7 h-7 flex items-center justify-center rounded-full transition-all hover:scale-110"
          style={{ background: "oklch(0.880 0.024 72 / 0.50)", color: "oklch(0.45 0.012 55 / 0.60)" }}
          aria-label="Cerrar"
        >
          ✕
        </button>
      </div>

      {/* Candidate grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {result.candidates.slice(0, 4).map((c) => (
          <button
            key={c.iata}
            type="button"
            onClick={() => onExplore(c, result.intent_tags)}
            className="group text-left rounded-2xl p-4 transition-all duration-200 hover:scale-[1.015] active:scale-[0.99]"
            style={{
              background: "oklch(1.000 0 0 / 0.80)",
              border:     "1px solid oklch(0.880 0.024 72 / 0.55)",
              boxShadow:  "0 1px 6px oklch(0.40 0.012 50 / 0.06)",
            }}
          >
            <div className="flex items-center gap-2.5 mb-2">
              <FlagImg flag={c.flag} size={20} className="shrink-0 rounded-sm" />
              <div className="min-w-0">
                <p
                  className="text-sm font-black leading-tight truncate"
                  style={{ color: "var(--color-heading-navy)" }}
                >
                  {c.display}
                </p>
                <p
                  className="text-[11px] truncate"
                  style={{ color: "oklch(0.545 0.020 60 / 0.55)" }}
                >
                  {c.country}
                </p>
              </div>
              <span
                className="ml-auto shrink-0 text-[11px] font-bold opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ color: "#E8194B" }}
              >
                Explorar →
              </span>
            </div>
            {/* Reason tags */}
            <div className="flex flex-wrap gap-1">
              {c.reason.split(" + ").map((r, i) => (
                <span
                  key={i}
                  className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                  style={{
                    background: "oklch(0.988 0.010 70 / 0.85)",
                    color:      "oklch(0.45 0.020 60 / 0.70)",
                    border:     "1px solid oklch(0.880 0.024 72 / 0.45)",
                  }}
                >
                  {r}
                </span>
              ))}
            </div>
          </button>
        ))}
      </div>

      {/* Footer attribution */}
      <p
        className="mt-4 text-center text-[10px]"
        style={{ color: "oklch(0.545 0.020 60 / 0.38)" }}
      >
        Destinos disponibles en el catálogo Sift Trip · Selecciona uno para personalizar tu selección
      </p>
    </div>
  );
}



// ─── Coming Soon panel — ciudad no en catálogo ───────────────────────────────

interface CityComingSoonPanelProps {
  cityName:    string | null;
  rawQuery:    string;
  waitlistSent: boolean;
  onWaitlist:  () => void;
  onDismiss:   () => void;
}

function CityComingSoonPanel({ cityName, waitlistSent, onWaitlist, onDismiss }: CityComingSoonPanelProps) {
  const label = cityName ?? "este destino";
  return (
    <div
      className="rounded-3xl p-6 sm:p-8 mt-2"
      style={{
        background: "oklch(0.994 0.008 72 / 0.97)",
        border:     "1px solid oklch(0.880 0.024 72 / 0.55)",
        boxShadow:  "0 2px 18px #E8194B14",
      }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          {/* Compass icon */}
          <div
            className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0"
            style={{ background: "oklch(0.965 0.020 72 / 0.80)" }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#E8194B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/>
            </svg>
          </div>
          <div>
            <p
              className="text-[10px] uppercase tracking-[0.22em] font-bold mb-0.5"
              style={{ color: "#E8194BA6" }}
            >
              Próximamente
            </p>
            <h3
              className="text-base font-bold leading-tight"
              style={{ color: "oklch(0.22 0.015 60)" }}
            >
              Estamos preparando experiencias para {label}
            </h3>
          </div>
        </div>
        <button
          onClick={onDismiss}
          className="shrink-0 mt-0.5 w-6 h-6 rounded-full flex items-center justify-center hover:opacity-70 transition-opacity"
          style={{ background: "oklch(0.880 0.024 72 / 0.35)" }}
          aria-label="Cerrar"
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M1 1L9 9M9 1L1 9" stroke="oklch(0.45 0.02 60)" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </button>
      </div>

      {/* Body */}
      <p
        className="text-sm leading-relaxed mb-5"
        style={{ color: "oklch(0.40 0.015 60 / 0.75)" }}
      >
        Todavía no tenemos inventario suficiente para este destino.
        Mientras tanto, puedes explorar las ciudades disponibles en el catálogo.
      </p>

      {/* Waitlist CTA */}
      {waitlistSent ? (
        <div
          className="flex items-center gap-2 px-4 py-3 rounded-2xl text-sm font-medium"
          style={{
            background: "oklch(0.965 0.035 152 / 0.20)",
            color:      "oklch(0.40 0.12 152)",
            border:     "1px solid oklch(0.70 0.12 152 / 0.25)",
          }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M2 7L5.5 10.5L12 3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Te avisaremos cuando {label} esté disponible.
        </div>
      ) : (
        <button
          onClick={onWaitlist}
          className="w-full sm:w-auto px-6 py-3 rounded-2xl text-sm font-semibold transition-all hover:opacity-90 active:scale-[0.98]"
          style={{
            background: "oklch(0.22 0.015 60)",
            color:      "oklch(0.98 0.005 72)",
            boxShadow:  "0 2px 10px oklch(0.22 0.015 60 / 0.25)",
          }}
        >
          Avisarme cuando esté disponible
        </button>
      )}

      {/* Footer hint */}
      <p
        className="mt-4 text-[11px]"
        style={{ color: "oklch(0.545 0.020 60 / 0.45)" }}
      >
        Disponibilidad pendiente de confirmar · {label} no está en el catálogo actual
      </p>
    </div>
  );
}


// ─── Provider Destination Discovery V2 — ProviderDiscoveryPanel ─────────────

interface ProviderDiscoveryPanelProps {
  result:    ProviderDestinationDiscoveryResult;
  onSelect:  (candidate: ProviderDestinationCandidate) => void;
  onDismiss: () => void;
}

function ProviderDiscoveryPanel({ result, onSelect, onDismiss }: ProviderDiscoveryPanelProps) {
  const hasItems = result.candidates.length > 0;

  return (
    <div
      className="rounded-3xl p-6 sm:p-8 mt-2"
      style={{
        background: "oklch(0.994 0.008 72 / 0.97)",
        border:     "1px solid oklch(0.880 0.024 72 / 0.55)",
        boxShadow:  "0 2px 18px #E8194B14",
      }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <p
            className="text-[10px] uppercase tracking-[0.22em] font-bold mb-1.5"
            style={{ color: "#E8194BB2" }}
          >
            Destinos respaldados por proveedor
          </p>
          <h3
            className="font-display text-lg sm:text-xl font-bold leading-tight"
            style={{ color: "var(--color-heading-navy)" }}
          >
            {hasItems
              ? "Encontramos destinos con oferta real para tu intención"
              : "No encontramos candidatos exactos"}
          </h3>
          {hasItems && result.product_signal_count > 0 && (
            <p className="mt-1.5 text-[11px]" style={{ color: "oklch(0.545 0.020 60 / 0.55)" }}>
              {result.product_signal_count} experiencias analizadas en tiempo real
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 w-7 h-7 flex items-center justify-center rounded-full transition-all hover:scale-110"
          style={{ background: "oklch(0.880 0.024 72 / 0.50)", color: "oklch(0.45 0.012 55 / 0.60)" }}
          aria-label="Cerrar"
        >
          ✕
        </button>
      </div>

      {/* Candidate grid */}
      {hasItems && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {result.candidates.map((c, idx) => (
            <button
              key={c.provider_destination_id || `${c.destination_name}-${idx}`}
              type="button"
              onClick={() => onSelect(c)}
              className="group text-left rounded-2xl p-4 transition-all duration-200 hover:scale-[1.015] active:scale-[0.99]"
              style={{
                background: "oklch(1.000 0 0 / 0.80)",
                border:     "1px solid oklch(0.880 0.024 72 / 0.55)",
                boxShadow:  "0 1px 6px oklch(0.40 0.012 50 / 0.06)",
              }}
            >
              {/* Name + real-offer badge */}
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <p
                    className="text-sm font-black leading-tight"
                    style={{ color: "var(--color-heading-navy)" }}
                  >
                    {c.destination_name}
                  </p>
                  <p
                    className="text-[11px] mt-0.5"
                    style={{ color: "oklch(0.545 0.020 60 / 0.55)" }}
                  >
                    {c.country}{c.region && c.region !== "Other" ? ` · ${c.region}` : ""}
                  </p>
                </div>
                <span
                  className="shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
                  style={{
                    background: "oklch(0.298 0.058 167 / 0.09)",
                    color:      "oklch(0.298 0.058 167)",
                    border:     "1px solid oklch(0.298 0.058 167 / 0.22)",
                  }}
                >
                  Destino con oferta real
                </span>
              </div>

              {/* Product-backed provenance badge */}
              {c.candidate_source === "product_backed" && (
                <div
                  className="flex items-center gap-1.5 mb-2 text-[10px] font-semibold"
                  style={{ color: "oklch(0.45 0.10 167)" }}
                >
                  <span>✓</span>
                  <span>Respaldado por experiencias reales</span>
                </div>
              )}

              {/* Affordance chips */}
              {c.affordances.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-1.5">
                  {c.affordances.slice(0, 4).map(a => (
                    <span
                      key={a}
                      className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                      style={{
                        background: "oklch(0.988 0.010 70 / 0.85)",
                        color:      "oklch(0.45 0.020 60 / 0.70)",
                        border:     "1px solid oklch(0.880 0.024 72 / 0.45)",
                      }}
                    >
                      {a}
                    </span>
                  ))}
                </div>
              )}

              {/* Product signal evidence */}
              {c.product_hit_count > 0 && (
                <p className="text-[10px] mt-0.5" style={{ color: "oklch(0.545 0.020 60 / 0.45)" }}>
                  {c.product_hit_count} {c.product_hit_count === 1 ? "experiencia" : "experiencias"} disponibles
                </p>
              )}

              {/* Explore CTA */}
              <p
                className="mt-2 text-[11px] font-bold opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ color: "#E8194B" }}
              >
                Explorar →
              </p>
            </button>
          ))}
        </div>
      )}

      {/* Empty state: expanded queries + reason */}
      {!hasItems && (
        <div className="space-y-4">
          {(result.expanded_queries?.length ?? 0) > 0 && (
            <div>
              <p
                className="text-[11px] font-semibold mb-2"
                style={{ color: "oklch(0.545 0.020 60 / 0.60)" }}
              >
                También buscamos:
              </p>
              <div className="flex flex-wrap gap-1.5">
                {result.expanded_queries!.map(q => (
                  <span
                    key={q}
                    className="text-[10px] px-2.5 py-1 rounded-full font-medium"
                    style={{
                      background: "oklch(0.988 0.010 70 / 0.85)",
                      color:      "oklch(0.45 0.020 60 / 0.60)",
                      border:     "1px solid oklch(0.880 0.024 72 / 0.45)",
                    }}
                  >
                    {q}
                  </span>
                ))}
              </div>
            </div>
          )}
          {result.empty_reason && (
            <p className="text-[11px]" style={{ color: "oklch(0.545 0.020 60 / 0.50)" }}>
              {result.empty_reason}
            </p>
          )}
        </div>
      )}

      {/* Footer — no booking CTA at discovery stage */}
      <p
        className="mt-5 text-center text-[10px]"
        style={{ color: "oklch(0.545 0.020 60 / 0.38)" }}
      >
        La reserva aparece después, cuando elijas una experiencia real · Destino respaldado por proveedor
      </p>
    </div>
  );
}

// ─── Loading state ────────────────────────────────────────────────────────────

function LoadingState({ parsed }: { parsed: ParsedQuery }) {
  const [msgIdx, setMsgIdx] = useState(0);
  useEffect(() => {
    setMsgIdx(0);
    const id = setInterval(() => setMsgIdx(i => (i + 1) % LOADING_MESSAGES.length), 2200);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="pt-2">
      <div className="mb-6 text-center">
        <div className="flex items-center justify-center gap-2 mb-1.5">
          <PulsingDot />
          <p className="text-sm font-semibold" style={{ color: "var(--color-heading-navy)" }}>
            {parsed.city}
          </p>
        </div>
        <AnimatePresence mode="wait">
          <motion.p
            key={msgIdx}
            initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }}
            transition={{ duration: 0.28, ease: "easeOut" }}
            className="text-xs h-4" style={{ color: "oklch(0.545 0.020 60 / 0.55)" }}
          >
            {LOADING_MESSAGES[msgIdx]}
          </motion.p>
        </AnimatePresence>
      </div>
      {[0, 1, 2].map(i => <SkeletonDayCard key={i} delay={i * 0.10} />)}
    </div>
  );
}

function PulsingDot() {
  return (
    <div className="relative shrink-0 w-2 h-2">
      <motion.div
        className="absolute inset-0 rounded-full" style={{ background: "oklch(0.580 0.220 256)" }}
        animate={{ scale: [1, 2.4, 1], opacity: [0.85, 0, 0.85] }}
        transition={{ duration: 1.7, repeat: Infinity, ease: "easeInOut" }}
      />
      <div className="absolute inset-0 rounded-full" style={{ background: "oklch(0.580 0.220 256)" }} />
    </div>
  );
}

function SkeletonDayCard({ delay }: { delay: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.40, delay, ease: [0.22, 1, 0.36, 1] }}
      className="flex gap-3 sm:gap-4 mb-5"
    >
      <div className="shrink-0 pt-1"><SkeletonBlock w={42} h={42} rounded="full" /></div>
      <div className="flex-1">
        {/* Mood strip */}
        <div className="mb-2 flex items-center gap-2">
          <SkeletonBlock w={24} h={16} />
          <SkeletonBlock w="38%" h={12} />
        </div>
        <div className="rounded-2xl overflow-hidden" style={{ background: "oklch(0.988 0.006 70 / 0.72)", border: "1px solid oklch(0.880 0.024 72 / 0.38)" }}>
          <div className="px-5 py-4 flex items-center gap-3 border-b border-[oklch(0.880_0.024_72_/_0.25)]">
            <div className="flex-1 space-y-1.5"><SkeletonBlock w="48%" h={14} /><SkeletonBlock w="30%" h={10} /></div>
            <SkeletonBlock w={72} h={22} rounded="full" />
          </div>
          {[0, 1, 2].map(j => (
            <div key={j} className="px-5 py-4 border-b last:border-0 border-[oklch(0.880_0.024_72_/_0.16)] flex gap-3">
              <SkeletonBlock w={32} h={32} rounded="xl" />
              <SkeletonBlock w={56} h={56} rounded="xl" />
              <div className="flex-1 space-y-2 pt-0.5">
                <SkeletonBlock w="32%" h={10} />
                <SkeletonBlock w="58%" h={13} />
                <SkeletonBlock w="75%" h={10} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

function SkeletonBlock({ w, h, rounded = "md" }: { w: number | string; h: number; rounded?: string }) {
  return (
    <motion.div
      className={`rounded-${rounded} shrink-0`}
      style={{ width: typeof w === "number" ? `${w}px` : w, height: `${h}px`, background: "oklch(0.875 0.016 72 / 0.58)", minWidth: typeof w === "number" ? `${w}px` : undefined }}
      animate={{ opacity: [0.42, 0.78, 0.42] }}
      transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
    />
  );
}

// ─── Filter types ─────────────────────────────────────────────────────────────
// ActiveFilters/DurationFilter/SortBy and every pure filter/sort helper now
// live in components/experiences/marketplace/marketplaceFilters.ts (see
// Marketplace Filter + Sorting Confidence V1) — imported above — so they're
// directly unit-testable and shared by both FilterBar (mobile) and
// ItineraryFilterChipPanel (desktop) from one definition.

// ─── FilterBar component ──────────────────────────────────────────────────────

function FilterBar({
  sequence,
  filters,
  onChange,
  dynamicMaxPriceOverride,
  tipBasedCountOverride,
}: {
  sequence: ExperienceSequence;
  filters:  ActiveFilters;
  onChange: (f: ActiveFilters) => void;
  /** Marketplace Inventory Scale & Discovery V1, task 8 — when
   *  /experiences/search is active, the real max price across the FULL
   *  matching pool (facets.max_price_available, already rounded/capped)
   *  replaces the value derived from whatever's loaded so far. */
  dynamicMaxPriceOverride?: number | null;
  /** Marketplace Continuity, Honest Pricing & Log Privacy V1, Parte B —
   *  same override pattern as dynamicMaxPriceOverride: the real tip_based
   *  count across the FULL price-ceiling-free pool (facets.tip_based_count)
   *  when backend search is active. */
  tipBasedCountOverride?: number;
}) {
  // Frontend Marketplace Integration V1 — filters derive their available
  // options from the same flat marketplace source (marketplace_inventory
  // when present, else flattened days[]) that the inventory list itself
  // uses, so a chip never appears for a value that isn't actually in the
  // list being filtered.
  const { items: allItems } = getMarketplaceSource(sequence);
  // Provider-Only Marketplace & Comparison V2 Foundation, Part B #6 — "Con
  // proveedor" (and the "Proveedor ↑" sort, its exact corollary) are no-op
  // filters once provider-only is the permanent launch default: every
  // visible item already IS a provider, so the toggle would sit permanently
  // "active" with nothing left to filter — confusing, not useful. Hidden
  // only in launch mode; the non-launch (show-local-references) mode keeps
  // both exactly as before.
  const showLocalRefs = isShowLocalReferencesEnabled();

  // Derive categories present in the sequence
  const presentCategories = Array.from(
    new Set(allItems.map(e => e.category).filter(Boolean))
  ).sort();

  // Derive available duration buckets
  const allDurations: DurationFilter[] = [];
  const hasShort  = allItems.some(e => (e.duration_hours ?? 0) > 0 && (e.duration_hours ?? 0) < 2);
  const hasMedium = allItems.some(e => (e.duration_hours ?? 0) >= 2 && (e.duration_hours ?? 0) <= 4);
  const hasLong   = allItems.some(e => (e.duration_hours ?? 0) > 4);
  if (hasShort)  allDurations.push("short");
  if (hasMedium) allDurations.push("medium");
  if (hasLong)   allDurations.push("long");

  // Derive time-of-day values present in the sequence
  const presentTimeOfDays = Array.from(
    new Set(allItems.map(e => e.time_of_day).filter((t): t is string => !!t && t !== "any"))
  );

  // Derive if any reservable items exist
  const hasReservable   = allItems.some(e => e.availability_confirmed === true);
  const hasLiveProvider = allItems.some(e => e.source_class === "LIVE_PROVIDER");

  // Marketplace Filters V2 — quick "Hasta $X" price chips, derived from the
  // full inventory (counts always reflect the full marketplace_inventory,
  // per product spec, not whatever's currently filtered by other chips).
  // Marketplace Inventory Scale & Discovery V1, task 8: dynamicMaxPriceOverride
  // (from the backend's facets.max_price_available) wins when present —
  // real prices across the FULL searchable pool, not just loaded items.
  const dynamicMaxPrice = dynamicMaxPriceOverride !== undefined
    ? dynamicMaxPriceOverride
    : computeDynamicMaxPrice(allItems);
  const priceChips = hasEnoughPricedItems(allItems) ? buildPriceChips(allItems, dynamicMaxPrice) : [];
  // Marketplace Continuity, Honest Pricing & Log Privacy V1, Parte B —
  // real count when backend search is active, else a client-side count
  // over whatever's loaded so far (never invented, just narrower scope).
  const tipBasedCount = tipBasedCountOverride !== undefined
    ? tipBasedCountOverride
    : allItems.filter(e => e.pricing_model === "tip_based").length;

  const toggleIncludeTipBased = () => {
    const filterValue = (!filters.includeTipBased).toString();
    const nextFilters = { ...filters, includeTipBased: !filters.includeTipBased };
    track("filter_applied", { filter_type: "include_tip_based", value: filterValue });
    track("marketplace_filter_applied", buildFilterAppliedProps("include_tip_based", filterValue, allItems, nextFilters, sequence.city_iata));
    onChange(nextFilters);
  };

  const durLabels: Record<DurationFilter, string> = {
    short:  "< 2h",
    medium: "2–4h",
    long:   "> 4h",
  };

  // Marketplace Filter Polish V2.1, task 3 — mobile touch targets must be
  // >= 40px tall (h-10). Desktop's ItineraryFilterChipPanel keeps its own,
  // separate, unchanged chip sizing — these two components each define
  // their own local `chip` closure, so this only affects the mobile drawer.
  const chip = (
    label: ReactNode,
    active: boolean,
    onClick: () => void,
    color = "oklch(0.120 0.040 258)",
    key?: string,
  ) => (
    <button
      key={key ?? (typeof label === "string" ? label : undefined)}
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="shrink-0 inline-flex items-center h-10 text-[12px] font-semibold px-3 rounded-lg transition-all duration-150 active:scale-95"
      style={{
        color:      active ? "white"               : "oklch(0.545 0.020 60 / 0.62)",
        background: active ? color                 : "oklch(0.988 0.008 70 / 0.82)",
        border:     active ? `1px solid ${color}`  : "1px solid oklch(0.880 0.024 72 / 0.45)",
        boxShadow:  "none",
      }}
    >
      {label}
    </button>
  );

  // Compact section wrapper — a label plus a wrapping chip row, no nested
  // card/border (the drawer itself is the only "card" on screen).
  const section = (label: string, content: ReactNode) => (
    <div>
      <p className="text-[10px] font-bold tracking-wider uppercase mb-2" style={{ color: "oklch(0.545 0.020 60 / 0.45)" }}>
        {label}
      </p>
      <div className="flex flex-wrap gap-2">
        {content}
      </div>
    </div>
  );

  // Launch Analytics & Funnel Instrumentation V1 — every handler below fires
  // the new marketplace_filter_applied/marketplace_sort_changed alongside
  // the legacy filter_applied (never instead of it), computed from the
  // SAME `next`/`nextFilters` object passed to onChange, so
  // result_count_after/active_filter_count always describe the state the
  // user is about to see, not the one they're leaving.
  const toggleCategory = (cat: string) => {
    const next = new Set(filters.categories);
    const adding = !next.has(cat);
    if (adding) { next.add(cat); } else { next.delete(cat); }
    const filterValue = cat + (adding ? ":on" : ":off");
    const nextFilters = { ...filters, categories: next };
    track("filter_applied", { filter_type: "category", value: filterValue });
    track("marketplace_filter_applied", buildFilterAppliedProps("category", filterValue, allItems, nextFilters, sequence.city_iata));
    onChange(nextFilters);
  };
  const toggleDuration = (d: DurationFilter) => {
    const next = filters.duration === d ? null : d;
    const filterValue = next ?? "off";
    const nextFilters = { ...filters, duration: next };
    track("filter_applied", { filter_type: "duration", value: filterValue });
    track("marketplace_filter_applied", buildFilterAppliedProps("duration", filterValue, allItems, nextFilters, sequence.city_iata));
    onChange(nextFilters);
  };
  const toggleReservable   = () => {
    const filterValue = (!filters.reservable).toString();
    const nextFilters = { ...filters, reservable: !filters.reservable };
    track("filter_applied", { filter_type: "reservable", value: filterValue });
    track("marketplace_filter_applied", buildFilterAppliedProps("reservable", filterValue, allItems, nextFilters, sequence.city_iata));
    onChange(nextFilters);
  };
  const toggleProviderOnly = () => {
    const filterValue = (!filters.providerOnly).toString();
    const nextFilters = { ...filters, providerOnly: !filters.providerOnly };
    track("filter_applied", { filter_type: "provider_only", value: filterValue });
    track("marketplace_filter_applied", buildFilterAppliedProps("provider_only", filterValue, allItems, nextFilters, sequence.city_iata));
    onChange(nextFilters);
  };
  const toggleTimeOfDay = (t: string) => {
    const next = filters.timeOfDay === t ? null : t;
    const filterValue = next ?? "off";
    const nextFilters = { ...filters, timeOfDay: next };
    track("filter_applied", { filter_type: "time_of_day", value: filterValue });
    track("marketplace_filter_applied", buildFilterAppliedProps("time_of_day", filterValue, allItems, nextFilters, sequence.city_iata));
    onChange(nextFilters);
  };
  const selectMaxPrice = (value: number | null) => {
    const filterValue = String(value ?? "unlimited");
    const nextFilters = { ...filters, maxPrice: value };
    track("filter_applied", { filter_type: "max_price", value: filterValue });
    track("marketplace_filter_applied", buildFilterAppliedProps("max_price", filterValue, allItems, nextFilters, sequence.city_iata));
    onChange(nextFilters);
  };
  const setSortBy = (s: SortBy) => {
    const next: SortBy = filters.sortBy === s ? "default" : s;
    track("filter_applied", { filter_type: "sort_by", value: next });
    track("marketplace_sort_changed", buildSortChangedProps(next, allItems, filters, sequence.city_iata));
    onChange({ ...filters, sortBy: next });
  };
  const setSortDefault = () => {
    track("filter_applied", { filter_type: "sort_by", value: "default" });
    track("marketplace_sort_changed", buildSortChangedProps("default", allItems, filters, sequence.city_iata));
    onChange({ ...filters, sortBy: "default" });
  };

  // Marketplace Filter Polish V2.1, task 3 — the drawer chrome (see
  // MobileFilterDrawer/MarketplaceToolbar) now owns the header ("Filtrar
  // experiencias"), subtitle, and "Limpiar filtros"/"Ver resultados"
  // footer, so this component is purely the six required sections in
  // order: Disponibilidad → Precio máximo → Duración → Categoría →
  // Momento recomendado → Ordenar. No nested card/border here — the
  // drawer itself is the only "card" on screen.
  return (
    <div className="flex flex-col gap-5">

      {/* 1. Disponibilidad */}
      {(hasReservable || (hasLiveProvider && showLocalRefs)) && section("Disponibilidad", <>
        {hasReservable && chip("Reservable", filters.reservable, toggleReservable)}
        {hasLiveProvider && showLocalRefs && chip("Con proveedor", filters.providerOnly, toggleProviderOnly)}
      </>)}

      {/* 2. Precio máximo — quick "Hasta $X" chips (Marketplace Filters V2).
          Counts come from the same buildPriceChips() helper/data used by
          the desktop panel (task 1) — no separate count logic here, just
          the same rendering rule. */}
      {section("Precio máximo", <>
        {priceChips.length > 0 ? (
          priceChips.map(pc => (
            <button
              key={`price-${pc.value ?? "unlimited"}`}
              type="button"
              disabled={pc.disabled}
              onClick={() => selectMaxPrice(pc.value)}
              aria-pressed={filters.maxPrice === pc.value}
              className="shrink-0 inline-flex items-center h-10 text-[12px] font-semibold px-3 rounded-lg transition-all duration-150 active:scale-95 disabled:opacity-35 disabled:cursor-not-allowed"
              style={{
                color:      filters.maxPrice === pc.value ? "white"                : "oklch(0.545 0.020 60 / 0.62)",
                background: filters.maxPrice === pc.value ? "oklch(0.44 0.10 145)" : "oklch(0.988 0.008 70 / 0.82)",
                border:     filters.maxPrice === pc.value ? "1px solid oklch(0.44 0.10 145)" : "1px solid oklch(0.880 0.024 72 / 0.45)",
              }}
            >
              {pc.label}{pc.count > 0 || pc.value === null ? ` (${pc.count})` : ""}
            </button>
          ))
        ) : (
          <p className="text-[11px]" style={{ color: "oklch(0.545 0.020 60 / 0.45)" }}>
            Sin precios suficientes
          </p>
        )}
        {/* Marketplace Continuity, Honest Pricing & Log Privacy V1, Parte B
            #4 — only shown once a price ceiling is active (the control is
            meaningless otherwise, since passesMaxPrice() short-circuits to
            "always passes" with no ceiling) and only when there's at least
            one voluntary-contribution item to actually discover. Explicit
            opt-in — never implied to fit the ceiling, just included. */}
        {filters.maxPrice !== null && tipBasedCount > 0 && (
          <button
            type="button"
            onClick={toggleIncludeTipBased}
            aria-pressed={filters.includeTipBased}
            className="w-full mt-1 inline-flex items-center justify-between h-10 text-[12px] font-semibold px-3 rounded-lg transition-all duration-150 active:scale-95"
            style={{
              color:      filters.includeTipBased ? "white"                : "oklch(0.545 0.020 60 / 0.62)",
              background: filters.includeTipBased ? "oklch(0.44 0.10 145)" : "oklch(0.988 0.008 70 / 0.82)",
              border:     filters.includeTipBased ? "1px solid oklch(0.44 0.10 145)" : "1px solid oklch(0.880 0.024 72 / 0.45)",
            }}
          >
            <span>Incluir experiencias de aporte voluntario</span>
            <span>({tipBasedCount})</span>
          </button>
        )}
      </>)}

      {/* 3. Duración */}
      {allDurations.length > 0 && section("Duración",
        allDurations.map(d => chip(durLabels[d], filters.duration === d, () => toggleDuration(d), "oklch(0.120 0.040 258)", d))
      )}

      {/* 4. Categoría */}
      {presentCategories.length > 0 && section("Categoría",
        presentCategories.map(cat => {
          const meta    = CATEGORY_META[cat];
          const CatIc   = CATEGORY_LUCIDE_ICONS[cat];
          const isActive = filters.categories.has(cat);
          const color    = meta?.color ?? "oklch(0.580 0.220 256)";
          return (
            <button
              key={cat}
              type="button"
              onClick={() => toggleCategory(cat)}
              aria-pressed={isActive}
              className="inline-flex items-center gap-1.5 h-10 shrink-0 text-[12px] font-semibold px-3 rounded-lg transition-all duration-150 active:scale-95"
              style={{
                color:      isActive ? "white"              : "oklch(0.545 0.020 60 / 0.62)",
                background: isActive ? color                : "oklch(0.988 0.008 70 / 0.82)",
                border:     isActive ? `1px solid ${color}` : "1px solid oklch(0.880 0.024 72 / 0.45)",
              }}
            >
              {CatIc ? <CatIc size={12} aria-hidden /> : null}
              {meta?.label ?? cat}
            </button>
          );
        })
      )}

      {/* 5. Momento recomendado */}
      {presentTimeOfDays.length > 0 && section("Momento recomendado",
        presentTimeOfDays.map(t => {
          const meta = TIME_META[t] ?? TIME_META.any;
          const TimeIc = TIME_LUCIDE_ICONS[t] ?? MapPin;
          return chip(
            <span className="inline-flex items-center gap-1.5">
              <TimeIc size={12} aria-hidden />
              {meta.label}
            </span>,
            filters.timeOfDay === t,
            () => toggleTimeOfDay(t),
            "oklch(0.120 0.040 258)",
            `time-${t}`,
          );
        })
      )}

      {/* 6. Ordenar — every sort option consolidated in one place, instead
          of scattered inline next to each filter type. */}
      {section("Ordenar", <>
        {chip("Recomendadas", filters.sortBy === "default", setSortDefault, "#E8194B", "sort-default")}
        {chip("Precio ↑", filters.sortBy === "price_asc", () => setSortBy("price_asc"), "#E8194B", "sort-price")}
        {allDurations.length > 0 && chip("Duración ↑", filters.sortBy === "duration_asc", () => setSortBy("duration_asc"), "#E8194B", "sort-duration")}
        {hasLiveProvider && showLocalRefs && chip("Proveedor ↑", filters.sortBy === "provider_first", () => setSortBy("provider_first"), "#E8194B", "sort-provider")}
      </>)}

    </div>
  );
}

// ─── Itinerary Filter Chip Panel (left rail, xl only) ────────────────────────
// Grouped sections: Tipo · Duración · Momento · Proveedor · Ordenar

function ItineraryFilterChipPanel({
  sequence, filters, onFiltersChange, activeFilterCount, dynamicMaxPriceOverride, tipBasedCountOverride,
}: {
  sequence:          ExperienceSequence;
  filters:           ActiveFilters;
  onFiltersChange:   (f: ActiveFilters) => void;
  activeFilterCount: number;
  /** See FilterBar's identical prop. */
  dynamicMaxPriceOverride?: number | null;
  /** See FilterBar's identical prop. */
  tipBasedCountOverride?: number;
}) {
  // Frontend Marketplace Integration V1 — same flat marketplace source as
  // the main inventory list and FilterBar (marketplace_inventory when
  // present, else flattened days[]).
  const { items: allItems } = getMarketplaceSource(sequence);
  // Provider-Only Marketplace & Comparison V2 Foundation, Part B #6 — see
  // FilterBar's identical comment.
  const showLocalRefs = isShowLocalReferencesEnabled();

  // Derive available values from sequence
  const presentCategories = Array.from(
    new Set(allItems.map(e => e.category).filter(Boolean))
  ).sort();

  const hasShort  = allItems.some(e => (e.duration_hours ?? 0) > 0 && (e.duration_hours ?? 0) < 2);
  const hasMedium = allItems.some(e => (e.duration_hours ?? 0) >= 2 && (e.duration_hours ?? 0) <= 4);
  const hasLong   = allItems.some(e => (e.duration_hours ?? 0) > 4);

  const presentTimeOfDays = Array.from(
    new Set(allItems.map(e => e.time_of_day).filter((t): t is string => !!t && t !== "any"))
  );

  const hasReservable   = allItems.some(e => e.availability_confirmed === true);
  const hasLiveProvider = allItems.some(e => e.source_class === "LIVE_PROVIDER");

  // Marketplace Filters V2 — dynamic "Precio máximo" range + quick chips,
  // derived from the full inventory (not the currently-filtered subset).
  // Marketplace Inventory Scale & Discovery V1, task 8: dynamicMaxPriceOverride
  // (backend facets.max_price_available) wins when present.
  const dynamicMaxPrice = dynamicMaxPriceOverride !== undefined
    ? dynamicMaxPriceOverride
    : computeDynamicMaxPrice(allItems);
  const priceFilterEnabled = dynamicMaxPriceOverride !== undefined
    ? dynamicMaxPriceOverride != null
    : hasEnoughPricedItems(allItems);
  const priceChips = priceFilterEnabled ? buildPriceChips(allItems, dynamicMaxPrice) : [];
  // Slider always represents the full dynamic range; null (no filter) shows
  // as the handle resting at the far right (dynamicMaxPrice itself).
  const sliderValue = filters.maxPrice ?? dynamicMaxPrice ?? 0;
  // Marketplace Continuity, Honest Pricing & Log Privacy V1, Parte B — see
  // FilterBar's identical comment.
  const tipBasedCount = tipBasedCountOverride !== undefined
    ? tipBasedCountOverride
    : allItems.filter(e => e.pricing_model === "tip_based").length;

  // Handlers — Launch Analytics & Funnel Instrumentation V1: same
  // dual-emit pattern as FilterBar's identical handlers (see its own
  // comment). Note: the price SLIDER's continuous drag (onSliderChange
  // below) is deliberately NOT instrumented — a native range input fires
  // onChange on every pixel dragged, and tracking each one would flood
  // analytics and work against this sprint's own "sin romper performance"
  // goal. Only the discrete quick-price chips (selectMaxPrice) fire
  // marketplace_filter_applied for "max_price".
  const toggleCat       = (cat: string) => {
    const next = new Set(filters.categories);
    if (next.has(cat)) { next.delete(cat); } else { next.add(cat); }
    const nextFilters = { ...filters, categories: next };
    track("filter_applied", { filter_type: "category", value: cat });
    track("marketplace_filter_applied", buildFilterAppliedProps("category", cat, allItems, nextFilters, sequence.city_iata));
    onFiltersChange(nextFilters);
  };
  const toggleDuration  = (d: DurationFilter) => {
    const nextFilters = { ...filters, duration: filters.duration === d ? null : d };
    track("filter_applied", { filter_type: "duration", value: d });
    track("marketplace_filter_applied", buildFilterAppliedProps("duration", d, allItems, nextFilters, sequence.city_iata));
    onFiltersChange(nextFilters);
  };
  const toggleTimeOfDay = (t: string) => {
    const nextFilters = { ...filters, timeOfDay: filters.timeOfDay === t ? null : t };
    track("filter_applied", { filter_type: "time_of_day", value: t });
    track("marketplace_filter_applied", buildFilterAppliedProps("time_of_day", t, allItems, nextFilters, sequence.city_iata));
    onFiltersChange(nextFilters);
  };
  const toggleReservable   = () => {
    const filterValue = (!filters.reservable).toString();
    const nextFilters = { ...filters, reservable: !filters.reservable };
    track("filter_applied", { filter_type: "reservable", value: filterValue });
    track("marketplace_filter_applied", buildFilterAppliedProps("reservable", filterValue, allItems, nextFilters, sequence.city_iata));
    onFiltersChange(nextFilters);
  };
  const toggleProviderOnly = () => {
    const filterValue = (!filters.providerOnly).toString();
    const nextFilters = { ...filters, providerOnly: !filters.providerOnly };
    track("filter_applied", { filter_type: "provider_only", value: filterValue });
    track("marketplace_filter_applied", buildFilterAppliedProps("provider_only", filterValue, allItems, nextFilters, sequence.city_iata));
    onFiltersChange(nextFilters);
  };
  const selectMaxPrice = (value: number | null) => {
    const filterValue = String(value ?? "unlimited");
    const nextFilters = { ...filters, maxPrice: value };
    track("filter_applied", { filter_type: "max_price", value: filterValue });
    track("marketplace_filter_applied", buildFilterAppliedProps("max_price", filterValue, allItems, nextFilters, sequence.city_iata));
    onFiltersChange(nextFilters);
  };
  const onSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = Number(e.target.value);
    // Dragging back to the dynamic max is equivalent to "Sin límite" — don't
    // leave a phantom "active" filter sitting at the edge of the range.
    onFiltersChange({ ...filters, maxPrice: dynamicMaxPrice != null && v >= dynamicMaxPrice ? null : v });
  };
  const toggleIncludeTipBased = () => {
    const filterValue = (!filters.includeTipBased).toString();
    const nextFilters = { ...filters, includeTipBased: !filters.includeTipBased };
    track("filter_applied", { filter_type: "include_tip_based", value: filterValue });
    track("marketplace_filter_applied", buildFilterAppliedProps("include_tip_based", filterValue, allItems, nextFilters, sequence.city_iata));
    onFiltersChange(nextFilters);
  };
  const setSortBy = (s: SortBy) => {
    const next: SortBy = filters.sortBy === s ? "default" : s;
    track("filter_applied", { filter_type: "sort_by", value: next });
    track("marketplace_sort_changed", buildSortChangedProps(next, allItems, filters, sequence.city_iata));
    onFiltersChange({ ...filters, sortBy: next });
  };
  const setSortDefault = () => {
    track("filter_applied", { filter_type: "sort_by", value: "default" });
    track("marketplace_sort_changed", buildSortChangedProps("default", allItems, filters, sequence.city_iata));
    onFiltersChange({ ...filters, sortBy: "default" });
  };
  const clearAll = () => onFiltersChange({ ...EMPTY_FILTERS, categories: new Set() });

  // Chip builder
  const chip = (
    label:   ReactNode,
    active:  boolean,
    onClick: () => void,
    color = "oklch(0.120 0.040 258)",
    key?: string,
  ) => (
    <button
      key={key ?? (typeof label === "string" ? label : undefined)}
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded-full transition-all duration-150 active:scale-95"
      style={{
        color:      active ? "white"              : "oklch(0.545 0.020 60 / 0.62)",
        background: active ? color                : "transparent",
        border:     active ? `1px solid ${color}` : "1px solid oklch(0.880 0.024 72 / 0.50)",
      }}
    >
      {label}
    </button>
  );

  // Section header
  const sectionHead = (label: string) => (
    <p
      className="text-[9px] font-black tracking-[0.18em] uppercase mb-2"
      style={{ color: "oklch(0.545 0.020 60 / 0.35)" }}
    >
      {label}
    </p>
  );

  const durLabels: Record<DurationFilter, string> = { short: "< 2h", medium: "2–4h", long: "> 4h" };

  return (
    <div>
      {/* Scoped thumb styling for the "Precio máximo" single-handle slider */}
      <style>{`
        .rhea-price-range {
          -webkit-appearance: none; appearance: none;
          width: 100%; height: 100%; margin: 0; padding: 0;
          position: absolute; inset: 0; background: transparent;
        }
        .rhea-price-range::-webkit-slider-runnable-track { background: transparent; height: 6px; }
        .rhea-price-range::-moz-range-track { background: transparent; height: 6px; }
        .rhea-price-range::-webkit-slider-thumb {
          -webkit-appearance: none; appearance: none;
          width: 16px; height: 16px; border-radius: 50%;
          background: oklch(0.44 0.10 145); border: 2.5px solid white;
          box-shadow: 0 1px 6px oklch(0.44 0.10 145 / 0.40); cursor: grab;
        }
        .rhea-price-range::-moz-range-thumb {
          width: 16px; height: 16px; border-radius: 50%;
          background: oklch(0.44 0.10 145); border: 2.5px solid white;
          box-shadow: 0 1px 6px oklch(0.44 0.10 145 / 0.40); cursor: grab;
        }
      `}</style>
      {/* Panel header */}
      <div
        className="pb-2.5 flex items-center justify-between"
        style={{ borderBottom: "1px solid oklch(0.880 0.024 72 / 0.30)" }}
      >
        <p className="text-[10px] uppercase tracking-[0.20em] font-bold" style={{ color: "oklch(0.545 0.020 60 / 0.45)" }}>
          Filtrar actividades
        </p>
        {activeFilterCount > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="text-[10px] font-semibold transition-opacity hover:opacity-70"
            style={{ color: "oklch(0.580 0.220 256)" }}
          >
            Limpiar ({activeFilterCount})
          </button>
        )}
      </div>

      <div className="pt-3 flex flex-col gap-4">

        {/* ── Disponibilidad ──────────────────────────────────────────────── */}
        {(hasReservable || (hasLiveProvider && showLocalRefs)) && (
          <div>
            {sectionHead("Disponibilidad")}
            <div className="flex flex-wrap gap-1.5">
              {hasReservable && chip(
                <span className="inline-flex items-center gap-1"><CheckCircle2 size={10} aria-hidden />Reservable</span>,
                filters.reservable,
                toggleReservable,
                "oklch(0.120 0.040 258)",
                "reservable",
              )}
              {hasLiveProvider && showLocalRefs && chip(
                <span className="inline-flex items-center gap-1"><Building2 size={10} aria-hidden />Con proveedor</span>,
                filters.providerOnly,
                toggleProviderOnly,
                "oklch(0.120 0.040 258)",
                "providerOnly",
              )}
            </div>
          </div>
        )}

        {/* ── Duración ─────────────────────────────────────────────────────── */}
        {(hasShort || hasMedium || hasLong) && (
          <div>
            {sectionHead("Duración")}
            <div className="flex flex-wrap gap-1.5">
              {hasShort  && chip(durLabels.short,  filters.duration === "short",  () => toggleDuration("short"))}
              {hasMedium && chip(durLabels.medium, filters.duration === "medium", () => toggleDuration("medium"))}
              {hasLong   && chip(durLabels.long,   filters.duration === "long",   () => toggleDuration("long"))}
            </div>
          </div>
        )}

        {/* ── Categoría ─────────────────────────────────────────────────────── */}
        {presentCategories.length > 0 && (
          <div>
            {sectionHead("Categoría")}
            <div className="flex flex-wrap gap-1.5">
              {presentCategories.map(cat => {
                const meta    = CATEGORY_META[cat];
                const CatIc   = CATEGORY_LUCIDE_ICONS[cat];
                const isActive = filters.categories.has(cat);
                const color    = meta?.color ?? "oklch(0.580 0.220 256)";
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => toggleCat(cat)}
                    aria-pressed={isActive}
                    className="inline-flex items-center gap-1 shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded-full transition-all duration-150 active:scale-95"
                    style={{
                      color:      isActive ? "white"              : "oklch(0.545 0.020 60 / 0.62)",
                      background: isActive ? color                : "transparent",
                      border:     isActive ? `1px solid ${color}` : "1px solid oklch(0.880 0.024 72 / 0.50)",
                    }}
                  >
                    {CatIc ? <CatIc size={11} aria-hidden /> : null}
                    {meta?.label ?? cat}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Momento recomendado ──────────────────────────────────────────── */}
        {presentTimeOfDays.length > 0 && (
          <div>
            {sectionHead("Momento recomendado")}
            <div className="flex flex-wrap gap-1.5">
              {presentTimeOfDays.map(t => {
                const meta = TIME_META[t] ?? TIME_META.any;
                const TimeIc = TIME_LUCIDE_ICONS[t] ?? MapPin;
                return chip(
                  <span className="inline-flex items-center gap-1">
                    <TimeIc size={11} aria-hidden />
                    {meta.label}
                  </span>,
                  filters.timeOfDay === t,
                  () => toggleTimeOfDay(t),
                  "oklch(0.120 0.040 258)",
                  `time-${t}`,
                );
              })}
            </div>
          </div>
        )}

        {/* ── Precio máximo (Marketplace Filters V2) ───────────────────────────
            "Hasta cuánto quieres pagar" por actividad — NOT a total-trip
            budget. Hidden entirely when there isn't enough real price data
            to make a ceiling meaningful. */}
        {priceFilterEnabled ? (
          <div>
            {sectionHead("Precio máximo")}
            <p className="text-[11px] font-semibold mb-2 tabular-nums" style={{ color: "oklch(0.44 0.10 145)" }}>
              {filters.maxPrice === null ? "Sin límite" : `Hasta $${filters.maxPrice}`}
            </p>
            {dynamicMaxPrice != null && dynamicMaxPrice > 0 && (
              <div className="relative h-[18px] flex items-center select-none mx-1 mb-2">
                <div className="absolute inset-x-0 h-1.5 rounded-full" style={{ background: "oklch(0.880 0.024 72 / 0.50)" }} />
                <div
                  className="absolute h-1.5 rounded-full pointer-events-none"
                  style={{ left: 0, width: `${(sliderValue / dynamicMaxPrice) * 100}%`, background: "oklch(0.44 0.10 145)" }}
                />
                <input
                  type="range"
                  className="rhea-price-range"
                  min={0}
                  max={dynamicMaxPrice}
                  step={Math.max(1, Math.round(dynamicMaxPrice / 100))}
                  value={sliderValue}
                  onChange={onSliderChange}
                  aria-label="Precio máximo por actividad"
                />
              </div>
            )}
            <div className="flex flex-wrap gap-1.5">
              {priceChips.map(pc => (
                <button
                  key={`price-${pc.value ?? "unlimited"}`}
                  type="button"
                  disabled={pc.disabled}
                  onClick={() => selectMaxPrice(pc.value)}
                  aria-pressed={filters.maxPrice === pc.value}
                  className="shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded-full transition-all duration-150 active:scale-95 disabled:opacity-35 disabled:cursor-not-allowed"
                  style={{
                    color:      filters.maxPrice === pc.value ? "white"                : "oklch(0.545 0.020 60 / 0.62)",
                    background: filters.maxPrice === pc.value ? "oklch(0.44 0.10 145)"  : "transparent",
                    border:     filters.maxPrice === pc.value ? "1px solid oklch(0.44 0.10 145)" : "1px solid oklch(0.880 0.024 72 / 0.50)",
                  }}
                >
                  {pc.label}{pc.count > 0 || pc.value === null ? ` (${pc.count})` : ""}
                </button>
              ))}
            </div>
            {/* Marketplace Continuity, Honest Pricing & Log Privacy V1,
                Parte B #4 — see FilterBar's identical comment. */}
            {filters.maxPrice !== null && tipBasedCount > 0 && (
              <button
                type="button"
                onClick={toggleIncludeTipBased}
                aria-pressed={filters.includeTipBased}
                className="w-full mt-2 inline-flex items-center justify-between text-[11px] font-semibold px-2.5 py-1.5 rounded-full transition-all duration-150 active:scale-95"
                style={{
                  color:      filters.includeTipBased ? "white"                : "oklch(0.545 0.020 60 / 0.62)",
                  background: filters.includeTipBased ? "oklch(0.44 0.10 145)"  : "transparent",
                  border:     filters.includeTipBased ? "1px solid oklch(0.44 0.10 145)" : "1px solid oklch(0.880 0.024 72 / 0.50)",
                }}
              >
                <span>Incluir experiencias de aporte voluntario</span>
                <span>({tipBasedCount})</span>
              </button>
            )}
          </div>
        ) : (
          <div>
            {sectionHead("Precio máximo")}
            <p className="text-[11px]" style={{ color: "oklch(0.545 0.020 60 / 0.45)" }}>
              Sin precios suficientes
            </p>
          </div>
        )}

        {/* ── Ordenar: every sort option consolidated in one place — never
            scattered inline next to a filter section (Marketplace Visual
            Diversity V3: a "Proveedor" section previously held nothing but a
            chip labeled "Ordenar", which read as broken/confusing since the
            section name gave no hint the chip was a sort control). Matches
            the mobile drawer's "Ordenar" section content exactly. ────────── */}
        <div>
          {sectionHead("Ordenar")}
          <div className="flex flex-wrap gap-1.5">
            {chip(
              <span className="inline-flex items-center gap-1"><Sparkles size={10} aria-hidden />Recomendadas</span>,
              filters.sortBy === "default",
              setSortDefault,
              "#E8194B",
              "sort-default",
            )}
            {chip(
              <span className="inline-flex items-center gap-1"><DollarSign size={10} aria-hidden />Precio</span>,
              filters.sortBy === "price_asc",
              () => setSortBy("price_asc"),
              "#E8194B",
              "sort-price",
            )}
            {(hasShort || hasMedium || hasLong) && chip(
              <span className="inline-flex items-center gap-1"><Clock size={10} aria-hidden />Duración</span>,
              filters.sortBy === "duration_asc",
              () => setSortBy("duration_asc"),
              "#E8194B",
              "sort-duration",
            )}
            {hasLiveProvider && showLocalRefs && chip(
              <span className="inline-flex items-center gap-1"><Building2 size={10} aria-hidden />Proveedor</span>,
              filters.sortBy === "provider_first",
              () => setSortBy("provider_first"),
              "#E8194B",
              "sort-provider",
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

// ─── Travelers panel (left rail, xl only) ────────────────────────────────────
// Marketplace Filters V2 — replaces FilterSidebar in the Experiences
// marketplace context. FilterSidebar itself is untouched and still used
// as-is by the trip Planner (a genuinely different concept: total-trip
// budget, not a per-activity price ceiling) — this is a separate, minimal
// component so Planner's dual min/max $0–$10k slider can never regress from
// changes made here. "Actualizar búsqueda" still triggers a real backend
// regeneration when the traveler count changes (adults affects generation
// quality); price filtering, by contrast, is instant and client-side (see
// the "Precio máximo" section in ItineraryFilterChipPanel/FilterBar above).
function TravelersPanel({
  adults, loading, dirty, onAdultsChange, onApply,
}: {
  adults:         number;
  loading:        boolean;
  dirty:          boolean;
  onAdultsChange: (n: number) => void;
  onApply:        () => void;
}) {
  return (
    <div>
      <p
        className="text-[10px] uppercase tracking-[0.20em] font-bold pb-2.5 mb-4"
        style={{ color: "oklch(0.545 0.020 60 / 0.45)", borderBottom: "1px solid oklch(0.880 0.024 72 / 0.30)" }}
      >
        Viajeros
      </p>
      <div className="flex items-center gap-3 mb-4">
        <button
          type="button"
          onClick={() => onAdultsChange(Math.max(1, adults - 1))}
          disabled={adults <= 1}
          className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold
                     transition-all hover:scale-110 active:scale-95 disabled:opacity-30"
          style={{ background: "oklch(0.880 0.024 72 / 0.55)", color: "oklch(0.28 0.025 55)" }}
        >−</button>
        <span className="text-sm font-bold w-5 text-center tabular-nums" style={{ color: "oklch(0.28 0.025 55)" }}>
          {adults}
        </span>
        <button
          type="button"
          onClick={() => onAdultsChange(Math.min(12, adults + 1))}
          disabled={adults >= 12}
          className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold
                     transition-all hover:scale-110 active:scale-95 disabled:opacity-30"
          style={{ background: "oklch(0.880 0.024 72 / 0.55)", color: "oklch(0.28 0.025 55)" }}
        >+</button>
        <span className="text-xs" style={{ color: "oklch(0.545 0.020 60 / 0.55)" }}>
          {adults === 1 ? "persona" : "personas"}
        </span>
      </div>
      <button
        type="button"
        onClick={onApply}
        disabled={loading || !dirty}
        className="w-full py-2.5 rounded-xl text-xs font-semibold
                   transition-all hover:opacity-90 active:scale-[0.98]
                   disabled:opacity-40"
        style={{
          background: dirty ? "oklch(0.298 0.058 167)" : "oklch(0.880 0.024 72 / 0.50)",
          color:      dirty ? "#fff" : "oklch(0.45 0.020 55)",
        }}
      >
        {loading ? "Buscando…" : dirty ? "Actualizar búsqueda" : "Sin cambios"}
      </button>
    </div>
  );
}

// ─── Experience Timeline ──────────────────────────────────────────────────────

export function ExperienceTimeline({
  sequence, rawQuery, travelDate, familyMode = false, onNewSearch, onTryDestination, adults, children, childrenAges,
  sidebarBudgetMax = 5000, sidebarDirty = false, sidebarLoading = false,
  onSidebarAdultsChange, onSidebarApply,
  filters: externalFilters, onFiltersChange,
}: {
  sequence:               ExperienceSequence;
  rawQuery?:              string;
  travelDate?:            string | null;
  familyMode?:            boolean;
  onNewSearch:            () => void;
  /** Launch Empty-State & Coverage UX V1 — jumps directly to a suggested
   *  launch-ready destination from the zero-provider empty state. */
  onTryDestination?:     (iata: string, cityDisplay: string) => void;
  adults?:                number;
  children?:              number;
  childrenAges?:          number[];
  sidebarBudgetMax?:      number;
  sidebarDirty?:          boolean;
  sidebarLoading?:        boolean;
  onSidebarAdultsChange?: (n: number) => void;
  onSidebarApply?:        () => void;
  filters?:               ActiveFilters;
  onFiltersChange?:       (f: ActiveFilters) => void;
}) {
  const isMultiCity = sequence.multi_city_sequence?.mode === "multi_city";
  // Hotfix/Sprint: Language Selector Marketplace Wiring V1 — the uncontrolled
  // fallback default is a plain expression (never React state), so it
  // already reflects the header's CURRENT language on every render with no
  // extra effect needed; the controlled case (externalFilters provided by a
  // parent) is kept in sync by the effect below instead.
  const { language } = usePreferences();
  const filters    = externalFilters ?? { ...EMPTY_FILTERS, categories: new Set(), marketLocale: languageToMarketLocale(language) };
  const setFilters = onFiltersChange ?? (() => {});

  // Hotfix/Sprint: Language Selector Marketplace Wiring V1 — usePreferences()
  // is a shared, module-level store: the header's ES/EN/PT selector can
  // change `language` at any time while this timeline is already mounted.
  // This keeps filters.marketLocale (and therefore filtersKey, and the
  // request runMarketplaceSearch below sends) in sync, so switching language
  // always re-fetches the current search — never just flips localStorage.
  // A no-op for the uncontrolled case (setFilters is the no-op fallback
  // above and filters.marketLocale already matches every render).
  useEffect(() => {
    const nextMarketLocale = languageToMarketLocale(language);
    if (filters.marketLocale === nextMarketLocale) return;
    setFilters({ ...filters, marketLocale: nextMarketLocale });
    // setFilters is intentionally excluded — it's either the parent's own
    // onFiltersChange (whose identity this effect must not react to) or the
    // local no-op fallback above; including it risks an extra run on every
    // parent re-render if a future caller ever passes a fresh callback
    // identity, with no benefit (the equality check above already makes
    // this effect idempotent regardless of how often it runs).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, filters]);

  const [mapOpen,  setMapOpen]  = useState(false);
  // Sprint 3F-T4: aria-live announcement text for screen readers
  const [dndAnnouncement, setDndAnnouncement] = useState("");

  // Launch Empty-State & Coverage UX V1 — dynamically imported (never a
  // static top-level import) so city_catalog.ts's ~79kB dataset stays out
  // of the shared entry bundle. home_featured.ts is already its own
  // reachable module from Home's chunk; adding a second STATIC import edge
  // from this route was what pulled city_catalog into the shared/entry
  // chunk in the first place (Launch Performance & Bundle Trim V1's whole
  // point was keeping that out of the always-loaded path). Only fetched
  // once, doesn't block anything else in this component's render.
  const [destinationSuggestions, setDestinationSuggestions] = useState<CatalogCity[]>([]);
  useEffect(() => {
    let cancelled = false;
    import("@/lib/cities/home_featured").then(({ getEmptyStateDestinationSuggestions }) => {
      if (!cancelled) setDestinationSuggestions(getEmptyStateDestinationSuggestions(5));
    });
    return () => { cancelled = true; };
  }, []);

  // Sprint 2A — Marketplace Actions: local, session-only selection state.
  // "Marketplace Behavior V2" — no preselection: the user starts with an
  // empty cart and builds it entirely by hand. selectionOrder (added in
  // "Marketplace Inventory Layout V1") is the tray's display/reorder order,
  // kept as a subset of selectedIds, never a parallel source of truth for
  // membership. Never mutates sequence.
  const [selectedIds,    setSelectedIds]    = useState<Set<string>>(() => new Set());
  const [selectionOrder, setSelectionOrder] = useState<string[]>(() => []);

  // Marketplace Product Detail & Conversion V1 — the single open-detail
  // state for ExperienceDetailDrawer, shared by every marketplace surface
  // (featured shelf, main list). null = closed. Deliberately independent of
  // filters/sort/selection state, so opening or closing it never resets any
  // of those or the scroll position.
  const [detailItem, setDetailItem] = useState<MarketplaceItem | null>(null);
  const [detailSurface, setDetailSurface] = useState<MarketplaceDetailSurface | null>(null);
  const openDetail = (item: MarketplaceItem, surface: MarketplaceDetailSurface) => {
    setDetailItem(item);
    setDetailSurface(surface);
  };

  // Marketplace Comparison V1 — the comparison table's own open state, plus
  // the pending-replace state for task 2's "replace one of the 3" flow.
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const [pendingCompare, setPendingCompare] = useState<{ compositeId: string; title: string } | null>(null);

  // Marketplace Inventory Scale & Discovery V1, task 9 — a persistent
  // snapshot of every ExperienceItem ever added to the comparison, keyed by
  // compositeId, independent of whatever page/filter state currently
  // populates the marketplace list. Without this, "Cargar más" (which
  // ACCUMULATES pages) would still be fine, but changing a filter (which
  // REPLACES the loaded set) would silently drop a compared item from
  // trayItems the moment it no longer matched the new filter or was in an
  // unloaded page — exactly the bug task 9 requires fixing.
  const [comparedExpById, setComparedExpById] = useState<Map<string, ExperienceItem>>(() => new Map());

  // Marketplace Inventory Scale & Discovery V1 — paginated marketplace
  // search state. searchItems accumulates across "Cargar más" pages;
  // searchReady flips true after the first successful /experiences/search
  // response for the CURRENT sequence, at which point the list switches
  // from sourceItems (sequence.marketplace_inventory, shown immediately so
  // there's no empty flash) to searchItems. searchUsingFallback stays true
  // for the rest of this sequence once /experiences/search has failed once
  // for page 1 — task 6's "fallback temporal".
  const [searchItems,        setSearchItems]        = useState<ExperienceItem[]>([]);
  const [searchCursor,       setSearchCursor]        = useState<string | null>(null);
  const [searchHasMore,      setSearchHasMore]       = useState(false);
  const [searchFacets,       setSearchFacets]        = useState<ExperienceSearchFacets | null>(null);
  const [searchCoverageTier, setSearchCoverageTier]  = useState<string | null>(null);
  // Provider-Only Marketplace & Comparison V2 Foundation, Part D #1 — the
  // destination's real depth BEFORE any filter. Separate from searchFacets
  // (which reflects the currently-filtered matching pool) so applying a
  // filter can never make "inventario limitado" fire — see totalOriginal.
  const [searchDestinationDepth, setSearchDestinationDepth] = useState<{ provider_count: number; local_count: number } | null>(null);
  const [searchReady,        setSearchReady]         = useState(false);
  const [searchUsingFallback, setSearchUsingFallback] = useState(false);
  const [searchLoading,      setSearchLoading]       = useState(false);
  const [searchLoadingMore,  setSearchLoadingMore]   = useState(false);
  const [searchLoadError,    setSearchLoadError]     = useState<string | null>(null);
  const [searchCursorExpired, setSearchCursorExpired] = useState(false);
  // Fast First Result + Background Deep Warmup Foundation V1 — true only
  // during the short (~800ms) window between a "cargar más" response that
  // came back with inventory_warming=true/no new items and its single
  // automatic retry. Kept separate from searchLoadingMore so the load-more
  // button stays visibly "working" across that gap instead of flickering
  // to its idle state and back.
  const [searchWarmupPending, setSearchWarmupPending] = useState(false);
  // Marketplace Provider Survival + Stable First Paint V1 — true from the
  // moment a sequence resolves until the FIRST (non-append) /experiences/
  // search for it settles, success or failure. Starts true (useState(true))
  // rather than false-until-an-effect-runs specifically to close the one-
  // frame race the sequence.marketplace_inventory swap used to leak
  // through: without this, there's a render where `loading` has already
  // flipped false (ExperienceTimeline just mounted) but the search effect
  // hasn't fired yet, so sourceItems would paint for one frame before
  // searchLoading ever becomes true. While this is true, the single-city
  // marketplace list renders the existing loading skeleton instead of
  // sourceItems (sequence.marketplace_inventory) — never a provisional
  // list that then gets replaced. Reset to true only on a brand new
  // sequence; a later filter/sort change re-search does NOT re-arm it
  // (that already has its own, unchanged "Actualizando resultados…"
  // indicator over the previous results).
  const [awaitingFirstSearch, setAwaitingFirstSearch] = useState(true);
  // Guards against a slow, superseded request (stale filter change, or a
  // race between two rapid "Cargar más" clicks) overwriting a newer one —
  // task 6's "evitar búsquedas duplicadas".
  const searchRequestIdRef = useRef(0);

  // Reset everything whenever a new sequence loads — always back to an empty selection.
  useEffect(() => {
    setSelectedIds(new Set());
    setSelectionOrder([]);
    setComparedExpById(new Map());
    setMapOpen(false);
    setDetailItem(null);
    setDetailSurface(null);
    setComparisonOpen(false);
    setPendingCompare(null);
    setSearchItems([]);
    setSearchCursor(null);
    setSearchHasMore(false);
    setSearchFacets(null);
    setSearchCoverageTier(null);
    setSearchDestinationDepth(null);
    setSearchReady(false);
    setSearchUsingFallback(false);
    setSearchLoadError(null);
    setSearchCursorExpired(false);
    setAwaitingFirstSearch(true);
    searchRequestIdRef.current += 1; // invalidate any in-flight search from the previous sequence
  }, [sequence]);

  const filtersActive = hasActiveFilters(filters);
  const activeFilterCount = countActiveFilters(filters);

  // Marketplace Inventory Scale & Discovery V1 — fetches one page from
  // /experiences/search and either replaces (append=false, a fresh search:
  // new destination or a filter/sort change) or appends (append=true,
  // "Cargar más") searchItems. A monotonically increasing request id guards
  // against a slow/superseded request overwriting a newer one — e.g. the
  // user changes a filter again before the previous request resolves, or
  // double-clicks "Cargar más" (task 6's "evitar búsquedas duplicadas").
  // Hotfix: Marketplace Load-More Continuity Completion V1 — every
  // decision this function needs to make about stale cursors and warmup
  // polling comes from decideNextLoadMoreStep() (marketplaceContinuity.ts),
  // fed by ONE explicit LoadMoreActionContext that is threaded, unmodified
  // in identity (only ever a fresh COPY with one field bumped), through
  // every recovery/poll step of a single logical action — never loose,
  // independently-updated booleans again (that gap is exactly what let a
  // warmup poll's own retry forget an already-used recovery, see that
  // module's header comment for the full failure sequence this closes).
  const finishLoadMoreWithManualExit = (reason: ManualExitReason) => {
    setSearchWarmupPending(false);
    switch (reason) {
      case "stale_cursor":
        setSearchCursorExpired(true);
        setSearchHasMore(false);
        setSearchLoadError("La búsqueda cambió mientras explorabas. Actualiza los resultados para continuar.");
        break;
      case "warmup_timeout":
        // Honest, non-technical: we simply stopped waiting for the
        // warmup within budget — nothing "changed", nothing broke.
        setSearchCursorExpired(true);
        setSearchHasMore(false);
        setSearchLoadError("Sigue preparando más experiencias. Actualiza para continuar.");
        break;
      case "append_failed":
      case "network_error":
        setSearchLoadError("No pudimos cargar más experiencias en este momento.");
        break;
    }
  };

  const runMarketplaceSearch = async (opts: {
    cursor?: string | null;
    append: boolean;
    /** The full context for this logical action. Undefined ONLY for a
     *  genuinely new action (a fresh click, or the filter/sequence
     *  effect's own page-1 fetch) — this function mints a new one via
     *  createLoadMoreActionContext() in that case. Every recovery/poll/
     *  append-fresh step this function schedules itself always passes
     *  the (possibly updated, but same-lineage) context forward. */
    actionContext?: LoadMoreActionContext;
    /** Internal only — true for the recovery's own two AWAITED steps
     *  (reload page 1, then retry the append once against the fresh
     *  cursor) so this call's own loading-state setup/teardown is
     *  entirely owned by the outer, non-chained frame that started the
     *  whole action (see the `finally` block below). Warmup-poll
     *  continuations are dispatched via setTimeout — never awaited by
     *  the frame that scheduled them — so they are NOT chained; each one
     *  owns its own loading-state lifecycle, same as a fresh top-level
     *  call (this is intentional: searchWarmupPending is the flag that
     *  keeps the button visibly busy across that gap, not this one). */
    isChainedStep?: boolean;
    /** Progressive First Page + Deferred Provider Warmup V1, Part H —
     *  true only for a page-1 progressive-warmup poll's OWN follow-up
     *  calls (never the original page-1 request that discovered
     *  inventory_warming=true). Replaces searchItems with the fresh
     *  page-1 window instead of appending (the pool's rank order can
     *  shift as deferred providers merge in), and never re-fires page-1
     *  analytics a second time. */
    isPageOnePoll?: boolean;
  }): Promise<ExperienceSearchResponse | undefined> => {
    if (isMultiCity) return undefined;
    const isChainedCall = opts.isChainedStep === true;
    const context = opts.actionContext ?? createLoadMoreActionContext(++searchRequestIdRef.current, Date.now());
    const requestId = context.requestId;
    if (!isChainedCall) {
      if (opts.append) setSearchLoadingMore(true); else setSearchLoading(true);
      setSearchLoadError(null);
      if (!opts.append) setSearchCursorExpired(false);
    }

    const params: ExperienceSearchParams = buildMarketplaceSearchParamsFromSequence(sequence, filters, {
      cursor: opts.cursor,
      query: rawQuery,
      travelDate,
      travelers: adults,
      hasChildren: (children ?? 0) > 0,
      childrenAges,
    });

    const trackPayload = (returnedCount: number, providerCount: number, hasMore: boolean) => ({
      destination_iata: sequence.city_iata,
      page_size:        params.limit,
      returned_count:   returnedCount,
      provider_count:   providerCount,
      filter_count:     activeFilterCount,
      sort:             filters.sortBy,
      has_more:         hasMore,
    });

    try {
      const result = await searchExperiences(params);
      if (searchRequestIdRef.current !== requestId) return undefined; // superseded — discard, never revive a stale action

      // Progressive First Page + Deferred Provider Warmup V1, Part H —
      // a page-1 progressive-warmup poll's OWN follow-up call. Never
      // routed through the append/recovery machinery below (there is no
      // cursor involved); a failure here just stops polling silently —
      // whatever is already on screen stays exactly as it was.
      if (opts.isPageOnePoll) {
        if (!result.success) {
          setSearchWarmupPending(false);
          return undefined;
        }
        setSearchItems(appendUniqueExperienceItems([], result.items));
        setSearchCursor(result.next_cursor);
        setSearchHasMore(result.has_more);
        setSearchFacets(result.facets);
        setSearchCoverageTier(result.coverage_tier);
        setSearchDestinationDepth(result.destination_depth ?? null);
        if (result.inventory_warming && hasWarmupBudgetRemaining(context, Date.now())) {
          const delay = computeWarmupPollDelayMs(context.warmupPollAttempt);
          setTimeout(() => {
            if (searchRequestIdRef.current !== requestId) return;
            void runMarketplaceSearch({
              append: false,
              actionContext: { ...context, warmupPollAttempt: context.warmupPollAttempt + 1 },
              isChainedStep: true, isPageOnePoll: true,
            });
          }, delay);
        } else {
          setSearchWarmupPending(false);
        }
        return result;
      }

      if (!result.success) {
        if (opts.append) {
          const transition = decideNextLoadMoreStep(
            { kind: "append_result", response: { success: false, error: result.error } },
            context, Date.now(),
          );
          if (transition.type === "recover_page_one") {
            // Silently reload page 1 (same destino/query/filtros/
            // viajeros/fecha/sort — all already captured in `filters`/
            // closure state) and, if it still has more to load, retry
            // this exact append once against the fresh cursor it
            // yields — one user click ends with page 1 refreshed AND
            // the next page appended, never a second click required.
            const reloadResult = await runMarketplaceSearch({
              append: false, actionContext: transition.nextContext, isChainedStep: true,
            });
            if (searchRequestIdRef.current !== requestId) return undefined; // superseded mid-recovery
            return reloadResult;
          }
          // transition.type === "manual_exit" — append_result failures
          // never produce "success"/"poll"/"append_fresh".
          if (transition.type === "manual_exit") finishLoadMoreWithManualExit(transition.reason);
          return undefined;
        }
        if (isChainedCall) {
          // This IS the recovery's own page-1 reload, and it failed.
          const transition = decideNextLoadMoreStep(
            { kind: "recovery_reload_result", response: { success: false } },
            context, Date.now(),
          );
          if (transition.type === "manual_exit") finishLoadMoreWithManualExit(transition.reason);
          return undefined;
        }
        // A genuinely fresh (non-append, non-recovery) page-1 search
        // failed — task 6's existing fallback, unrelated to cursors.
        setSearchUsingFallback(true);
        return undefined;
      }

      // Fast First Result + Background Deep Warmup Foundation V1,
      // extended by Marketplace Load-More Continuity Completion V1 — a
      // "cargar más" response with inventory_warming=true and no new
      // items means the backend deliberately left everything untouched
      // (cursor not consumed) while a background deep-inventory warmup
      // was still finishing. Poll the SAME cursor on a bounded, moderate
      // backoff schedule against this action's own explicit deadline —
      // never an infinite loop, and never fire load-more analytics for
      // one of these non-results. Each poll is dispatched independently
      // (fire-and-forget via setTimeout, not awaited here) carrying the
      // SAME action context forward, so a later poll coming back as a
      // stale-cursor failure instead (the warmup's own write-back
      // changed the revision) re-enters the exact recovery path above —
      // with recoveryCount already reflecting whatever this action has
      // already spent, never silently forgotten.
      if (opts.append && result.inventory_warming && result.items.length === 0) {
        const transition = decideNextLoadMoreStep(
          { kind: "append_result", response: { success: true, inventory_warming: true, itemsCount: 0 } },
          context, Date.now(),
        );
        if (transition.type === "poll") {
          setSearchWarmupPending(true);
          setTimeout(() => {
            if (searchRequestIdRef.current !== requestId) return; // superseded — never revive a stale click
            void runMarketplaceSearch({ cursor: opts.cursor, append: true, actionContext: transition.nextContext });
          }, transition.delayMs);
          return undefined;
        }
        // transition.type === "manual_exit" (reason: "warmup_timeout") —
        // the action's total wait budget ran out. Never a technical
        // error, never an inert button: a clear manual exit instead,
        // with every existing result/filter/selection/scroll untouched.
        if (transition.type === "manual_exit") finishLoadMoreWithManualExit(transition.reason);
        return undefined;
      }
      setSearchWarmupPending(false);

      setSearchItems((prev) => {
        if (opts.append || isChainedCall) {
          return appendUniqueExperienceItems(prev, result.items);
        }
        return appendUniqueExperienceItems([], result.items);
      });
      setSearchCursor(result.next_cursor);
      setSearchHasMore(result.has_more);
      setSearchFacets(result.facets);
      setSearchCoverageTier(result.coverage_tier);
      setSearchDestinationDepth(result.destination_depth ?? null);
      setSearchUsingFallback(false);
      setSearchCursorExpired(false);
      setSearchReady(true);

      // Progressive First Page + Deferred Provider Warmup V1, Part H — a
      // fresh (non-append) page 1 that comes back with inventory_
      // warming=true means deferred providers are still merging into the
      // live pool. Whatever items this response brought (possibly zero)
      // are already applied above and stay visible; poll the same
      // page-1 search on a bounded backoff until warming clears.
      if (!opts.append && result.inventory_warming) {
        if (hasWarmupBudgetRemaining(context, Date.now())) {
          setSearchWarmupPending(true);
          const delay = computeWarmupPollDelayMs(context.warmupPollAttempt);
          setTimeout(() => {
            if (searchRequestIdRef.current !== requestId) return;
            void runMarketplaceSearch({
              append: false,
              actionContext: { ...context, warmupPollAttempt: context.warmupPollAttempt + 1 },
              isChainedStep: true, isPageOnePoll: true,
            });
          }, delay);
        } else {
          setSearchWarmupPending(false);
        }
      } else if (!opts.append) {
        setSearchWarmupPending(false);
      }

      track(
        opts.append ? "marketplace_page_loaded" : "marketplace_search_completed",
        trackPayload(result.returned_count, result.provider_count, result.has_more),
      );
      if (!result.has_more) {
        track("marketplace_inventory_exhausted", trackPayload(result.returned_count, result.provider_count, false));
      }
      // Supplier Pagination Contract V1, extended by the Provider Pagination
      // Context Parity + Multi-Provider Analytics hotfix — fired ALONGSIDE
      // marketplace_page_loaded, only when the backend reports this specific
      // page required a genuine new provider fetch (never on an ordinary
      // cache-served page, never on the first page/search of a session).
      // No raw query, title, booking_url, or cursor — see
      // buildProviderPageLoadedEvents()'s own doc for the exact provider_pages
      // vs. legacy provider_page precedence (never both, never double-counted).
      for (const event of buildProviderPageLoadedEvents(result, sequence.city_iata)) {
        track("marketplace_provider_page_loaded", event);
      }

      if (isChainedCall && !opts.append) {
        // This successful page was the recovery's own reload — decide
        // whether to continue into the action's one automatic
        // append-fresh retry (page 1 -> fresh cursor -> automatic append).
        const transition = decideNextLoadMoreStep(
          {
            kind: "recovery_reload_result",
            response: { success: true, hasMore: result.has_more, nextCursor: result.next_cursor },
          },
          context, Date.now(),
        );
        if (transition.type === "append_fresh") {
          return await runMarketplaceSearch({
            cursor: transition.cursor, append: true, actionContext: transition.nextContext, isChainedStep: true,
          });
        }
        // transition.type === "success" — page 1 already reflects the
        // true current state and there's nothing more to append.
      }

      return result;
    } catch {
      if (searchRequestIdRef.current !== requestId) return undefined;
      if (opts.isPageOnePoll) {
        // Progressive First Page + Deferred Provider Warmup V1, Part H —
        // a poll tick's own network failure never disturbs whatever is
        // already on screen; it just stops polling silently.
        setSearchWarmupPending(false);
        return undefined;
      }
      if (opts.append) {
        const transition = decideNextLoadMoreStep({ kind: "append_error" }, context, Date.now());
        if (transition.type === "manual_exit") finishLoadMoreWithManualExit(transition.reason);
      } else if (isChainedCall) {
        const transition = decideNextLoadMoreStep({ kind: "recovery_reload_error" }, context, Date.now());
        if (transition.type === "manual_exit") finishLoadMoreWithManualExit(transition.reason);
      } else {
        setSearchUsingFallback(true);
      }
      return undefined;
    } finally {
      // Chained steps (the recovery's own reload/retry) never touch
      // loading state — the outer, non-chained frame that started the
      // whole action owns it for the full chain, clearing it exactly
      // once here after every awaited step has fully settled. A
      // warmup-poll continuation is NOT a chained step (see the opts
      // doc above), so each poll correctly owns and clears its own.
      if (!isChainedCall && searchRequestIdRef.current === requestId) {
        setSearchLoading(false);
        setSearchLoadingMore(false);
        // Stable First Paint V1 — the first (non-append) search for this
        // sequence has now settled, success or failure. Runs regardless of
        // which branch above returned, since `finally` always executes.
        if (!opts.append) setAwaitingFirstSearch(false);
      }
    }
  };

  // Marketplace Inventory Scale & Discovery V1, task 8 — a new destination
  // OR any filter/sort change always resets the cursor and re-fetches page
  // 1 (never patches page 2+ onto a stale filter's results). filtersKey
  // compares by VALUE (flattened string), not object identity — a parent
  // re-render can hand down a new filters object/Set with the same members.
  const filtersKey = marketplaceFiltersKey(filters);
  useEffect(() => {
    if (isMultiCity) return;
    void runMarketplaceSearch({ append: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sequence, filtersKey, isMultiCity]);

  const handleLoadMoreExperiences = () => {
    if (!searchHasMore || searchLoadingMore || searchWarmupPending) return;
    track("marketplace_load_more_clicked", {
      destination_iata: sequence.city_iata,
      page_size:        24,
      filter_count:      activeFilterCount,
      sort:             filters.sortBy,
    });
    void runMarketplaceSearch({ cursor: searchCursor, append: true });
  };

  // Sprint 2A — Marketplace Actions: add/remove/reset for the local
  // selection. Marketplace Comparison V1 — reframed as "comparar": every
  // add/remove fires comparison_item_added/removed with safe metadata
  // looked up from the same sourceItems array the marketplace list itself
  // reads (never a description/query/booking_url). "Same destination" is
  // never checked explicitly — this whole comparison set is scoped to one
  // sequence/one destination by construction (isMultiCity sequences never
  // populate the marketplace list these items come from, and the effect
  // above clears the set on every new search) — see providerLinkState.ts
  // and comparisonDecision.ts for the rest of the comparison rules.
  const addToSelection = (compositeId: string, title: string) => {
    if (selectedIds.has(compositeId)) return;
    // The item must be visible on whatever's currently rendered to have been
    // clickable at all — look it up there and snapshot it into
    // comparedExpById so it survives a later filter change or page swap.
    const exp = comparedExpById.get(compositeId) ?? effectiveSourceItems.find(e => e.id === compositeId);
    const comparisonSize = selectedIds.size + 1;
    setSelectedIds(prev => {
      if (prev.has(compositeId)) return prev;
      const next = new Set(prev);
      next.add(compositeId);
      return next;
    });
    setSelectionOrder(prev => prev.includes(compositeId) ? prev : [...prev, compositeId]);
    if (exp) {
      setComparedExpById(prev => {
        if (prev.has(compositeId)) return prev;
        const next = new Map(prev);
        next.set(compositeId, exp);
        return next;
      });
    }
    setDndAnnouncement(`${title}, añadida a comparar`);
    track("comparison_item_added", {
      destination_iata: sequence.city_iata,
      provider:         exp?.source_type === "provider" ? (exp.provider_name ?? null) : null,
      category:         exp?.category ?? null,
      comparison_size:  comparisonSize,
    });
    // Launch Analytics & Funnel Instrumentation V1 — canonical funnel
    // event, fired alongside the legacy one above.
    if (exp) {
      const _compareProps = buildCompareItemAddedProps(exp, sequence.city_iata, comparisonSize);
      track("marketplace_compare_item_added", _compareProps);
      // Admin Metrics UX V1 — same event, alongside PostHog.
      logMarketplaceEvent("marketplace_compare_item_added", {
        destination_iata: _compareProps.destination_iata,
        provider_name:    _compareProps.provider_name,
        category:         _compareProps.category,
      });
    }
  };

  const removeFromSelection = (compositeId: string, title: string) => {
    if (!selectedIds.has(compositeId)) return;
    const exp = comparedExpById.get(compositeId);
    const comparisonSize = Math.max(0, selectedIds.size - 1);
    setSelectedIds(prev => {
      if (!prev.has(compositeId)) return prev;
      const next = new Set(prev);
      next.delete(compositeId);
      return next;
    });
    setSelectionOrder(prev => prev.filter(id => id !== compositeId));
    setComparedExpById(prev => {
      if (!prev.has(compositeId)) return prev;
      const next = new Map(prev);
      next.delete(compositeId);
      return next;
    });
    setDndAnnouncement(`${title}, quitada de la comparación`);
    track("comparison_item_removed", {
      destination_iata: sequence.city_iata,
      provider:         exp?.source_type === "provider" ? (exp.provider_name ?? null) : null,
      category:         exp?.category ?? null,
      comparison_size:  comparisonSize,
    });
  };

  const resetSelection = () => {
    setSelectedIds(new Set());
    setSelectionOrder([]);
    setDndAnnouncement("Comparación vaciada");
  };

  // Marketplace Comparison V1, task 2 — up to MAX_COMPARISON_ITEMS at once
  // (raised to 6 by Comparador UX Redesign V1 — see comparisonDecision.ts).
  // An item already in the comparison is a silent no-op (no duplicates, no
  // dialog). A new item while already at capacity opens a small chooser to
  // replace one of them, instead of silently doing nothing or silently
  // evicting the oldest pick without asking — this route's own established
  // replace-chooser UX (AlertDialog below) is preserved as-is; only the
  // decision value it branches on was renamed ("request_replace" ->
  // "blocked") when comparisonDecision.ts's shared contract changed.
  const requestAddToComparison = (compositeId: string, title: string) => {
    // Comparison V2, Part E #1 — only real, bookable provider products can
    // be compared. Structurally already true in launch mode (the list
    // itself is provider-only), but enforced independently here too, since
    // the comparator has its own hard rule, not one merely inherited from
    // whatever happens to be visible right now (e.g. once
    // RHEA_SHOW_LOCAL_REFERENCES=true mixes local items back into the list).
    const candidateExp = comparedExpById.get(compositeId) ?? effectiveSourceItems.find(e => e.id === compositeId);
    if (!candidateExp || !isMarketplaceEligibleProvider(candidateExp)) return;
    const decision = getAddToComparisonDecision(compositeId, selectedIds);
    if (decision === "noop_already_selected") return;
    if (decision === "blocked") {
      setPendingCompare({ compositeId, title });
      return;
    }
    addToSelection(compositeId, title);
  };

  // Single state update for the swap (never two separate add/remove calls
  // chained in the same tick — both would read the same stale selectedIds
  // closure and report the wrong comparison_size to analytics).
  const replaceInComparison = (oldCompositeId: string, oldTitle: string, newCompositeId: string, newTitle: string) => {
    const newExp = comparedExpById.get(newCompositeId) ?? effectiveSourceItems.find(e => e.id === newCompositeId);
    // A replace is one atomic swap, not a remove-then-add — the comparison
    // size never actually changes (one out, one in), so it gets its own
    // distinct event (Part F) instead of a misleading added+removed pair
    // that would report a transient size change that never happened.
    const comparisonSize = selectedIds.size;

    setSelectedIds(prev => {
      const next = new Set(prev);
      next.delete(oldCompositeId);
      next.add(newCompositeId);
      return next;
    });
    setSelectionOrder(prev => {
      const withoutOld = prev.filter(id => id !== oldCompositeId);
      return withoutOld.includes(newCompositeId) ? withoutOld : [...withoutOld, newCompositeId];
    });
    setComparedExpById(prev => {
      const next = new Map(prev);
      next.delete(oldCompositeId);
      if (newExp) next.set(newCompositeId, newExp);
      return next;
    });
    setDndAnnouncement(`${newTitle} reemplazó a ${oldTitle} en la comparación`);

    track("comparison_item_replaced", {
      destination_iata: sequence.city_iata,
      provider:         newExp?.source_type === "provider" ? (newExp.provider_name ?? null) : null,
      category:         newExp?.category ?? null,
      comparison_size:  comparisonSize,
    });
  };

  const confirmReplaceInComparison = (oldCompositeId: string, oldTitle: string) => {
    if (!pendingCompare) return;
    replaceInComparison(oldCompositeId, oldTitle, pendingCompare.compositeId, pendingCompare.title);
    setPendingCompare(null);
  };

  // "Marketplace Behavior V2" — no baseline to diff against anymore (there is
  // no preselection), so this is simply "has the user added anything yet."
  const hasSelection = selectedIds.size > 0;

  // Frontend Marketplace Integration V1 — sourceItems is the sequence's own
  // marketplace_inventory (deeper, gate-filtered, ranked) when present, else
  // flattened days[].experiences for older/cached responses. This remains
  // the FALLBACK/first-paint source: it's what's shown immediately (no
  // empty flash) and permanently if /experiences/search fails — task 6.
  const { items: sourceItems, source: sourceKind } = isMultiCity
    ? { items: [] as ExperienceItem[], source: "days_fallback" as const }
    : getMarketplaceSource(sequence);

  // Marketplace Inventory Scale & Discovery V1 — once the paginated
  // /experiences/search endpoint has answered successfully for this
  // sequence, its (already gate-filtered/ranked/deduped AND already
  // server-filtered-by-the-active-filters) items become the source of
  // truth; sourceItems is the pre-search/fallback state only.
  const usingBackendSearch = !isMultiCity && searchReady && !searchUsingFallback;
  const effectiveSourceItems: ExperienceItem[] = usingBackendSearch ? searchItems : sourceItems;
  // Marketplace Inventory Scale & Discovery V1, task 8 — the real max price
  // across the FULL matching pool (not just loaded items), rounded/capped
  // with the exact same rule computeDynamicMaxPrice() uses so the UI reads
  // identically either way. undefined (not null) when backend search isn't
  // active yet, so FilterBar/ItineraryFilterChipPanel fall back to their own
  // client-side derivation instead of treating "no override" as "no price
  // data at all".
  const backendDynamicMaxPrice = usingBackendSearch
    ? (searchFacets?.max_price_available != null
        ? Math.min(roundUpToCleanStep(searchFacets.max_price_available), PRICE_FILTER_UI_CAP)
        : null)
    : undefined;
  // Marketplace Continuity, Honest Pricing & Log Privacy V1, Parte B —
  // same override pattern as backendDynamicMaxPrice: once backend search
  // is active, the real tip_based count across the FULL price-ceiling-
  // free pool (facets.tip_based_count) replaces the client-side count
  // derived from whatever's loaded so far. undefined (not null/0) when
  // backend search isn't active yet, so FilterBar/ItineraryFilterChipPanel
  // fall back to counting allItems locally instead of reading "no data".
  const backendTipBasedCount = usingBackendSearch
    ? (searchFacets?.tip_based_count ?? 0)
    : undefined;

  // Filters/sort are applied server-side once usingBackendSearch is true
  // (task 8) — re-applying them client-side here would be redundant at
  // best; skip both and just pass the backend's own order straight through.
  const filteredItems = usingBackendSearch
    ? effectiveSourceItems
    : (filtersActive ? applyFilters(sourceItems, filters) : sourceItems);
  const totalFiltered = filteredItems.length;
  // Provider-Only Marketplace & Comparison V2 Foundation, Part D #1 — the
  // Madrid "$50 filter drops 50->2 and says Inventario limitado" bug: this
  // MUST be the destination's real depth before ANY filter, never the
  // currently-filtered matching pool (searchFacets), so applying a filter
  // can never make a destination look artificially thin. Falls back to
  // searchFacets only for responses that predate destination_depth.
  const totalOriginal = usingBackendSearch
    ? (searchDestinationDepth
        ? searchDestinationDepth.provider_count + searchDestinationDepth.local_count
        : (searchFacets ? searchFacets.total_provider_count + searchFacets.total_local_count : 0))
    : sourceItems.length;
  const inventoryDepthCopy = getInventoryDepthCopy(totalOriginal);
  // Part D #2 — a SEPARATE, honest count for "how many match the ACTIVE
  // filters" (the full matching pool, before pagination) — never conflated
  // with the depth number above. In fallback mode filteredItems already IS
  // the full matching set (no separate pool-vs-loaded-page distinction).
  const matchingFilterCount = usingBackendSearch && searchFacets
    ? searchFacets.total_provider_count + searchFacets.total_local_count
    : totalFiltered;
  // Launch Conversion UX V1, task 4: honest, distinct banner for reference-
  // only/thin-supply destinations — a real UI element (not trailing inline
  // text) so it reads as a genuine product state, not an afterthought.
  const marketplaceProviderCount = usingBackendSearch && searchFacets
    ? searchFacets.total_provider_count
    : getMarketplaceCounts(sequence, sourceItems, sourceKind).providerCount;
  const coverageTierBanner = getCoverageTierBanner(
    usingBackendSearch ? searchCoverageTier : sequence.coverage_tier,
    marketplaceProviderCount,
  );

  // "Marketplace Behavior V2" — RHEA recommendation context, built entirely
  // from data already on hand (detected interests, whether kids are along,
  // and an explicit budget ceiling only if the user actually set one below
  // the wide-open default). Never invents a signal.
  const rheaRecommendationContext = {
    interests:    sequence.interests ?? [],
    hasChildren:  (children ?? 0) > 0,
    budgetMaxUsd: sidebarBudgetMax < 5000 ? sidebarBudgetMax : null,
  };
  const rheaRecommendationFilters = {
    categories: filters.categories,
    timeOfDay:  filters.timeOfDay,
    reservable: filters.reservable,
  };
  const rankItem = (exp: ExperienceItem) =>
    getRheaRecommendationRank(exp, rheaRecommendationContext, rheaRecommendationFilters, sequence);

  // "Marketplace Inventory Layout V1" — the unified "Todas las experiencias
  // disponibles" feed. Default order is the RHEA recommendation rank; defers
  // to the user's manual sort (Precio/Duración/Proveedor) when active.
  // Recommendation badges are computed from the rank regardless of which
  // sort is showing, so they never shuffle with the display order.
  // usingBackendSearch: the server already sorted (default rank order, or
  // the user's explicit sort) — pass an identity function so
  // buildMarketplaceInventory preserves it exactly instead of re-sorting
  // client-side (rankItem below still runs regardless, but only for badge
  // assignment — see buildMarketplaceInventory's own doc: badges are always
  // computed from rankItem, independent of whichever sortItems is passed).
  // Recommendation Badge Coherence V1 — an item confidently classified into
  // a DIFFERENT specific activity family than the one rawQuery names (e.g.
  // Pub Crawl for "Madrid Segway tour") never earns a badge, regardless of
  // how it otherwise scores. rawQuery is the user's literal typed text —
  // exactly what a generic query ("Madrid", "Madrid experiencias") leaves
  // untouched, so this never narrows badge eligibility for a broad search.
  const isActivityIntentIncompatible = (exp: ExperienceItem) =>
    hasActivityIntentConflict(exp, rawQuery);

  // Stable First Paint V1 — while awaitingFirstSearch, inventory (and
  // everything derived from it below: intentGroups, the "Recomendado por
  // Sift Trip" shelf, the themed teaser, the main list) stays empty rather
  // than sourced from sourceItems/filteredItems (sequence.marketplace_
  // inventory) — every provisional-list surface, not just the main list,
  // must skip straight to the final /experiences/search result.
  const inventory = (isMultiCity || awaitingFirstSearch) ? [] : buildMarketplaceInventory(
    filteredItems,
    selectedIds,
    usingBackendSearch
      ? (exps) => exps
      : (filters.sortBy !== "default" ? (exps) => sortExperiences(exps, filters.sortBy) : undefined),
    rankItem,
    isActivityIntentIncompatible,
  );

  // Marketplace Inventory Sections V1 — split the (already sorted) inventory
  // by user intent: core city experiences, excursions/multi-day/multi-city
  // trips, and local/reference content. Filtering preserves the active sort
  // order within each group (see groupMarketplaceByIntent's own doc), so —
  // unlike the old provider/local split this replaces — the grouping now
  // applies regardless of the active sort, not just the default ordering.
  const cityNamesForGrouping = [sequence.city_display, sequence.city].filter(
    (n): n is string => typeof n === "string" && n.length > 0,
  );
  const intentGroups = groupMarketplaceByIntent(inventory, cityNamesForGrouping);

  // "Vertical discovery rhythm" — a small "Recomendado por Sift Trip" shelf plus
  // one optional themed teaser before the full inventory. Items shown here
  // are never removed from the main list below — this is a highlight, not a
  // move — but the teaser avoids repeating whatever's already in the shelf so
  // the two discovery moments never show the same item twice.
  //
  // Task 3: the shelf prefers real city experiences — an excursion/transit/
  // logistics item only fills a shelf slot when there aren't enough badged
  // city experiences to begin with (badges themselves already come from
  // rankItem's interest-matching score computed over the whole inventory).
  const badgedCityExperiences = intentGroups.cityExperiences.filter(i => i.recommendationBadge !== null);
  const badgedExcursions      = intentGroups.excursions.filter(i => i.recommendationBadge !== null);
  const recommendedItems = badgedCityExperiences.length >= 3
    ? badgedCityExperiences
    : [...badgedCityExperiences, ...badgedExcursions].slice(0, 3);
  const recommendedIds   = new Set(recommendedItems.map(i => i.compositeId));
  const themedTeaser      = pickThemedTeaser(
    intentGroups.cityExperiences.filter(i => !recommendedIds.has(i.compositeId)),
  );
  const excursionIds = new Set(intentGroups.excursions.map(i => i.compositeId));

  // Main list sections — a header only appears when more than one group is
  // actually non-empty (a destination that's 100% core city content, with
  // zero excursions and zero local references, shouldn't show a
  // "Experiencias en la ciudad" header with nothing to contrast it against).
  const marketplaceIntentSections: { key: string; label: string; items: typeof inventory }[] = [
    { key: "city", label: "Experiencias en la ciudad", items: intentGroups.cityExperiences },
    { key: "excursions", label: "Excursiones y viajes de varios días", items: intentGroups.excursions },
    { key: "local", label: "Referencias locales", items: intentGroups.localReferences },
  ].filter(section => section.items.length > 0);
  const showIntentSectionHeaders = marketplaceIntentSections.length > 1;

  // Result count copy — split so users understand the mix at a glance
  // ("50 experiencias · 10 excursiones · 20 referencias locales") instead of
  // one opaque total — "experiencias" here means the city-experiences group
  // specifically once the split is shown (excursions/local are called out
  // separately, not double-counted into that number). Only mentions a group
  // when it's actually non-empty and there's more than one group to
  // contrast (same gating as the section headers above), so a 100%-core-
  // city destination still just says "23 experiencias" using the grand
  // total, not a fragmented "23 experiencias · 0 excursiones".
  // Marketplace Inventory Depth + Pagination V1 — "Mostrando" prefix makes
  // explicit that this is the count of what's currently loaded/displayed,
  // never a claim about Viator's total catalog (which /experiences/search
  // never reveals — total_available is always null).
  const inventoryCountCopy = "Mostrando " + (showIntentSectionHeaders
    ? [
        `${intentGroups.cityExperiences.length} ${intentGroups.cityExperiences.length === 1 ? "experiencia" : "experiencias"}`,
        intentGroups.excursions.length > 0
          ? `${intentGroups.excursions.length} ${intentGroups.excursions.length === 1 ? "excursión" : "excursiones"}`
          : null,
        intentGroups.localReferences.length > 0
          ? `${intentGroups.localReferences.length} ${intentGroups.localReferences.length === 1 ? "referencia local" : "referencias locales"}`
          : null,
      ].filter(Boolean).join(" · ")
    : `${inventory.length} ${inventory.length === 1 ? "experiencia" : "experiencias"} disponible${inventory.length === 1 ? "" : "s"}`);

  // Selection tray items, resolved from selectionOrder (display order) back to
  // their source ExperienceItem via the same flat marketplace source the
  // inventory list itself uses. Never mutates sequence.
  // Marketplace Inventory Scale & Discovery V1, task 9 — resolved from
  // comparedExpById (a persistent snapshot), NOT from whatever page/filter
  // state currently populates the marketplace list, so a compared item
  // never disappears from the tray just because a filter change or page
  // swap made it fall out of the currently-loaded set.
  const trayItems: TrayItem[] = isMultiCity ? [] : selectionOrder
    .map(compositeId => {
      const exp = comparedExpById.get(compositeId);
      if (!exp) return null;
      return { exp, neighborhood: exp.neighborhood || "", compositeId };
    })
    .filter((x): x is TrayItem => x !== null);

  return (
    <div className="pt-1">

      {/* Screen-reader announcements for selection add/remove/reorder */}
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {dndAnnouncement}
      </div>

      {/* ── Multi-city journey rendering ──────────────────────────────────── */}
      {isMultiCity ? (
        <MultiCityTimeline sequence={sequence} />
      ) : (
        <>
          {/* 3-col marketplace workspace: left filter rail | center inventory | right selection tray + map */}
          <div className="xl:grid xl:grid-cols-[220px_minmax(0,1fr)_280px] 2xl:grid-cols-[240px_minmax(0,1fr)_300px] xl:gap-4 2xl:gap-5 xl:items-start">

            {/* LEFT RAIL — marketplace filters first, traveler/budget lower priority, sticky, xl only.
                Quiet by design: no boxed cards, just a hairline divider between the two groups. */}
            <div className="hidden xl:flex xl:flex-col sticky top-[140px] self-start">
              <ItineraryFilterChipPanel
                sequence={sequence}
                filters={filters}
                onFiltersChange={setFilters}
                activeFilterCount={activeFilterCount}
                dynamicMaxPriceOverride={backendDynamicMaxPrice}
                tipBasedCountOverride={backendTipBasedCount}
              />
              <div className="mt-5 pt-5" style={{ borderTop: "1px solid oklch(0.880 0.024 72 / 0.30)" }}>
                <TravelersPanel
                  adults={adults ?? 1}
                  loading={sidebarLoading}
                  dirty={sidebarDirty}
                  onAdultsChange={onSidebarAdultsChange ?? (() => {})}
                  onApply={onSidebarApply ?? (() => {})}
                />
              </div>
            </div>

            {/* CENTER — intro + toolbar + marketplace inventory */}
            <div className="xl:min-w-0">

              {/* Results hero — compact editorial header, not a dashboard fact panel */}
              <motion.div {...fadeUp(0.06, 0.55)}>
                <ResultsHero
                  sequence={sequence}
                  adults={adults}
                  children={children}
                  selectedIds={selectedIds}
                  hasSelection={hasSelection}
                  onResetSelection={resetSelection}
                  isMultiCity={isMultiCity}
                  searchReady={searchReady}
                  searchUsingFallback={searchUsingFallback}
                  searchDestinationDepth={searchDestinationDepth}
                  searchFacets={searchFacets}
                />
              </motion.div>

              {/* Toolbar: filters + new search */}
              <MarketplaceToolbar
                onNewSearch={() => {
                  track("new_search_clicked", { from_city: sequence.city_display });
                  onNewSearch();
                }}
                onClearFilters={() => {
                  track("filter_applied", { filter_type: "category", value: "clear_all" });
                  setFilters({ ...EMPTY_FILTERS, categories: new Set(), marketLocale: filters.marketLocale });
                }}
                filterBar={<FilterBar sequence={sequence} filters={filters} onChange={setFilters} dynamicMaxPriceOverride={backendDynamicMaxPrice} tipBasedCountOverride={backendTipBasedCount} />}
                filterNote={filtersActive
                  ? (matchingFilterCount === 0
                      ? (
                        <>
                          No hay experiencias que coincidan con estos filtros.{" "}
                          <button
                            type="button"
                            onClick={() => {
                              track("filter_applied", { filter_type: "category", value: "clear_all" });
                              setFilters({ ...EMPTY_FILTERS, categories: new Set(), marketLocale: filters.marketLocale });
                            }}
                            className="underline font-semibold"
                            style={{ color: "oklch(0.580 0.220 256)" }}
                          >
                            Limpiar filtros
                          </button>
                        </>
                      )
                      : `${matchingFilterCount} ${matchingFilterCount === 1 ? "experiencia" : "experiencias"} coinciden con tus filtros`)
                  : undefined}
                activeFilterCount={activeFilterCount}
              />

              {/* Comparar tray — mobile/tablet only, collapsible inline section */}
              <div className="xl:hidden mb-4">
                <SelectionTray
                  items={trayItems}
                  onRemove={removeFromSelection}
                  isModified={hasSelection}
                  onReset={resetSelection}
                  onOpenComparison={() => setComparisonOpen(true)}
                  collapsible
                />
              </div>

              {/* Family mode banner */}
              {familyMode && (
                <motion.div
                  {...fadeUp(0.17, 0.45)}
                  className="mb-4 flex items-center gap-2.5 rounded-2xl px-4 py-3"
                  style={{
                    background: "oklch(0.960 0.018 258 / 0.55)",
                    border: "1px solid oklch(0.580 0.220 256 / 0.18)",
                  }}
                >
                  <Users size={18} aria-hidden style={{ color: "oklch(0.22 0.050 258)", flexShrink: 0 }} />
                  <div>
                    <p className="text-[12.5px] font-semibold leading-tight" style={{ color: "oklch(0.22 0.050 258)" }}>
                      Experiencias familiares
                    </p>
                    <p className="text-[11px] leading-tight mt-0.5" style={{ color: "oklch(0.545 0.020 60 / 0.65)" }}>
                      Experiencias aptas para todas las edades · ritmo adaptado
                    </p>
                  </div>
                </motion.div>
              )}

              {/* Recomendado por Sift Trip — a discovery moment before the full inventory.
                  Never preselected: same Añadir/En selección action as every row. */}
              <FeaturedShelf
                title="Recomendado por Sift Trip"
                icon={<Sparkles size={13} aria-hidden style={{ color: "#E8194B" }} />}
                items={recommendedItems}
                cityHint={sequence.city_display}
                cityIata={sequence.city_iata}
                rawQuery={rawQuery}
                showListHint={recommendedItems.length > 0}
                excursionIds={excursionIds}
                onAdd={requestAddToComparison}
                onRemove={removeFromSelection}
                onOpenDetail={openDetail}
              />

              {/* Themed teaser — dynamic, only when the data actually supports one */}
              {themedTeaser && (
                <FeaturedShelf
                  title={themedTeaser.label}
                  items={themedTeaser.items}
                  cityHint={sequence.city_display}
                  cityIata={sequence.city_iata}
                  rawQuery={rawQuery}
                  onOpenDetail={openDetail}
                  showListHint
                  onAdd={requestAddToComparison}
                  onRemove={removeFromSelection}
                />
              )}

              {/* Marketplace inventory — "Experiencias disponibles" */}
              <section>
                {/* flex-wrap + min-w-0: the split count copy
                    ("N experiencias · N excursiones · N referencias
                    locales") is meaningfully longer than the old single
                    count — wraps to its own line at 390px instead of
                    forcing horizontal overflow (Marketplace Inventory
                    Sections V1, task 6). */}
                <div className="flex items-baseline justify-between gap-x-2 gap-y-0.5 flex-wrap mb-1">
                  <h2 className="min-w-0 font-display font-bold text-[15px] sm:text-base" style={{ color: "var(--color-heading-navy)" }}>
                    Experiencias disponibles
                  </h2>
                  <span className="text-[11px]" style={{ color: "oklch(0.545 0.020 60 / 0.55)" }}>
                    {inventoryCountCopy}
                  </span>
                </div>
                {/* Honest inventory-depth framing — never a warning banner, just
                    subtle context calibrated to the destination's real total. */}
                <p className="text-[11px] mb-2" style={{ color: "oklch(0.545 0.020 60 / 0.50)" }}>
                  {inventoryDepthCopy.headline} · {inventoryDepthCopy.detail}
                </p>
                {/* Launch Empty-State & Coverage UX V1, task 5 — /experiences/search
                    failed and this destination is showing sequence.marketplace_inventory
                    instead. Only when there's real content to show (never
                    alongside the zero-result empty state below, which already
                    speaks for itself) and never technical ("backend"/"endpoint"/
                    "fallback") — just a calm, honest heads-up that this list
                    might not reflect the very latest search refinements. */}
                {searchUsingFallback && !isMultiCity && inventory.length > 0 && (
                  <p className="text-[11px] mb-2" style={{ color: "oklch(0.545 0.020 60 / 0.45)" }}>
                    Resultados iniciales para este destino · podrían actualizarse en tu próxima búsqueda.
                  </p>
                )}
                {/* Launch Conversion UX V1, task 4: honest supplier-coverage
                    framing as a real, distinct UI element — never implies a
                    booking link exists, never calls local content "curated"/
                    "verified"/"confirmed". reference_only gets a visible
                    (but calm, informational — never alarming) bordered
                    banner; thin_provider_supply gets a lighter-weight line,
                    matching the smaller degree of caveat. */}
                {/* Launch Empty-State & Coverage UX V1 — the old reference_only
                    banner here was removed: providerCount === 0 always
                    coincides with inventory.length === 0, so it only ever
                    duplicated the richer empty state (message + destination
                    suggestions) rendered below. One honest message, not two. */}
                {coverageTierBanner?.kind === "thin_provider_supply" && (
                  <p className="text-[11px] mb-3" style={{ color: "oklch(0.545 0.020 60 / 0.50)" }}>
                    {coverageTierBanner.message}
                  </p>
                )}
                {awaitingFirstSearch ? (
                  // Stable First Paint V1 — never paint sequence.
                  // marketplace_inventory as a provisional list that then
                  // gets replaced by /experiences/search's own items (the
                  // "GuruWalk appears and disappears" perception this
                  // fixes). Reuses the exact same skeleton grammar as the
                  // sequence-level LoadingState above, so the transition
                  // reads as one continuous load, not two different states.
                  <div className="flex flex-col gap-2.5">
                    {[0, 1, 2].map(i => <SkeletonDayCard key={i} delay={i * 0.08} />)}
                  </div>
                ) : inventory.length === 0 ? (
                  searchWarmupPending ? (
                    // Progressive First Page + Deferred Provider Warmup
                    // V1, Part H — zero items but a deferred/deep warmup
                    // is still running: this is not yet the honest empty
                    // state, so it must never render as one. Reuses the
                    // exact same skeleton grammar as awaitingFirstSearch
                    // above.
                    <div className="flex flex-col gap-2.5">
                      {[0, 1, 2].map(i => <SkeletonDayCard key={i} delay={i * 0.08} />)}
                    </div>
                  ) : filtersActive ? (
                    <p
                      className="px-4 py-6 text-xs italic text-center rounded-xl"
                      style={{ color: "oklch(0.545 0.020 60 / 0.45)", border: "1px solid oklch(0.880 0.024 72 / 0.20)", background: "transparent" }}
                    >
                      No hay experiencias que coincidan con estos filtros.
                    </p>
                  ) : (
                    // Launch Stability & Error Recovery V1 — a genuine zero-provider
                    // destination (no filters active) is a different situation than
                    // "your filters excluded everything" above: there's no filter to
                    // clear, so the honest recovery is trying a different destination.
                    // Never falls back to showing local/reference content as filler —
                    // that would contradict provider-only launch mode.
                    <div
                      className="px-4 py-6 text-center rounded-xl"
                      style={{ border: "1px solid oklch(0.880 0.024 72 / 0.20)", background: "transparent" }}
                    >
                      <p className="text-xs italic" style={{ color: "oklch(0.545 0.020 60 / 0.45)" }}>
                        Aún no tenemos experiencias reservables para este destino.
                      </p>
                      <button
                        type="button"
                        onClick={onNewSearch}
                        className="mt-3 text-[12px] font-semibold px-4 rounded-lg transition-opacity hover:opacity-85"
                        style={{ minHeight: "40px", color: "oklch(0.998 0.003 72)", background: "oklch(0.120 0.040 258)" }}
                      >
                        Probar otra ciudad
                      </button>
                      {onTryDestination && (() => {
                        // Same shared Home-promotion allowlist as the
                        // landing page — never a second, separately-curated
                        // list — minus the destination that just came up
                        // empty, so we never "suggest" the city the user is
                        // already looking at. destinationSuggestions is
                        // populated by the dynamic import above (empty
                        // until it resolves, which is fine — this section
                        // simply doesn't render for that brief moment).
                        const suggestions = destinationSuggestions
                          .filter(c => c.iata !== sequence.city_iata);
                        return suggestions.length > 0 ? (
                          <div className="mt-4">
                            <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: "oklch(0.545 0.020 60 / 0.45)" }}>
                              O prueba un destino listo para reservar
                            </p>
                            <div className="flex flex-wrap items-center justify-center gap-1.5">
                              {suggestions.map(city => (
                                <button
                                  key={city.iata}
                                  type="button"
                                  onClick={() => onTryDestination(city.iata, city.display)}
                                  className="text-[11px] font-semibold px-3 rounded-full transition-opacity hover:opacity-80"
                                  style={{
                                    minHeight:  "36px",
                                    color:      "var(--color-heading-navy)",
                                    background: "oklch(0.960 0.010 72)",
                                    border:     "1px solid oklch(0.880 0.024 72 / 0.6)",
                                  }}
                                >
                                  {city.display}
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  )
                ) : (
                  <div className="flex flex-col gap-4">
                    {marketplaceIntentSections.map(section => (
                      <div key={section.key}>
                        {/* Compact header — a short uppercase label + count,
                            never pushing the first card far down (Marketplace
                            Inventory Sections V1, task 6: mobile polish). */}
                        {showIntentSectionHeaders && (
                          <p
                            className="text-[10px] font-bold uppercase tracking-[0.10em] mb-2"
                            style={{ color: "oklch(0.545 0.020 60 / 0.45)" }}
                          >
                            {section.label}
                            <span className="normal-case font-semibold"> · {section.items.length}</span>
                          </p>
                        )}
                        <div
                          className="rounded-xl overflow-hidden divide-y divide-[oklch(0.880_0.024_72_/_0.14)]"
                          style={{ border: "1px solid oklch(0.880 0.024 72 / 0.20)", background: "oklch(0.998 0.003 72 / 0.6)" }}
                        >
                          {section.items.map(item => (
                            <ExperienceMarketplaceRow
                              key={item.compositeId}
                              item={item}
                              cityHint={sequence.city_display}
                              cityIata={sequence.city_iata}
                              rawQuery={rawQuery}
                              isExcursion={excursionIds.has(item.compositeId)}
                              onAdd={() => requestAddToComparison(item.compositeId, item.exp.title)}
                              onRemove={() => removeFromSelection(item.compositeId, item.exp.title)}
                              onOpenDetail={openDetail}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Marketplace Inventory Scale & Discovery V1, task 7 —
                    loading/"load more" lives entirely inside this section;
                    filters, sort, comparison, and scroll position are
                    completely untouched by any of this. Existing results
                    are never replaced while the next page is in flight —
                    searchItems only ever grows via append. */}
                {/* Stable First Paint V1 — the initial (first-search)
                    loading state now has its own skeleton below instead of
                    this text, so this is scoped to a LATER re-search
                    (filter/sort change) only, never the first paint. */}
                {!isMultiCity && searchLoading && !usingBackendSearch && !awaitingFirstSearch && (
                  <p className="mt-3 text-[11px] text-center" style={{ color: "oklch(0.545 0.020 60 / 0.45)" }}>
                    Actualizando resultados…
                  </p>
                )}
                {!isMultiCity && usingBackendSearch && inventory.length > 0 && (
                  <div className="mt-4 flex flex-col items-center gap-2">
                    {searchLoadError && (
                      <p className="text-[12px] text-center" style={{ color: "oklch(0.55 0.14 30)" }}>
                        {searchLoadError}
                      </p>
                    )}
                    {searchHasMore ? (
                      <button
                        type="button"
                        onClick={handleLoadMoreExperiences}
                        disabled={searchLoadingMore || searchWarmupPending}
                        className="px-5 rounded-lg text-[13px] font-bold transition-opacity hover:opacity-90 disabled:cursor-not-allowed"
                        style={{
                          minHeight:  "44px",
                          color:      "oklch(0.998 0.003 72)",
                          background: "oklch(0.120 0.040 258)",
                          opacity:    (searchLoadingMore || searchWarmupPending) ? 0.6 : 1,
                        }}
                      >
                        {searchWarmupPending
                          ? "Preparando más experiencias…"
                          : searchLoadingMore
                            ? "Cargando más experiencias…"
                            : "Ver más experiencias"}
                      </button>
                    ) : searchCursorExpired ? (
                      <button
                        type="button"
                        onClick={() => { void runMarketplaceSearch({ append: false }); }}
                        disabled={searchLoading}
                        className="px-5 rounded-lg text-[13px] font-bold transition-opacity hover:opacity-90 disabled:cursor-not-allowed"
                        style={{
                          minHeight: "44px",
                          color: "oklch(0.120 0.040 258)",
                          background: "oklch(0.960 0.010 72)",
                          border: "1px solid oklch(0.880 0.024 72 / 0.65)",
                        }}
                      >
                        Actualizar resultados
                      </button>
                    ) : (
                      // Marketplace Inventory Depth + Pagination V1 — honest
                      // end-of-inventory state: never claims to have shown
                      // "all available" experiences (Viator's real catalog
                      // can be far deeper than what we load per search) —
                      // only that there's nothing more queued to load here.
                      <p className="text-[11px] text-center" style={{ color: "oklch(0.545 0.020 60 / 0.45)" }}>
                        No hay más experiencias para cargar en esta búsqueda.
                      </p>
                    )}
                  </div>
                )}
              </section>

              {/* Map — mobile/tablet only, collapsible, auxiliary */}
              <div
                className="xl:hidden mt-4 overflow-hidden rounded-lg"
                style={{
                  background: "oklch(0.993 0.004 72)",
                  border:     "1px solid oklch(0.880 0.024 72 / 0.35)",
                }}
              >
                <button
                  type="button"
                  className="w-full flex items-center justify-between gap-2 px-4 py-2.5"
                  style={{
                    borderBottom: mapOpen ? "1px solid oklch(0.880 0.024 72 / 0.20)" : undefined,
                    cursor: "pointer",
                    background: "transparent",
                  }}
                  onClick={() => setMapOpen(v => !v)}
                  aria-expanded={mapOpen}
                  aria-controls="mobile-marketplace-map"
                >
                  <p
                    className="text-[9px] font-black tracking-[0.18em] uppercase"
                    style={{ color: "#E8194B" }}
                  >
                    Mapa aproximado
                  </p>
                  <span
                    className="flex items-center gap-1 text-[10px] font-medium"
                    style={{ color: "oklch(0.545 0.020 60 / 0.55)" }}
                  >
                    {mapOpen ? "Ocultar mapa aproximado" : "Ver mapa aproximado"}
                    <ChevronDown
                      size={11}
                      aria-hidden
                      style={{
                        color: "#E8194B",
                        transition: "transform 200ms",
                        transform: mapOpen ? "rotate(180deg)" : "rotate(0deg)",
                      }}
                    />
                  </span>
                </button>
                {mapOpen && (
                  <div id="mobile-marketplace-map" className="p-2.5">
                    <LazyPanelBoundary onClose={() => setMapOpen(false)}>
                      <Suspense fallback={<SkeletonBlock w="100%" h={150} rounded="lg" />}>
                        <ItineraryGeoMap
                          sequence={sequence}
                          draftDays={sequence.days}
                          activeDay={1}
                          viewMode="timeline"
                          height={150}
                          activityMapMode
                        />
                      </Suspense>
                    </LazyPanelBoundary>
                  </div>
                )}
              </div>

            </div>{/* /center column */}

            {/* RIGHT RAIL — comparar tray + map, sticky, xl only */}
            <div className="hidden xl:flex xl:flex-col xl:gap-3 sticky top-[140px] self-start">
              <SelectionTray
                items={trayItems}
                onRemove={removeFromSelection}
                isModified={hasSelection}
                onReset={resetSelection}
                onOpenComparison={() => setComparisonOpen(true)}
              />
              {/* Marketplace Map Launch Decision V1 — collapsed by default,
                  secondary to the tray above it; pins are largely synthetic
                  (production audit), so this never opens pre-expanded, and
                  its copy is explicit about being approximate. */}
              <div
                className="overflow-hidden rounded-lg"
                style={{
                  background: "oklch(0.993 0.004 72)",
                  border:     "1px solid oklch(0.880 0.024 72 / 0.35)",
                }}
              >
                <button
                  type="button"
                  className="w-full flex items-center justify-between gap-2 px-3 py-2"
                  style={{
                    borderBottom: mapOpen ? "1px solid oklch(0.880 0.024 72 / 0.20)" : undefined,
                    cursor: "pointer",
                    background: "transparent",
                  }}
                  onClick={() => setMapOpen(v => !v)}
                  aria-expanded={mapOpen}
                  aria-controls="desktop-marketplace-map"
                >
                  <p
                    className="text-[9px] font-black tracking-[0.18em] uppercase"
                    style={{ color: "#E8194B" }}
                  >
                    Mapa aproximado
                  </p>
                  <span
                    className="flex items-center gap-1 text-[9px] font-medium"
                    style={{ color: "oklch(0.545 0.020 60 / 0.45)" }}
                  >
                    {mapOpen ? "Ocultar mapa aproximado" : "Ver mapa aproximado"}
                    <ChevronDown
                      size={11}
                      aria-hidden
                      style={{
                        color: "#E8194B",
                        transition: "transform 200ms",
                        transform: mapOpen ? "rotate(180deg)" : "rotate(0deg)",
                      }}
                    />
                  </span>
                </button>
                {mapOpen && (
                  <div id="desktop-marketplace-map" className="p-2.5">
                    <LazyPanelBoundary onClose={() => setMapOpen(false)}>
                      <Suspense fallback={<SkeletonBlock w="100%" h={190} rounded="lg" />}>
                        <ItineraryGeoMap
                          sequence={sequence}
                          draftDays={sequence.days}
                          activeDay={1}
                          viewMode="timeline"
                          height={190}
                          activityMapMode
                        />
                      </Suspense>
                    </LazyPanelBoundary>
                  </div>
                )}
              </div>
            </div>

          </div>{/* /3-col grid */}
        </>
      )}

      {/* Footer */}
      <motion.div
        {...fadeUp(0.18 + sequence.days.length * 0.07 + 0.08)}
        className="mt-8 pt-6 border-t text-center space-y-1"
        style={{ borderColor: "oklch(0.880 0.024 72 / 0.26)" }}
      >
        <p className="inline-flex items-center justify-center gap-1 text-xs font-medium" style={{ color: "oklch(0.545 0.020 60 / 0.42)" }}>
          <Sparkles size={12} aria-hidden />
          Resultados organizados por relevancia y disponibilidad
        </p>
        <p className="text-[10px]" style={{ color: "oklch(0.545 0.020 60 / 0.26)" }}>
          Precios orientativos en USD · Las reservas se completan con proveedores externos cuando están disponibles.
        </p>
        <p className="text-[10px]" style={{ color: "oklch(0.545 0.020 60 / 0.20)" }}>
          Imágenes: Unsplash
        </p>
      </motion.div>

      {/* Marketplace Product Detail & Conversion V1 — single shared drawer
          instance for every "Ver detalle" trigger on this page (featured
          shelf, marketplace list). Rendered via Portal (Sheet/Radix Dialog),
          so its position here doesn't affect layout or stacking. */}
      <LazyPanelBoundary onClose={() => { setDetailItem(null); setDetailSurface(null); }}>
        <Suspense fallback={null}>
          <ExperienceDetailDrawer
            item={detailItem}
            originSurface={detailSurface}
            cityHint={sequence.city_display}
            cityIata={sequence.city_iata}
            rawQuery={rawQuery}
            selectedIds={selectedIds}
            onAdd={requestAddToComparison}
            onRemove={removeFromSelection}
            onOpenChange={(open) => { if (!open) { setDetailItem(null); setDetailSurface(null); } }}
          />
        </Suspense>
      </LazyPanelBoundary>

      {/* Marketplace Comparison V1 — the real side-by-side comparison table,
          opened from "Ver comparación" in the Comparar tray. */}
      <LazyPanelBoundary onClose={() => setComparisonOpen(false)}>
        <Suspense fallback={null}>
          <ComparisonDrawer
            items={trayItems}
            open={comparisonOpen}
            onOpenChange={setComparisonOpen}
            onRemove={removeFromSelection}
            onClearAll={resetSelection}
            cityHint={sequence.city_display}
            cityIata={sequence.city_iata}
            rawQuery={rawQuery}
          />
        </Suspense>
      </LazyPanelBoundary>

      {/* Marketplace Comparison V1, task 2 — replace-one-of-three chooser,
          shown only when the user tries to add a 4th item. */}
      <AlertDialog
        open={pendingCompare !== null}
        onOpenChange={(next) => { if (!next) setPendingCompare(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ya tienes {MAX_COMPARISON_ITEMS} experiencias en comparación</AlertDialogTitle>
            <AlertDialogDescription>
              Elige cuál quitar para añadir "{pendingCompare?.title}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-col gap-2">
            {trayItems.map(item => (
              <button
                key={item.compositeId}
                type="button"
                onClick={() => confirmReplaceInComparison(item.compositeId, item.exp.title)}
                className="text-left px-3 rounded-lg text-[13px] font-semibold transition-colors hover:opacity-80"
                style={{ minHeight: "44px", background: "oklch(0.960 0.010 72)", border: "1px solid oklch(0.880 0.024 72 / 0.6)", color: "var(--color-heading-navy)" }}
              >
                Reemplazar "{item.exp.title}"
              </button>
            ))}
          </div>
          <AlertDialogFooter>
            {/* Marketplace Inventory Scale & Discovery V1, task 9 — the
                default button size is h-9 (36px); min-h-[44px] floors it to
                the accessible touch-target minimum without fighting the
                existing h-9 utility (CSS min-height wins when it exceeds
                height). */}
            <AlertDialogCancel className="min-h-[44px]" onClick={() => setPendingCompare(null)}>Cancelar</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


// ─── Activity pool — collapsible overflow in normal mode ──────────────────────
// Shows first POOL_VISIBLE items; hides the rest behind a "Más opciones" toggle.
// Only rendered in normal (non-edit) mode — edit mode uses SortableExperienceShell.

const POOL_VISIBLE = 3;

// ─── Local selection state (Sprint 2A — Marketplace Actions) ──────────────────
// "Marketplace Behavior V2" — no preselection. selectedIds/selectionOrder
// always start empty; the user builds their selection entirely by hand.
// Session-only — never mutates backend data or sequence.

function DayActivityPool({
  experiences,
  isExpanded,
  moodAccent,
  poolOpen,
  setPoolOpen,
  dayNumber,
  selectedIds,
  onAdd,
  onRemove,
}: {
  experiences:  ExperienceItem[];
  isExpanded:   boolean;
  moodAccent:   string;
  poolOpen:     boolean;
  setPoolOpen:  (open: boolean) => void;
  dayNumber?:   number;
  selectedIds?: Set<string>;
  onAdd?:       (compositeId: string, title: string) => void;
  onRemove?:    (compositeId: string, title: string) => void;
}) {
  const main  = experiences.slice(0, POOL_VISIBLE);
  const extra = experiences.slice(POOL_VISIBLE);

  return (
    <>
      {/* Main items — always visible */}
      {main.map((exp, idx) => {
        const compositeId = dayNumber != null ? makeCompositeId(dayNumber, exp.id) : null;
        return (
          <ExperienceRow
            key={exp.id}
            experience={exp}
            isLast={extra.length === 0 && idx === main.length - 1}
            isExpanded={isExpanded}
            moodAccent={moodAccent}
            isSelected={compositeId ? selectedIds?.has(compositeId) : undefined}
            onAdd={compositeId && onAdd ? () => onAdd(compositeId, exp.title) : undefined}
            onRemove={compositeId && onRemove ? () => onRemove(compositeId, exp.title) : undefined}
          />
        );
      })}
      {/* Alternatives section header */}
      {poolOpen && extra.length > 0 && (
        <div
          className="px-4 sm:px-5 pt-3 pb-1.5"
          style={{ borderTop: `1px solid ${moodAccent}20` }}
        >
          <p
            className="text-[9px] font-black tracking-[0.16em] uppercase"
            style={{ color: "#E8194B" }}
          >
            Más experiencias disponibles
          </p>
        </div>
      )}
      {/* Extra items — revealed when pool is open */}
      {poolOpen && extra.map((exp, idx) => {
        const compositeId = dayNumber != null ? makeCompositeId(dayNumber, exp.id) : null;
        return (
          <ExperienceRow
            key={exp.id}
            experience={exp}
            isLast={idx === extra.length - 1}
            isExpanded={isExpanded}
            moodAccent={moodAccent}
            isSelected={compositeId ? selectedIds?.has(compositeId) : undefined}
            onAdd={compositeId && onAdd ? () => onAdd(compositeId, exp.title) : undefined}
            onRemove={compositeId && onRemove ? () => onRemove(compositeId, exp.title) : undefined}
          />
        );
      })}
      {/* Collapse button — only shown when expanded */}
      {extra.length > 0 && poolOpen && (
        <button
          type="button"
          aria-expanded={true}
          onClick={() => setPoolOpen(false)}
          className="w-full flex items-center justify-center gap-1.5 px-4 sm:px-5 py-2.5 transition-opacity hover:opacity-70"
          style={{
            color:      "oklch(0.545 0.020 60 / 0.50)",
            background: `${moodAccent}04`,
            border:     "none",
            borderTop:  `1px solid ${moodAccent}18`,
            cursor:     "pointer",
            fontSize:   "10px",
            fontWeight: 600,
          }}
        >
          <ChevronUp size={12} aria-hidden />
          Ocultar experiencias
        </button>
      )}
    </>
  );
}


// ─── Day card ─────────────────────────────────────────────────────────────────
// Still used by the multi-city storytelling render (MultiCityTimeline). The
// single-city marketplace path uses ExperienceMarketplaceRow instead.

function DayCard({
  day,
  isExpanded,
  compact       = false,
  editMode      = false,
  filtersActive = false,
  dayIndex      = 0,
  totalDays     = 1,
  onMoveExp,
  activeOverId,
  activeDragId,
  insertAfter = false,
  cityDisplay = "",
  selectedIds,
  onAddToSelection,
  onRemoveFromSelection,
}: {
  day:            ExperienceDay;
  isExpanded:     boolean;
  compact?:       boolean;
  editMode?:      boolean;
  filtersActive?: boolean;
  dayIndex?:      number;
  totalDays?:     number;
  onMoveExp?:     (expIdx: number, action: "up" | "down" | "prev" | "next") => void;
  activeOverId?:  string | null;
  activeDragId?:  string | null;
  insertAfter?:   boolean;
  cityDisplay?:   string;
  /** Sprint 2A — local selection state, wired only by the interactive single-city path. */
  selectedIds?:            Set<string>;
  onAddToSelection?:       (compositeId: string, title: string) => void;
  onRemoveFromSelection?:  (compositeId: string, title: string) => void;
}) {
  // Day-level droppable — receives drops when dragging over an empty day or
  // below all items.  Always registered so dnd-kit can include it in collision
  // detection; visually active only in edit mode.
  const { setNodeRef: setDayDropRef, isOver: isDayOver } = useDroppable({
    id:       makeDayDropId(day.day_number),
    disabled: !editMode || filtersActive,
  });

  // Pool expansion state — lifted here so the top CTA can control it
  const [poolOpen, setPoolOpen] = useState(false);

  // Alternatives count: experiences beyond POOL_VISIBLE (excluding provider in featured slot)
  const _featIdxForCta = day.experiences.findIndex(e => e.source_type === "provider");
  const _poolExpCount  = _featIdxForCta === -1 ? day.experiences.length : day.experiences.length - 1;
  const alternativesCount = Math.max(0, _poolExpCount - POOL_VISIBLE);

  // Subtly highlight this day card when a cross-day drag targets one of its items
  const isCrossDayHighlight = editMode && activeDragId != null && activeOverId != null
    && activeOverId.startsWith(`${day.day_number}:`)
    && !activeDragId.startsWith(`${day.day_number}:`);

  // Use content-weighted display mood — avoids single provider skewing day identity
  const displayMoodKey = getDisplayMoodKey(day);
  const mood           = MOOD_META[displayMoodKey] ?? MOOD_META.default;

  // Pre-compute display title and decide if mood chip text is redundant
  const displayTitle = stripScheduleFraming(normalizeDayTitle(day, displayMoodKey), "title", cityDisplay);
  const showMoodChip = mood.label.toLowerCase().trim() !== displayTitle.toLowerCase().trim();

  const energyStyle = {
    high:   { color: "oklch(0.50 0.14 262)", bg: "oklch(0.50 0.14 262 / 0.08)", border: "oklch(0.50 0.14 262 / 0.22)" },
    low:    { color: "oklch(0.50 0.08 220)", bg: "oklch(0.50 0.08 220 / 0.08)", border: "oklch(0.50 0.08 220 / 0.22)" },
    medium: { color: "oklch(0.545 0.020 60)",  bg: "oklch(0.545 0.020 60 / 0.08)", border: "oklch(0.545 0.020 60 / 0.22)" },
  };
  const es = energyStyle[day.energy as keyof typeof energyStyle] ?? energyStyle.medium;

  return (
    <div className="flex gap-3 sm:gap-4">
      {/* Day number circle — smaller and higher to sit flush with card top */}
      <div className="shrink-0 flex flex-col items-center pt-2">
        <div
          className="w-[38px] h-[38px] rounded-full flex items-center justify-center font-sans font-black text-sm z-10 relative"
          style={{
            background: "var(--color-background)",
            border:     "2px solid #E8194B7A",
            color:      "var(--color-heading-navy)",
            boxShadow:  "0 0 0 4px var(--color-background)",
          }}
        >
          {day.day_number}
        </div>
      </div>

      {/* Right column */}
      <div className="flex-1 min-w-0">
        {/* Card */}
        <div
          className="rounded-2xl overflow-hidden transition-shadow duration-150"
          style={{
            background: isCrossDayHighlight ? `${mood.accent}06` : "oklch(0.998 0.003 72)",
            border:     `1px solid ${isCrossDayHighlight ? `${mood.accent}60` : `${mood.accent}28`}`,
            boxShadow:  isCrossDayHighlight
              ? `0 0 0 2px ${mood.accent}22, 0 1px 6px oklch(0.10 0.010 55 / 0.06)`
              : "0 1px 6px oklch(0.10 0.010 55 / 0.06)",
          }}
        >
          {/* Day header — compact editorial */}
          <div
            className="px-4 sm:px-5 pt-3 pb-3"
            style={{ borderBottom: `1px solid ${mood.accent}18`, background: mood.stripBg }}
          >
            {/* Row 1: mood chip (suppressed when it duplicates the title) + pacing badge */}
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                {(() => {
                  const MoodIc = MOOD_LUCIDE_ICONS[displayMoodKey] ?? Globe;
                  return <MoodIc size={10} aria-hidden style={{ color: mood.accent, flexShrink: 0 }} />;
                })()}
                {showMoodChip && (
                  <span className="text-[10px] font-semibold" style={{ color: mood.accent }}>
                    {mood.label}
                  </span>
                )}
              </div>
              {day.pacing && (
                <span
                  className="shrink-0 inline-flex items-center text-[8px] font-bold tracking-[0.06em] uppercase leading-none px-1.5 py-0.5 rounded-full"
                  style={{ background: es.bg, border: `1px solid ${es.border}`, color: es.color, opacity: 0.75 }}
                >
                  {day.pacing}
                </span>
              )}
            </div>

            {/* Row 2: Normalized title — editorial hero heading */}
            <h3
              className="font-display font-extrabold text-[15px] sm:text-[16px] leading-snug"
              style={{ color: "oklch(0.120 0.040 258)" }}
            >
              {displayTitle}
            </h3>

            {/* Row 3: Neighborhood / area */}
            {(day.neighborhood || day.day_subtitle) && (
              <p
                className="text-[10px] font-semibold tracking-[0.10em] uppercase mt-0.5 leading-snug"
                style={{ color: `${mood.accent}bb` }}
              >
                {day.neighborhood ?? stripScheduleFraming(day.day_subtitle ?? "", "subtitle", cityDisplay)}
              </p>
            )}

            {/* Row 4: Quick stats — compact single line */}
            {(() => {
              const hrs = day.experiences
                .filter(e => typeof e.duration_hours === "number" && (e.duration_hours as number) > 0)
                .reduce((s, e) => s + (e.duration_hours ?? 0), 0);
              const pc = day.experiences.filter(
                e => typeof e.provider_name === "string" && e.provider_name !== ""
              ).length;
              const statParts: string[] = [
                `${day.experiences.length} ${day.experiences.length === 1 ? "actividad" : "actividades"}`,
                ...(hrs > 0.5 ? [`~${Math.round(hrs)}h`] : []),
                ...(pc > 0 ? [`${pc} ${pc === 1 ? "proveedor" : "proveedores"}`] : []),
              ];
              return (
                <p
                  className="mt-1.5 text-[10px] leading-snug"
                  style={{ color: "oklch(0.545 0.020 60 / 0.45)" }}
                >
                  {statParts.join(" · ")}
                </p>
              );
            })()}

            {/* Alternatives CTA — visible near day header when hidden alternatives exist */}
            {!editMode && alternativesCount > 0 && !poolOpen && (
              <button
                type="button"
                aria-expanded={false}
                onClick={() => setPoolOpen(true)}
                className="mt-2.5 w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg transition-colors text-left"
                style={{
                  background: `${mood.accent}07`,
                  border:     `1px solid ${mood.accent}22`,
                  cursor:     "pointer",
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = `${mood.accent}0f`; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = `${mood.accent}07`; }}
              >
                <span
                  className="shrink-0 inline-flex items-center justify-center rounded-full text-[9px] font-black tabular-nums"
                  style={{
                    width:      "18px",
                    height:     "18px",
                    color:      mood.accent,
                    background: `${mood.accent}16`,
                    border:     `1px solid ${mood.accent}32`,
                  }}
                >
                  +{alternativesCount}
                </span>
                <span className="flex-1 text-[10px] font-medium leading-snug" style={{ color: "oklch(0.28 0.040 258 / 0.60)" }}>
                  {alternativesCount === 1 ? "experiencia disponible para comparar" : `experiencias disponibles para comparar`}
                </span>
                <ChevronDown size={11} aria-hidden style={{ color: mood.accent, opacity: 0.65, flexShrink: 0 }} />
              </button>
            )}
          </div>

          {/* Experience rows */}
          {editMode ? (
            /* Edit mode — single column, SortableContext per day, day-level droppable */
            <SortableContext
              items={day.experiences.map(e => makeCompositeId(day.day_number, e.id))}
              strategy={verticalListSortingStrategy}
            >
              {/* setDayDropRef makes this container a named droppable for empty-day drops */}
              <div ref={setDayDropRef}>
                {/* Edit mode hint */}
                {!filtersActive && day.experiences.length > 0 && (
                  <div className="px-4 sm:px-5 pt-2.5 pb-1">
                    <div
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg"
                      style={{
                        background: "oklch(0.38 0.10 262 / 0.06)",
                        border:     "1px solid oklch(0.38 0.10 262 / 0.18)",
                      }}
                    >
                      <GripVertical size={11} aria-hidden style={{ color: "oklch(0.38 0.10 262 / 0.60)", flexShrink: 0 }} />
                      <p className="text-[10px] font-medium" style={{ color: "oklch(0.38 0.10 262 / 0.60)" }}>
                        Arrastra actividades para ajustar el orden del borrador.
                      </p>
                    </div>
                  </div>
                )}
                {filtersActive && day.experiences.length > 0 && (
                  <div className="flex items-center gap-1.5 px-4 sm:px-5 pt-2.5 pb-1">
                    <p className="text-[10px]" style={{ color: "oklch(0.545 0.020 60 / 0.52)" }}>
                      Limpia los filtros para reorganizar la selección.
                    </p>
                  </div>
                )}
                {/* Pool-visibility note — only shown when view mode would hide extras */}
                {!filtersActive && day.experiences.length > POOL_VISIBLE && (
                  <div className="px-4 sm:px-5 pb-1">
                    <p className="text-[10px] italic" style={{ color: "oklch(0.545 0.020 60 / 0.38)" }}>
                      Todas las actividades están visibles para reorganizar.
                    </p>
                  </div>
                )}

                {day.experiences.map((exp, idx) => (
                  <SortableExperienceShell
                    key={makeCompositeId(day.day_number, exp.id)}
                    id={makeCompositeId(day.day_number, exp.id)}
                    expIdx={idx}
                    totalInDay={day.experiences.length}
                    hasPrevDay={dayIndex > 0}
                    hasNextDay={dayIndex < totalDays - 1}
                    filtersActive={filtersActive}
                    editMode={editMode}
                    insertionIndicator={
                      activeOverId === makeCompositeId(day.day_number, exp.id)
                        ? (insertAfter ? "below" : "above")
                        : undefined
                    }
                    onMoveUp={()     => onMoveExp?.(idx, "up")}
                    onMoveDown={()   => onMoveExp?.(idx, "down")}
                    onMoveToPrev={()  => onMoveExp?.(idx, "prev")}
                    onMoveToNext={()  => onMoveExp?.(idx, "next")}
                  >
                    <ExperienceRow
                      experience={exp}
                      isLast={idx === day.experiences.length - 1}
                      isExpanded={isExpanded}
                      moodAccent={mood.accent}
                    />
                  </SortableExperienceShell>
                ))}

                {/* Empty-day drop zone */}
                {day.experiences.length === 0 && (
                  <div
                    className="mx-4 my-3 rounded-xl flex items-center justify-center transition-colors"
                    style={{
                      minHeight:   "56px",
                      border:      `2px dashed ${isDayOver ? mood.accent : "oklch(0.880 0.024 72 / 0.45)"}`,
                      background:  isDayOver ? `${mood.accent}0a` : "transparent",
                    }}
                  >
                    <p className="text-xs" style={{ color: isDayOver ? mood.accent : "oklch(0.545 0.020 60 / 0.38)" }}>
                      {filtersActive ? "Sin experiencias para este filtro." : "Arrastra aquí"}
                    </p>
                  </div>
                )}
              </div>
            </SortableContext>
          ) : (
            /* Normal mode — single column always (compact prop preserved for future use) */
            <div>
              {day.experiences.length === 0 ? (
                <p className="px-5 py-4 text-xs italic" style={{ color: "oklch(0.545 0.020 60 / 0.40)" }}>
                  Sin experiencias para este filtro.
                </p>
              ) : (() => {
                const featIdx = day.experiences.findIndex(e => e.source_type === "provider");
                if (featIdx === -1) {
                  return (
                    <>
                      {day.narrative_intro && (
                        <p
                          className="px-4 sm:px-5 pt-2 pb-1 text-[11px] sm:text-xs leading-relaxed line-clamp-2 italic"
                          style={{ color: "oklch(0.38 0.010 55 / 0.50)" }}
                        >
                          {stripScheduleFraming(day.narrative_intro, "intro", cityDisplay)}
                        </p>
                      )}
                      <DayActivityPool
                        experiences={day.experiences}
                        isExpanded={isExpanded}
                        moodAccent={mood.accent}
                        poolOpen={poolOpen}
                        setPoolOpen={setPoolOpen}
                        dayNumber={day.day_number}
                        selectedIds={selectedIds}
                        onAdd={onAddToSelection}
                        onRemove={onRemoveFromSelection}
                      />
                    </>
                  );
                }
                const featuredExp = day.experiences[featIdx];
                const restExps = [
                  ...day.experiences.slice(0, featIdx),
                  ...day.experiences.slice(featIdx + 1),
                ];
                const featuredCompositeId = makeCompositeId(day.day_number, featuredExp.id);
                return (
                  <>
                    <FeaturedExperienceCard
                      exp={featuredExp}
                      moodAccent={mood.accent}
                      isSelected={selectedIds?.has(featuredCompositeId)}
                      onAdd={onAddToSelection ? () => onAddToSelection(featuredCompositeId, featuredExp.title) : undefined}
                      onRemove={onRemoveFromSelection ? () => onRemoveFromSelection(featuredCompositeId, featuredExp.title) : undefined}
                    />
                    {/* Editorial note — after anchor card, before timeline items */}
                    {day.narrative_intro && (
                      <p
                        className="px-4 sm:px-5 pt-1 pb-2 text-[11px] sm:text-xs leading-relaxed line-clamp-2 italic"
                        style={{ color: "oklch(0.38 0.010 55 / 0.50)" }}
                      >
                        {stripScheduleFraming(day.narrative_intro, "intro", cityDisplay)}
                      </p>
                    )}
                    <DayActivityPool
                      experiences={restExps}
                      isExpanded={isExpanded}
                      moodAccent={mood.accent}
                      poolOpen={poolOpen}
                      setPoolOpen={setPoolOpen}
                      dayNumber={day.day_number}
                      selectedIds={selectedIds}
                      onAdd={onAddToSelection}
                      onRemove={onRemoveFromSelection}
                    />
                  </>
                );
              })()}
            </div>
          )}

          {/* ── Provider Supply Options — augmentation layer ─────────────── */}
          {day.supply_options && day.supply_options.length > 0 && (
            <SupplyOptionsPanel options={day.supply_options} />
          )}

          {/* ── Evidence Context Block ───────────────────────────────────── */}
          {/* Rendered after provider experiences so editorial context
              supports the supplier product contextually (Phase 4).
              Dedup is applied inside EvidenceContextBlock — passing raw here
              so dedup logic lives in one place. */}
          {day.evidence_context && day.evidence_context.length > 0 && (
            <EvidenceContextBlock items={day.evidence_context} />
          )}

          {/* RHEA moment highlight */}
          {day.narrative_moment && (
            <div
              className="mx-4 sm:mx-5 my-3 px-4 py-3 rounded-xl"
              style={{
                background: `linear-gradient(135deg, ${mood.accent}0c 0%, ${mood.accent}06 100%)`,
                border:     `1px solid ${mood.accent}28`,
              }}
            >
              <div className="flex items-start gap-2">
                <span
                  className="shrink-0 text-[10px] font-black tracking-[0.18em] uppercase mt-0.5"
                  style={{ color: mood.accent }}
                >
                  Momento Sift Trip
                </span>
              </div>
              <p
                className="mt-1 text-[11px] sm:text-xs leading-relaxed italic"
                style={{ color: "oklch(0.18 0.010 55 / 0.60)" }}
              >
                {stripScheduleFraming(day.narrative_moment, "moment", cityDisplay)}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Link integrity helpers ───────────────────────────────────────────────────

/**
 * True when an item qualifies for an active booking CTA.
 *
 * Rules (per Real Data Policy):
 *   source_class === "LIVE_PROVIDER" AND affiliate_ready === true
 *   AND booking_url / url starts with https://
 *
 * Works for both ExperienceItem (booking_url) and ExperienceSupplyOption (url).
 */
function isBookableLiveProvider(item: {
  source_class?:   string | null;
  affiliate_ready?: boolean;
  booking_url?:    string | null;
  url?:            string;
}): boolean {
  if (item.source_class !== "LIVE_PROVIDER") return false;
  if (!item.affiliate_ready) return false;
  const link = item.booking_url ?? item.url ?? "";
  return typeof link === "string" && link.startsWith("https://");
}

// isUserFacingValidUrl moved to @/lib/experiences/linkIntegrity (shared with
// the marketplace inventory row).

// Phase 4: Maximum evidence items rendered per day card.
// Keeps the block compact while the product matures.
const MAX_EVIDENCE_ITEMS_PER_DAY = 1;

/**
 * Deduplicate evidence items with two-pass strategy:
 *
 * Pass 1 — Exact dedup: normalized (title + source_name + url) key.
 *          Removes byte-identical entries (shouldn't reach frontend, but safe.)
 *
 * Pass 2 — Per-source-name dedup: keep only the FIRST (highest-scored) item
 *          per source_name.  Prevents "Turismo di Verona · Fuente oficial"
 *          appearing twice from two editorial items of the same source.
 *
 * Preserves original order (backend sends highest-scored items first).
 */
function dedupeEvidenceItems(items: EvidenceContextItem[]): EvidenceContextItem[] {
  if (!items || items.length === 0) return [];

  // Pass 1: exact dedup
  const seenKeys = new Set<string>();
  const afterPass1 = items.filter(item => {
    const key = [
      (item.title       ?? "").trim().toLowerCase(),
      (item.source_name ?? "").trim().toLowerCase(),
      (item.url         ?? "").trim().toLowerCase(),
    ].join("|");
    if (seenKeys.has(key)) return false;
    seenKeys.add(key);
    return true;
  });

  // Pass 2: one item per source_name (keep first occurrence = highest score)
  const seenSources = new Set<string>();
  return afterPass1.filter(item => {
    const src = (item.source_name ?? "").trim().toLowerCase();
    // Items with no source_name are always kept (unknown origin)
    if (!src) return true;
    if (seenSources.has(src)) return false;
    seenSources.add(src);
    return true;
  });
}


// ─── Evidence context block ───────────────────────────────────────────────────
// Rendered when day.evidence_context is non-empty.
// Visually distinct from provider cards: magenta accent, no booking CTA,
// source badge, attribution-forward copy.  Never shows price, rating, or
// a provider CTA.  DEV-only: evidence score micro-badge.

const _SOURCE_TYPE_LABELS: Record<string, string> = {
  official_tourism:  "Fuente oficial",
  wikivoyage:        "Guía colaborativa",
  wikidata:          "Base de conocimiento",
  partner_guide:     "Guía aliada",
  licensed_blog:     "Fuente editorial",
  editorial_note:    "Nota editorial",
  local_expert:      "Experto local",
};

function EvidenceContextBlock({ items }: { items: EvidenceContextItem[] }) {
  // Phase 2+3: two-pass dedup (exact + per-source-name), then cap to
  // MAX_EVIDENCE_ITEMS_PER_DAY so the block never shows more than one item,
  // preventing the "heading appears twice" visual from same-source evidence pairs.
  const deduped = dedupeEvidenceItems(items ?? []).slice(0, MAX_EVIDENCE_ITEMS_PER_DAY);
  if (deduped.length === 0) return null;

  return (
    <div
      className="mx-3 sm:mx-4 mb-3 mt-1 rounded-xl overflow-hidden"
      style={{
        background: "#E8194B0F",
        border:     "1px solid #E8194B47",
      }}
    >
      {/* Section header */}
      <div
        className="px-3 sm:px-4 py-2 flex items-center gap-1.5"
        style={{
          borderBottom: "1px solid #E8194B33",
          background:   "#E8194B0A",
        }}
      >
        <Sparkles size={11} aria-hidden style={{ color: "#E8194B" }} />
        <span
          className="text-[10px] font-black tracking-[0.18em] uppercase"
          style={{ color: "#E8194B" }}
        >
          Contexto verificado por Sift Trip
        </span>
      </div>

      {/* Evidence items — use deduped array */}
      {deduped.map((item, idx) => {
        const sourceLabel = (item.source_type && _SOURCE_TYPE_LABELS[item.source_type])
          ?? item.source_type ?? "";
        // Phase 6: only show "Ver fuente" when URL is actually user-facing.
        // Respects backend link_available when present; falls back to URL pattern check.
        const backendSaysAvailable = (item as EvidenceContextItem & { link_available?: boolean }).link_available;
        const hasUrl = backendSaysAvailable === true
          ? true
          : backendSaysAvailable === false
            ? false
            : isUserFacingValidUrl(item.url);
        const hasSummary = typeof item.summary === "string" && item.summary.length > 0;
        const hasAttr    = typeof item.attribution === "string" && item.attribution.length > 0;

        // DEV-only score badge
        const showScore = import.meta.env.DEV
          && typeof item.score === "number"
          && item.score > 0;

        return (
          <div
            key={idx}
            className="px-3 sm:px-4 py-3"
            style={{
              borderBottom: idx < deduped.length - 1
                ? "1px solid #E8194B29"
                : "none",
            }}
          >
            {/* Row 1: Source badge + type label */}
            <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
              {item.source_name && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full"
                  style={{
                    color:      "#E8194B",
                    background: "#E8194B1A",
                    border:     "1px solid #E8194B47",
                  }}
                >
                  <span aria-hidden style={{ fontSize: "8px", opacity: 0.7 }}>◉</span>
                  {item.source_name}
                </span>
              )}
              {sourceLabel && (
                <span
                  className="text-[9px] font-semibold px-1.5 py-0.5 rounded"
                  style={{
                    color:      "#E8194BB2",
                    background: "#E8194B12",
                    border:     "1px solid #E8194B2E",
                  }}
                >
                  {sourceLabel}
                </span>
              )}
              {/* DEV-only evidence score */}
              {showScore && (
                <span
                  className="text-[8px] font-mono px-1.5 py-0.5 rounded"
                  style={{
                    color:      "oklch(0.30 0.10 285 / 0.70)",
                    background: "#E8194B0F",
                    border:     "1px dashed #E8194B38",
                  }}
                  title="Evidence score (DEV only)"
                >
                  Evidence score: {item.score!.toFixed(2)}
                </span>
              )}
            </div>

            {/* Row 2: Summary */}
            {hasSummary && (
              <p
                className="text-[11px] sm:text-xs leading-relaxed mb-1.5"
                style={{ color: "oklch(0.28 0.020 55 / 0.70)" }}
              >
                {item.summary}
              </p>
            )}

            {/* Row 3: Attribution + source link */}
            <div className="flex flex-wrap items-center justify-between gap-2">
              {hasAttr && (
                <p
                  className="text-[9px] italic"
                  style={{ color: "oklch(0.545 0.020 60 / 0.48)" }}
                >
                  {item.attribution}
                </p>
              )}
              {hasUrl && (
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-0.5 text-[10px] font-semibold transition-opacity hover:opacity-75"
                  style={{ color: "#E8194B", marginLeft: hasAttr ? "auto" : undefined }}
                >
                  Ver fuente
                  <ArrowUpRight size={10} aria-hidden />
                </a>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}


// SelectionAction moved to @/components/experiences/marketplace/SelectionAction
// (shared with the marketplace inventory row and selection tray).

// ─── Featured experience card ─────────────────────────────────────────────────
// Hero treatment for the first provider experience in a day (normal/view mode only).
// Never rendered inside editMode or SortableExperienceShell.

function FeaturedExperienceCard({
  exp,
  moodAccent,
  isSelected,
  onAdd,
  onRemove,
}: {
  exp:         ExperienceItem;
  moodAccent:  string;
  isSelected?: boolean;
  onAdd?:      () => void;
  onRemove?:   () => void;
}) {
  const [detailOpen, setDetailOpen] = useState(false);

  const providerLabel = exp.provider_name
    ? exp.provider_name.charAt(0).toUpperCase() + exp.provider_name.slice(1)
    : "Proveedor";

  const hasRating  = typeof exp.rating === "number" && exp.rating > 0;
  const hasReviews = typeof exp.review_count === "number" && exp.review_count > 0;
  const hasDuration = typeof exp.duration_hours === "number" && exp.duration_hours > 0;

  const _backendLinkOk = (exp as ExperienceItem & { link_available?: boolean }).link_available;
  const hasBooking = _backendLinkOk === true
    ? true
    : _backendLinkOk === false
      ? false
      : (typeof exp.booking_url === "string" && isUserFacingValidUrl(exp.booking_url));

  const priceDisplay = exp.price_from != null
    ? (exp.price_from === 0
        ? "Gratis"
        : `desde ${exp.currency && exp.currency !== "USD" ? exp.currency : "$"}${Math.round(exp.price_from)}`)
    : null;

  const catVisual    = CATEGORY_VISUALS[exp.category] ?? null;
  const heroSrc      = exp.image_url || catVisual?.image || null;
  const [imgSrc, setImgSrc] = useState<string | null>(heroSrc);
  const FallbackIcon = CATEGORY_LUCIDE_ICONS[exp.category] ?? MapPin;

  const handleImgError = () => {
    if (imgSrc === exp.image_url && catVisual?.image) {
      setImgSrc(catVisual.image);
    } else {
      setImgSrc(null);
    }
  };

  const explanationText =
    (exp.supplier_explanation && exp.supplier_explanation.length > 0)
      ? exp.supplier_explanation
      : (exp.editorial_note && exp.editorial_note.length > 0)
        ? exp.editorial_note
        : null;

  return (
    <div
      className="mx-3 sm:mx-4 mt-3 mb-4 rounded-xl overflow-hidden transition-opacity duration-200"
      style={{
        border:     isSelected === true ? "1px solid oklch(0.46 0.12 145 / 0.45)" : `1px solid ${moodAccent}28`,
        boxShadow:  isSelected === true
          ? "0 0 0 1.5px oklch(0.46 0.12 145 / 0.22), 0 2px 12px oklch(0.10 0.010 55 / 0.07)"
          : "0 2px 12px oklch(0.10 0.010 55 / 0.07)",
        opacity:    isSelected === false ? 0.82 : 1,
      }}
    >
      {/* Hero image / gradient strip */}
      {imgSrc ? (
        <img
          src={imgSrc}
          alt=""
          aria-hidden
          className="w-full object-cover block"
          style={{ height: "156px" }}
          loading="lazy"
          onError={handleImgError}
        />
      ) : (
        <div
          aria-hidden
          className="w-full flex items-center justify-center"
          style={{
            height:     "96px",
            background: `linear-gradient(135deg, ${moodAccent}16 0%, ${moodAccent}08 100%)`,
          }}
        >
          <FallbackIcon size={40} aria-hidden style={{ color: moodAccent, opacity: 0.48 }} />
        </div>
      )}

      {/* Card body */}
      <div className="px-4 pt-3 pb-4" style={{ background: "oklch(0.993 0.004 72)" }}>
        {/* Title — primary anchor */}
        <h4
          className="font-display font-bold text-[15px] sm:text-base leading-snug mb-1.5"
          style={{ color: "oklch(0.120 0.040 258)" }}
        >
          {exp.title}
        </h4>

        {/* Compact meta: rating · duration · price */}
        {(hasRating || hasDuration || priceDisplay) && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mb-2.5">
            {hasRating && (
              <span
                className="inline-flex items-center gap-1 text-[11px] font-semibold"
                style={{ color: "#E8194B" }}
              >
                <Star size={11} aria-hidden style={{ fill: "currentColor" }} />
                {exp.rating!.toFixed(1)}
                {hasReviews && (
                  <span className="font-normal text-[10px]" style={{ color: "oklch(0.545 0.020 60 / 0.50)" }}>
                    ({exp.review_count!.toLocaleString()})
                  </span>
                )}
              </span>
            )}
            {hasDuration && (
              <span
                className="inline-flex items-center gap-1 text-[11px]"
                style={{ color: "oklch(0.50 0.020 60 / 0.55)" }}
              >
                <Clock size={10} aria-hidden />
                {exp.duration_hours}h
              </span>
            )}
            {priceDisplay && (
              <span className="text-[11px] font-semibold" style={{ color: "oklch(0.120 0.040 258)" }}>
                {priceDisplay}
              </span>
            )}
          </div>
        )}

        {/* Trust signals — confirm quality, below title */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-2.5">
          <span
            className="inline-flex items-center gap-1 text-[10px] font-semibold"
            style={{ color: "oklch(0.46 0.12 145)" }}
            title={`Actividad de ${providerLabel}`}
          >
            <CheckCircle2 size={9} aria-hidden />
            Verificado con proveedor
          </span>
          {exp.availability_confirmed === true && (
            <span
              className="inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded-md"
              style={{
                color:      "oklch(0.580 0.220 256)",
                background: "oklch(0.580 0.220 256 / 0.08)",
                border:     "1px solid oklch(0.580 0.220 256 / 0.22)",
              }}
            >
              <CheckCircle2 size={9} aria-hidden />
              Reservable
            </span>
          )}
          {(exp.availability_status === "skipped_no_date" || exp.catalog_only === true) && exp.price_from != null && (
            <span
              className="inline-flex items-center gap-1 text-[9px] font-medium"
              style={{ color: "oklch(0.545 0.020 60 / 0.65)" }}
            >
              <Timer size={9} aria-hidden />
              Precio estimado
            </span>
          )}
        </div>

        {/* Description (2 lines clamped) */}
        {explanationText && (
          <p
            className="text-[11px] sm:text-xs leading-relaxed line-clamp-2 mb-3"
            style={{ color: "oklch(0.38 0.010 55 / 0.55)", fontStyle: "italic" }}
          >
            {explanationText}
          </p>
        )}

        {/* CTA row */}
        {(hasBooking || (exp.source_class === "LIVE_PROVIDER" && exp.provider_id)) && (
          <div className="flex items-center gap-2">
            {hasBooking && (
              <a
                href={exp.booking_url!}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("booking_clicked", {
                  provider_name: exp.provider_name ?? undefined,
                  city:          exp.neighborhood ?? "",
                  price_from:    exp.price_from ?? undefined,
                })}
                className="flex-1 inline-flex items-center justify-center gap-1.5 text-[12px] font-bold px-4 py-2 rounded-lg transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
                style={{
                  color:      "oklch(1.000 0 0)",
                  background: "oklch(0.120 0.040 258)",
                }}
              >
                Reservar
                <ArrowUpRight size={12} aria-hidden />
              </a>
            )}
            {exp.source_class === "LIVE_PROVIDER" && exp.provider_id && (
              <button
                type="button"
                onClick={() => setDetailOpen(true)}
                className="inline-flex items-center gap-1 text-[11px] font-semibold px-3 py-2 rounded-lg transition-all duration-200 hover:opacity-85 active:scale-[0.97]"
                style={{
                  color:      "oklch(0.38 0.10 262)",
                  background: "oklch(0.38 0.10 262 / 0.07)",
                  border:     "1px solid oklch(0.38 0.10 262 / 0.20)",
                }}
              >
                Ver detalle
                <Info size={10} aria-hidden />
              </button>
            )}
          </div>
        )}

        {/* Marketplace selection action */}
        {isSelected !== undefined && (
          <div className="flex justify-end mt-2.5">
            <SelectionAction isSelected={isSelected} onAdd={onAdd} onRemove={onRemove} itemTitle={exp.title} />
          </div>
        )}
      </div>

      {/* Product detail drawer (lazy) */}
      {exp.source_class === "LIVE_PROVIDER" && exp.provider_id && (
        <LazyPanelBoundary onClose={() => setDetailOpen(false)}>
          <Suspense fallback={null}>
            <ProviderProductDetailDrawer
              productCode={exp.provider_id}
              fallbackTitle={exp.title}
              open={detailOpen}
              onOpenChange={setDetailOpen}
              baseInfo={{
                priceFrom:     exp.price_from,
                currency:      exp.currency,
                rating:        exp.rating,
                reviewCount:   exp.review_count,
                durationHours: exp.duration_hours,
                bookingUrl:    exp.booking_url,
              }}
            />
          </Suspense>
        </LazyPanelBoundary>
      )}
    </div>
  );
}


// ─── Provider experience row ──────────────────────────────────────────────────
// Rendered when exp.source_type === "provider".  Visually distinct from
// curated/knowledge rows: magenta-indigo palette, provider badge, rating,
// booking CTA.  No raw debug language in production.

function ProviderExperienceRow({
  exp,
  isLast,
  isExpanded,
  moodAccent: _moodAccent,   // kept for future mood-tinting, unused for now
  isSelected,
  onAdd,
  onRemove,
}: {
  exp:         ExperienceItem;
  isLast:      boolean;
  isExpanded:  boolean;
  moodAccent:  string;
  isSelected?: boolean;
  onAdd?:      () => void;
  onRemove?:   () => void;
}) {
  const [detailOpen, setDetailOpen] = useState(false);

  const providerLabel = exp.provider_name
    ? exp.provider_name.charAt(0).toUpperCase() + exp.provider_name.slice(1)
    : "Proveedor";
  const hasRating     = typeof exp.rating === "number" && exp.rating > 0;
  const hasReviews    = typeof exp.review_count === "number" && exp.review_count > 0;
  // Phase 5: CTA only when backend confirms link is available AND URL passes client check.
  // Prefers explicit backend signal (link_available); falls back to URL pattern validation.
  const _backendLinkOk = (exp as ExperienceItem & { link_available?: boolean }).link_available;
  const hasBooking = _backendLinkOk === true
    ? true
    : _backendLinkOk === false
      ? false
      : (typeof exp.booking_url === "string" && isUserFacingValidUrl(exp.booking_url));
  const hasDuration   = typeof exp.duration_hours === "number" && exp.duration_hours > 0;
  const hasTags       = Array.isArray(exp.tags) && exp.tags.length > 0;

  // Explanation copy: prefer supplier_explanation → editorial_note → description
  const explanationText =
    (exp.supplier_explanation && exp.supplier_explanation.length > 0)
      ? exp.supplier_explanation
      : (exp.editorial_note && exp.editorial_note.length > 0)
        ? exp.editorial_note
        : null;

  // Price display
  const priceDisplay = exp.price_from != null
    ? (exp.price_from === 0
        ? "Gratis"
        : `desde ${exp.currency && exp.currency !== "USD" ? exp.currency : "$"}${Math.round(exp.price_from)}`)
    : null;

  // DEV-only SI score (never production)
  const showSiBadge = import.meta.env.DEV
    && typeof exp.supplier_intelligence_score === "number"
    && exp.supplier_intelligence_score > 0;

  return (
    <div
      className={isExpanded ? "px-4 sm:px-5 py-4 transition-opacity duration-200" : "px-4 sm:px-5 py-3.5 transition-opacity duration-200"}
      style={{
        borderBottom: isLast ? "none" : "1px solid oklch(0.38 0.10 262 / 0.10)",
        borderLeft:   isSelected === true ? "3px solid oklch(0.46 0.12 145 / 0.55)" : "3px solid oklch(0.38 0.10 262 / 0.45)",
        background:   "oklch(0.38 0.10 262 / 0.025)",
        opacity:      isSelected === false ? 0.82 : 1,
      }}
    >
      {/* ── Row 1: Provider badge + availability badges ───────────────────── */}
      <div className="flex flex-wrap items-center gap-1.5 mb-2">

        {/* Provider source badge */}
        <span
          className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full"
          style={{
            color:      "oklch(0.46 0.12 145)",
            background: "oklch(0.46 0.12 145 / 0.08)",
            border:     "1px solid oklch(0.46 0.12 145 / 0.22)",
          }}
          title={`Actividad de ${providerLabel}`}
        >
          <CheckCircle2 size={9} aria-hidden />
          Verificado con proveedor
        </span>

        {/* Sprint 5F — Availability / source badges */}
        {exp.availability_confirmed === true && (
          <span
            className="inline-flex items-center gap-1 text-[9px] font-bold px-2 py-0.5 rounded-full"
            style={{
              color:      "oklch(0.580 0.220 256)",
              background: "oklch(0.580 0.220 256 / 0.10)",
              border:     "1px solid oklch(0.580 0.220 256 / 0.28)",
            }}
            title="Disponibilidad verificada con el proveedor para tu fecha"
          >
            <CheckCircle2 size={9} aria-hidden />
            Reservable
          </span>
        )}
        {(exp.availability_status === "skipped_no_date" || exp.catalog_only === true) && exp.price_from != null && (
          <span
            className="inline-flex items-center gap-1 text-[9px] font-semibold px-2 py-0.5 rounded-full"
            style={{
              color:      "oklch(0.545 0.020 60)",
              background: "oklch(0.545 0.020 60 / 0.10)",
              border:     "1px solid oklch(0.545 0.020 60 / 0.25)",
            }}
            title="Precio estimado del catálogo. La disponibilidad se confirma al reservar con el proveedor."
          >
            <Timer size={9} aria-hidden />
            Precio estimado
          </span>
        )}
        {/* editorial badge removed — not user-facing useful */}

        {/* DEV-only SI score — never production */}
        {showSiBadge && (
          <span
            className="inline-flex items-center text-[8px] font-mono font-bold px-1.5 py-0.5 rounded"
            style={{
              color:      "oklch(0.30 0.10 285 / 0.80)",
              background: "oklch(0.30 0.10 285 / 0.07)",
              border:     "1px dashed oklch(0.30 0.10 285 / 0.25)",
            }}
            title="Supplier Intelligence score (DEV only)"
          >
            SI: {exp.supplier_intelligence_score!.toFixed(2)}
          </span>
        )}
      </div>

      {/* ── Row 2: Title + optional thumbnail ─────────────────────────────── */}
      <div className="flex items-start gap-3 mb-2">
        <h4
          className="flex-1 font-display font-semibold text-[13px] sm:text-sm leading-snug"
          style={{ color: "var(--color-heading-navy)" }}
        >
          {exp.title}
        </h4>
        {exp.image_url && (
          <img
            src={exp.image_url}
            alt=""
            aria-hidden
            className="shrink-0 rounded-xl object-cover"
            style={{ width: "96px", height: "72px", border: "1px solid oklch(0.880 0.024 72 / 0.28)" }}
            loading="lazy"
            onError={e => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
          />
        )}
      </div>

      {/* ── Row 3: Trust metadata ──────────────────────────────────────────── */}
      {(hasRating || hasDuration || priceDisplay) && (
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 mb-2">
          {/* Rating */}
          {hasRating && (
            <span
              className="inline-flex items-center gap-1 text-[11px] font-semibold"
              style={{ color: "#E8194B" }}
            >
              <Star size={11} aria-hidden style={{ fill: "currentColor", color: "#E8194B" }} />
              {exp.rating!.toFixed(1)}
              {hasReviews && (
                <span className="font-normal text-[10px]" style={{ color: "oklch(0.545 0.020 60 / 0.50)" }}>
                  ({exp.review_count!.toLocaleString()})
                </span>
              )}
            </span>
          )}

          {/* Duration */}
          {hasDuration && (
            <span
              className="inline-flex items-center gap-1 text-[11px]"
              style={{ color: "oklch(0.50 0.020 60 / 0.55)" }}
            >
              <Clock size={10} aria-hidden />
              {exp.duration_hours}h
            </span>
          )}

          {/* Price */}
          {priceDisplay && (
            <span
              className="inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-md"
              style={{
                color:      "oklch(0.120 0.040 258)",
                background: "#E8194B14",
                border:     "1px solid #E8194B33",
              }}
            >
              {priceDisplay}
            </span>
          )}
        </div>
      )}

      {/* ── Row 4: Supplier explanation / editorial note ──────────────────── */}
      {explanationText && (
        <p
          className={`text-[11px] sm:text-xs leading-relaxed mb-2 ${isExpanded ? "" : "line-clamp-2"}`}
          style={{ color: "oklch(0.38 0.010 55 / 0.58)", fontStyle: "italic" }}
        >
          {explanationText}
        </p>
      )}

      {/* ── Row 5: Tag chips ──────────────────────────────────────────────── */}
      {hasTags && isExpanded && (
        <div className="flex flex-wrap gap-1 mb-2.5">
          {exp.tags!.slice(0, 6).map(tag => (
            <span
              key={tag}
              className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full"
              style={{
                color:      "oklch(0.38 0.10 262 / 0.70)",
                background: "oklch(0.38 0.10 262 / 0.07)",
                border:     "1px solid oklch(0.38 0.10 262 / 0.18)",
              }}
            >
              {formatTagLabel(tag)}
            </span>
          ))}
        </div>
      )}

      {/* ── Row 6: CTA row ───────────────────────────────────────────────── */}
      {(hasBooking || (exp.source_class === "LIVE_PROVIDER" && exp.provider_id)) && (
        <div className="flex flex-wrap justify-between items-center gap-2 mt-1">
          <p
            className="text-[9px]"
            style={{ color: "oklch(0.545 0.020 60 / 0.40)" }}
          >
            Precio y disponibilidad sujetos al proveedor
          </p>
          <div className="flex items-center gap-2 shrink-0 ml-auto">
            {/* "Ver detalle" — only for LIVE_PROVIDER with a product code */}
            {exp.source_class === "LIVE_PROVIDER" && exp.provider_id && (
              <button
                type="button"
                onClick={() => setDetailOpen(true)}
                className="inline-flex items-center gap-1 text-[11px] font-semibold px-3 py-1.5 rounded-full transition-all duration-200 hover:opacity-85 active:scale-[0.97]"
                style={{
                  color:      "oklch(0.38 0.10 262)",
                  background: "oklch(0.38 0.10 262 / 0.08)",
                  border:     "1px solid oklch(0.38 0.10 262 / 0.25)",
                }}
              >
                Ver detalle
                <Info size={10} aria-hidden />
              </button>
            )}
            {/* "Continuar con el proveedor" — only when a valid booking URL exists */}
            {hasBooking && (
              <a
                href={exp.booking_url!}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("booking_clicked", {
                  provider_name: exp.provider_name ?? undefined,
                  city:          exp.neighborhood ?? "",
                  price_from:    exp.price_from ?? undefined,
                })}
                className="inline-flex items-center gap-1 text-[11px] font-bold px-3 py-1.5 rounded-full transition-all duration-200 hover:opacity-85 active:scale-[0.97]"
                style={{
                  color:      "oklch(1.000 0 0)",
                  background: "oklch(0.38 0.10 262)",
                  boxShadow:  "0 2px 8px oklch(0.38 0.10 262 / 0.28)",
                }}
              >
                Continuar con el proveedor
                <ArrowUpRight size={12} aria-hidden />
              </a>
            )}
          </div>
        </div>
      )}

      {/* ── Row 7: Marketplace selection action ────────────────────────────── */}
      {isSelected !== undefined && (
        <div className="flex justify-end mt-2">
          <SelectionAction isSelected={isSelected} onAdd={onAdd} onRemove={onRemove} itemTitle={exp.title} />
        </div>
      )}

      {/* ── Product detail drawer (lazy — Sprint 12C) ─────────────────────── */}
      {exp.source_class === "LIVE_PROVIDER" && exp.provider_id && (
        <LazyPanelBoundary onClose={() => setDetailOpen(false)}>
          <Suspense fallback={null}>
            <ProviderProductDetailDrawer
              productCode={exp.provider_id}
              fallbackTitle={exp.title}
              open={detailOpen}
              onOpenChange={setDetailOpen}
              baseInfo={{
                priceFrom:     exp.price_from,
                currency:      exp.currency,
                rating:        exp.rating,
                reviewCount:   exp.review_count,
                durationHours: exp.duration_hours,
                bookingUrl:    exp.booking_url,
              }}
            />
          </Suspense>
        </LazyPanelBoundary>
      )}
    </div>
  );
}


// ─── Experience row ───────────────────────────────────────────────────────────

function ExperienceRow({
  experience: exp,
  isLast,
  isExpanded,
  moodAccent,
  isSelected,
  onAdd,
  onRemove,
}: {
  experience:  ExperienceItem;
  isLast:      boolean;
  isExpanded:  boolean;
  moodAccent:  string;
  isSelected?: boolean;
  onAdd?:      () => void;
  onRemove?:   () => void;
}) {
  // Per-item "Ver más" toggle — descriptions start collapsed
  const [detailExpanded, setDetailExpanded] = useState(false);
  const COLLAPSE_THRESHOLD = 80; // chars — show "Ver más" above this length

  const timeMeta = TIME_META[exp.time_of_day] ?? TIME_META.any;
  const catMeta  = CATEGORY_META[exp.category] ?? { icon: "", label: formatTagLabel(exp.category), color: "oklch(0.50 0.06 200)" };
  const hasPrice = exp.price_from != null;

  // ── Knowledge item detection ───────────────────────────────────────────
  const isKnowledge = exp.source_type === "knowledge";
  const kv = isKnowledge ? resolveKnowledgeVisual(exp.knowledge_category) : null;
  const KnowledgeIc = isKnowledge
    ? (KNOWLEDGE_LUCIDE_ICONS[exp.knowledge_category ?? ""] ?? Compass)
    : Compass;

  if (import.meta.env.DEV && isKnowledge) {
    console.debug(`[KnowledgeVisual] ${exp.knowledge_category ?? "unknown"} — "${exp.title}"`);
  }

  // ── Provider experience detection ──────────────────────────────────────
  const isProvider = exp.source_type === "provider";

  if (isProvider) {
    return (
      <ProviderExperienceRow
        exp={exp}
        isLast={isLast}
        isExpanded={isExpanded}
        moodAccent={moodAccent}
        isSelected={isSelected}
        onAdd={onAdd}
        onRemove={onRemove}
      />
    );
  }

  return (
    <div
      className={isExpanded ? "px-4 sm:px-5 py-5 transition-opacity duration-200" : "px-4 sm:px-5 py-4 transition-opacity duration-200"}
      style={{
        borderBottom: isLast ? "none" : `1px solid ${moodAccent}14`,
        opacity: isSelected === false ? 0.82 : 1,
        // Knowledge accent strip: 3 px left border + very subtle tint
        ...(isKnowledge && kv ? {
          borderLeft: `3px solid ${kv.border}`,
          background: kv.bg,
        } : {}),
      }}
    >
      <div className="flex gap-2.5 sm:gap-3">

        {/* ── Left icon ──────────────────────────────────────────────────── */}
        <div className="shrink-0 mt-0.5 relative flex flex-col items-center">
          {isKnowledge && kv ? (
            // Knowledge items: show category glyph, not time-of-day
            <div
              className="w-8 h-8 rounded-xl flex items-center justify-center text-sm select-none"
              style={{
                background: `${kv.accent}12`,
                border:     `1px solid ${kv.border}`,
                color:      kv.accent,
              }}
              aria-hidden
            >
              <KnowledgeIc size={15} aria-hidden />
            </div>
          ) : (() => {
            const TimeIc = TIME_LUCIDE_ICONS[exp.time_of_day] ?? MapPin;
            return (
              <div
                className="w-8 h-8 rounded-xl flex items-center justify-center"
                style={{ background: `${moodAccent}0e`, border: `1px solid ${moodAccent}22`, color: moodAccent }}
              >
                <TimeIc size={15} aria-hidden />
              </div>
            );
          })()}
          {!isLast && (
            <div
              aria-hidden
              className="mt-2 w-px flex-1 min-h-[38px]"
              style={{ background: `linear-gradient(to bottom, ${moodAccent}30, ${moodAccent}08)` }}
            />
          )}
        </div>

        {/* ── Content ────────────────────────────────────────────────────── */}
        <div className="flex-1 min-w-0">

          {isKnowledge && kv ? (
            // ── Knowledge header: category badge + Sift Trip Local chip ──────
            <div className="flex flex-wrap items-center gap-1.5 mb-2">
              {/* Category badge */}
              <span
                className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full"
                style={{
                  color:      kv.accent,
                  background: `${kv.accent}12`,
                  border:     `1px solid ${kv.border}`,
                }}
                aria-label={`Categoría: ${kv.label}`}
              >
                <KnowledgeIc size={10} aria-hidden />
                {kv.label}
              </span>

              {/* Sift Trip Local source chip — shown for all knowledge items */}
              <span
                className="inline-flex items-center gap-0.5 text-[10px] font-bold px-2 py-0.5 rounded-full"
                style={{
                  color:      "#E8194B",
                  background: "#E8194B14",
                  border:     "1px solid #E8194B4C",
                }}
              >
                <Sparkles size={10} aria-hidden />
                Sift Trip Local
              </span>
            </div>
          ) : (
            // ── Regular header: neighborhood (left) + image (right) ────────
            <div className="flex items-start justify-between gap-2 mb-1">
              <p
                className="text-[9px] sm:text-[10px] font-bold tracking-[0.20em] uppercase leading-none"
                style={{ color: moodAccent + "b0", paddingTop: "2px" }}
              >
                {exp.neighborhood || timeMeta.label}
              </p>
              <ImagePlaceholder category={exp.category} imageUrl={exp.image_url} />
            </div>
          )}

          {/* Title */}
          <h4
            className="font-display font-semibold text-sm leading-snug pr-2"
            style={{ color: "var(--color-heading-navy)" }}
          >
            {exp.title}
          </h4>

          {/* Neighborhood line — secondary label for knowledge items */}
          {isKnowledge && exp.neighborhood && (
            <p
              className="text-[9px] font-semibold tracking-[0.14em] uppercase mt-0.5"
              style={{ color: kv ? `${kv.accent}90` : moodAccent + "80" }}
            >
              {exp.neighborhood}
            </p>
          )}

          {/* Meta row — dot-separated text line */}
          {(() => {
            const hasSpecificTime = exp.time_of_day && exp.time_of_day !== "any";
            const parts = [
              !isKnowledge ? catMeta.label : null,
              !isKnowledge ? "Referencia local" : null,
              hasSpecificTime ? timeMeta.label : null,
              exp.duration_hours > 0 ? `${exp.duration_hours}h` : null,
              hasPrice ? (exp.price_from === 0 ? "Gratis" : `~$${exp.price_from}`) : null,
            ].filter((s): s is string => s !== null && s !== "");
            return parts.length === 0 ? null : (
              <p className="text-[10px] mt-1.5 leading-none" style={{ color: "oklch(0.545 0.020 60 / 0.50)" }}>
                {parts.join(" · ")}
              </p>
            );
          })()}

          {/* Description + editorial note + progressive disclosure */}
          {(() => {
            const hasDesc      = Boolean(exp.description);
            const hasNote      = Boolean(exp.editorial_note) && !isKnowledge;
            const descOverflow = hasDesc && (exp.description?.length ?? 0) > COLLAPSE_THRESHOLD;
            const noteOverflow = hasNote && (exp.editorial_note?.length ?? 0) > 60;
            const needsToggle  = descOverflow || noteOverflow;
            return (
              <>
                {hasDesc && (
                  <div className="mt-2">
                    <p
                      className={`text-[13px] leading-relaxed ${detailExpanded ? "" : "line-clamp-2"}`}
                      style={{ color: "oklch(0.18 0.010 55 / 0.55)" }}
                    >
                      {exp.description}
                    </p>
                  </div>
                )}
                {hasNote && !detailExpanded && (
                  <p className="mt-1.5 text-[10px] leading-snug italic" style={{ color: moodAccent + "80" }}>
                    {(exp.editorial_note?.length ?? 0) > 80
                      ? exp.editorial_note!.slice(0, 78) + "…"
                      : exp.editorial_note}
                  </p>
                )}
                {hasNote && detailExpanded && (
                  <div
                    className="mt-2.5 pl-3 py-0.5"
                    style={{ borderLeft: `2px solid ${moodAccent}55` }}
                  >
                    <p className="text-[11px] sm:text-xs leading-relaxed italic" style={{ color: moodAccent + "cc" }}>
                      {exp.editorial_note}
                    </p>
                  </div>
                )}
                {(needsToggle || isSelected !== undefined) && (
                  <div className={`flex items-center gap-2 mt-2 flex-wrap ${needsToggle ? "justify-between" : "justify-end"}`}>
                    {needsToggle && (
                      <button
                        type="button"
                        onClick={() => setDetailExpanded(v => !v)}
                        aria-label={detailExpanded ? `Ver menos detalle de "${exp.title}"` : `Ver detalle de "${exp.title}"`}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold transition-opacity hover:opacity-70"
                        style={{ color: moodAccent + "bb", background: "none", border: "none", cursor: "pointer", padding: 0 }}
                      >
                        {detailExpanded ? "Ver menos" : "Ver detalle"}
                        {detailExpanded ? <ChevronUp size={11} aria-hidden /> : <Info size={11} aria-hidden />}
                      </button>
                    )}
                    {isSelected !== undefined && (
                      <SelectionAction isSelected={isSelected} onAdd={onAdd} onRemove={onRemove} itemTitle={exp.title} />
                    )}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

// ─── Supply Options Panel + Card ─────────────────────────────────────────────
//
// Augmentation layer rendered below curated experience rows when the backend
// attaches provider supply to a day card.
//
// Layout hierarchy:
//   SupplyOptionsPanel     — section wrapper, header, subtitle, trust footer
//     SupplyOptionCard ×N  — one card per supply option
//
// Visual identity rules:
//   - Magenta/ivory palette — visually distinct from curated mood-tinted cards
//   - Provider badge + "proveedor externo" on every card — clear external origin
//   - Trust metadata row (rating, reviews, duration, price) — social proof
//   - Role badge (Principal / Complementa / Opcional) — relevance signal
//   - CTA right-aligned on desktop, full-width pill on mobile
//   - DEV-only debug line (trace_id, mode) — never in production
//
// Field fallbacks: missing rating/price/duration/url → fields hidden, never "null" shown.

// ── Role badge metadata ────────────────────────────────────────────────────────

const _SUPPLY_ROLE_META: Record<string, { label: string; color: string; bg: string; border: string }> = {
  CORE: {
    label:  "Principal",
    color:  "oklch(0.44 0.14 262)",
    bg:     "oklch(0.44 0.14 262 / 0.08)",
    border: "oklch(0.44 0.14 262 / 0.22)",
  },
  SUPPORTING: {
    label:  "Complementa",
    color:  "oklch(0.46 0.12 162)",
    bg:     "oklch(0.46 0.12 162 / 0.08)",
    border: "oklch(0.46 0.12 162 / 0.22)",
  },
  AMBIENT: {
    label:  "Opcional",
    color:  "oklch(0.545 0.020 60)",
    bg:     "oklch(0.545 0.020 60 / 0.08)",
    border: "oklch(0.545 0.020 60 / 0.22)",
  },
};

// ── Duration formatter ─────────────────────────────────────────────────────────

function _fmtDuration(minutes: number): string {
  if (minutes <= 0) return "";
  if (minutes < 60)  return `${minutes}min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}

// ── SupplyOptionCard ───────────────────────────────────────────────────────────

function SupplyOptionCard({ opt }: { opt: ExperienceSupplyOption }) {
  const [detailOpen, setDetailOpen] = useState(false);

  const rm = _SUPPLY_ROLE_META[opt.contextual_role] ?? _SUPPLY_ROLE_META.AMBIENT;

  // Phase 3 — DEV-only supplier intelligence score badge
  const showSiBadge = import.meta.env.DEV
    && typeof opt.supplier_intelligence_score === "number"
    && isFinite(opt.supplier_intelligence_score)
    && opt.supplier_intelligence_score > 0;

  // Derived display values — never show undefined / null / NaN
  const providerLabel  = opt.provider
    ? opt.provider.charAt(0).toUpperCase() + opt.provider.slice(1)
    : "Proveedor";
  const hasRating      = typeof opt.rating === "number" && opt.rating > 0 && isFinite(opt.rating);
  const hasReviews     = typeof opt.review_count === "number" && opt.review_count > 0;
  const hasPrice       = typeof opt.price === "number" && opt.price > 0 && isFinite(opt.price);
  const hasDuration    = typeof opt.duration_minutes === "number" && opt.duration_minutes > 0;
  const hasUrl         = typeof opt.url === "string" && opt.url.length > 0;
  const hasExplanation = typeof opt.explanation === "string" && opt.explanation.length > 0;
  const affordances    = Array.isArray(opt.affordances) ? opt.affordances.slice(0, 5) : [];
  const currencySymbol = (opt.currency ?? "USD") === "USD" ? "$" : (opt.currency ?? "USD");

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{
        background: "oklch(1.000 0 0 / 0.92)",
        border:     "1px solid oklch(0.882 0.028 74 / 0.45)",
        boxShadow:  "0 1px 8px #E8194B12, 0 0 0 0.5px oklch(0.882 0.028 74 / 0.18)",
      }}
    >
      <div className="p-3 sm:p-4">

        {/* ── Row 1: Provider badge + Role badge ──────────────────────────── */}
        <div className="flex items-center justify-between gap-2 mb-2.5">
          {/* Provider badge */}
          <span
            className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-wide px-2.5 py-1 rounded-full"
            style={{
              color:      "oklch(0.38 0.10 262)",
              background: "oklch(0.38 0.10 262 / 0.07)",
              border:     "1px solid oklch(0.38 0.10 262 / 0.18)",
            }}
          >
            {/* External link icon glyph */}
            <span aria-hidden style={{ fontSize: "9px", opacity: 0.7 }}>⬡</span>
            {providerLabel}
            <span
              style={{
                color:      "oklch(0.38 0.10 262 / 0.55)",
                fontWeight: 500,
                fontSize:   "9px",
              }}
            >
              · proveedor externo
            </span>
          </span>

          {/* Role badge */}
          <span
            className="shrink-0 text-[9px] font-bold tracking-wide px-2 py-0.5 rounded-full whitespace-nowrap"
            style={{ color: rm.color, background: rm.bg, border: `1px solid ${rm.border}` }}
          >
            {rm.label}
          </span>
        </div>

        {/* ── DEV-only: Supplier Intelligence score badge ─────────────────── */}
        {showSiBadge && (
          <div className="mb-1.5">
            <span
              className="inline-flex items-center gap-1 text-[8px] font-mono font-bold px-1.5 py-0.5 rounded"
              style={{
                color:      "oklch(0.30 0.10 285 / 0.80)",
                background: "oklch(0.30 0.10 285 / 0.07)",
                border:     "1px dashed oklch(0.30 0.10 285 / 0.25)",
              }}
              title="Supplier Intelligence score (DEV only)"
            >
              SI: {opt.supplier_intelligence_score!.toFixed(2)}
            </span>
          </div>
        )}

        {/* ── Row 2: Title ────────────────────────────────────────────────── */}
        <p
          className="font-display font-semibold text-[14px] sm:text-[15px] leading-snug mb-2"
          style={{ color: "var(--color-heading-navy)" }}
        >
          {opt.title}
        </p>

        {/* ── Row 3: Trust metadata ────────────────────────────────────────── */}
        {(hasRating || hasDuration || hasPrice) && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-2">
            {/* Rating + reviews */}
            {hasRating && (
              <span
                className="inline-flex items-center gap-1 text-[11px] font-semibold"
                style={{ color: "#E8194B" }}
              >
                <span aria-hidden style={{ fontSize: "10px" }}>★</span>
                {opt.rating!.toFixed(1)}
                {hasReviews && (
                  <span
                    className="font-normal text-[10px]"
                    style={{ color: "oklch(0.545 0.020 60 / 0.50)" }}
                  >
                    ({opt.review_count!.toLocaleString()})
                  </span>
                )}
              </span>
            )}

            {/* Duration */}
            {hasDuration && (
              <span
                className="inline-flex items-center gap-1 text-[11px]"
                style={{ color: "oklch(0.50 0.020 60 / 0.58)" }}
              >
                <span aria-hidden style={{ fontSize: "10px", opacity: 0.65 }}>◷</span>
                {_fmtDuration(opt.duration_minutes!)}
              </span>
            )}

            {/* Price */}
            {hasPrice && (
              <span
                className="inline-flex items-center gap-0.5 text-[11px] font-semibold px-2 py-0.5 rounded-md"
                style={{
                  color:      "var(--color-forest)",
                  background: "oklch(0.298 0.058 167 / 0.08)",
                  border:     "1px solid oklch(0.298 0.058 167 / 0.16)",
                }}
              >
                desde {currencySymbol}{Math.round(opt.price!)}
              </span>
            )}
          </div>
        )}

        {/* ── Row 4: Semantic explanation ──────────────────────────────────── */}
        {hasExplanation && (
          <p
            className="text-[11px] sm:text-xs leading-relaxed mb-2"
            style={{ color: "oklch(0.38 0.010 55 / 0.58)", fontStyle: "italic" }}
          >
            {opt.explanation}
          </p>
        )}

        {/* ── Row 5: Affordance chips ───────────────────────────────────────── */}
        {affordances.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {affordances.map(aff => (
              <span
                key={aff}
                className="text-[9px] font-semibold px-2 py-0.5 rounded-full"
                style={{
                  color:      "#E8194BCC",
                  background: "#E8194B14",
                  border:     "1px solid #E8194B33",
                }}
              >
                {aff.replace(/_/g, " ")}
              </span>
            ))}
          </div>
        )}

        {/* ── Row 6: CTA ───────────────────────────────────────────────────── */}
        {(hasUrl || (opt.source_class === "LIVE_PROVIDER" && opt.provider_id)) && (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {/* "Ver detalle" — only for LIVE_PROVIDER with a product code */}
            {opt.source_class === "LIVE_PROVIDER" && opt.provider_id && (
              <button
                type="button"
                onClick={() => setDetailOpen(true)}
                className="
                  inline-flex items-center justify-center gap-1
                  w-full sm:w-auto
                  px-4 py-2 sm:px-3 sm:py-1.5
                  rounded-lg sm:rounded-full
                  text-[12px] sm:text-[11px] font-semibold
                  transition-all duration-200
                  hover:opacity-85 active:scale-[0.97]
                "
                style={{
                  color:      "oklch(0.38 0.10 262)",
                  background: "oklch(0.38 0.10 262 / 0.08)",
                  border:     "1px solid oklch(0.38 0.10 262 / 0.25)",
                }}
              >
                Ver detalle
                <Info size={10} aria-hidden />
              </button>
            )}
            {/* "Continuar con el proveedor" — only when a valid URL exists */}
            {hasUrl && (
              <a
                href={opt.url}
                target="_blank"
                rel="noopener noreferrer"
                className="
                  inline-flex items-center justify-center gap-1.5
                  w-full sm:w-auto
                  px-4 py-2 sm:px-3.5 sm:py-1.5
                  rounded-lg sm:rounded-full
                  text-[12px] sm:text-[11px] font-bold
                  transition-all duration-200
                  hover:opacity-85 active:scale-[0.97]
                "
                style={{
                  color:      "oklch(1.000 0 0)",
                  background: "oklch(0.38 0.10 262)",
                  boxShadow:  "0 2px 8px oklch(0.38 0.10 262 / 0.28)",
                }}
              >
                Continuar con el proveedor
                <ArrowUpRight size={12} aria-hidden />
              </a>
            )}
          </div>
        )}
      </div>

      {/* ── Product detail drawer (lazy — Sprint 12C) ─────────────────────── */}
      {opt.source_class === "LIVE_PROVIDER" && opt.provider_id && (
        <LazyPanelBoundary onClose={() => setDetailOpen(false)}>
          <Suspense fallback={null}>
            <ProviderProductDetailDrawer
              productCode={opt.provider_id}
              fallbackTitle={opt.title}
              open={detailOpen}
              onOpenChange={setDetailOpen}
              baseInfo={{
                priceFrom:     opt.price,
                currency:      opt.currency,
                rating:        opt.rating,
                reviewCount:   opt.review_count,
                durationHours: opt.duration_minutes != null ? opt.duration_minutes / 60 : null,
                bookingUrl:    opt.url,
              }}
            />
          </Suspense>
        </LazyPanelBoundary>
      )}
    </div>
  );
}

// ── SupplyOptionsPanel ─────────────────────────────────────────────────────────

function SupplyOptionsPanel({
  options,
}: {
  options: ExperienceSupplyOption[];
}) {
  // DEV-only: grab trace_id from first option (all share the same retrieval trace)
  const traceId = options[0]?.trace_id ?? null;
  const devMode = options[0]?.cached === true ? "cached" : "live";

  return (
    <div
      className="mx-3 sm:mx-4 mb-4 mt-2 rounded-2xl overflow-hidden"
      style={{
        background: "linear-gradient(160deg, oklch(0.990 0.018 76 / 0.97) 0%, oklch(0.986 0.012 68 / 0.97) 100%)",
        border:     "1px solid oklch(0.882 0.042 74 / 0.55)",
        boxShadow:  "0 2px 16px #E8194B17",
      }}
    >
      {/* ── Panel header ────────────────────────────────────────────────── */}
      <div
        className="px-4 sm:px-5 pt-4 pb-3"
        style={{ borderBottom: "1px solid oklch(0.882 0.042 74 / 0.22)" }}
      >
        {/* Title row */}
        <div className="flex items-start justify-between gap-3 mb-1">
          <div className="flex items-center gap-2 min-w-0">
            <span
              className="shrink-0 text-[10px] font-black tracking-[0.26em] uppercase"
              style={{ color: "#E8194B" }}
            >
              Sift Trip
            </span>
            <h4
              className="font-display font-semibold text-[13px] sm:text-[14px] leading-tight truncate"
              style={{ color: "var(--color-heading-navy)" }}
            >
              Experiencias encontradas por Sift Trip
            </h4>
          </div>
          <span
            className="shrink-0 text-[9px] font-bold px-2 py-0.5 rounded-full"
            style={{
              color:      "#E8194B",
              background: "#E8194B1A",
              border:     "1px solid #E8194B47",
              whiteSpace: "nowrap",
            }}
          >
            {options.length} {options.length === 1 ? "opción" : "opciones"}
          </span>
        </div>

        {/* Subtitle */}
        <p
          className="text-[11px] leading-snug"
          style={{ color: "oklch(0.50 0.020 60 / 0.60)" }}
        >
          Opciones externas que coinciden con tu intención de viaje
        </p>

        {/* Semantic priority line */}
        <p
          className="mt-1 text-[10px] font-medium italic"
          style={{ color: "#E8194BB2" }}
        >
          Sift Trip priorizó estas opciones por afinidad semántica con tu viaje.
        </p>
      </div>

      {/* ── Cards ───────────────────────────────────────────────────────── */}
      <div className="px-3 sm:px-4 py-3 flex flex-col gap-2.5">
        {options.map(opt => (
          <SupplyOptionCard key={opt.provider_id} opt={opt} />
        ))}
      </div>

      {/* ── Trust footer ────────────────────────────────────────────────── */}
      <div
        className="px-4 sm:px-5 py-2.5"
        style={{ borderTop: "1px solid oklch(0.882 0.042 74 / 0.18)" }}
      >
        <p
          className="text-[9px] text-center leading-relaxed"
          style={{ color: "oklch(0.545 0.020 60 / 0.42)" }}
        >
          Proveedor externo · Precio y disponibilidad sujetos al proveedor
        </p>

        {/* DEV debug — never shown in production */}
        {import.meta.env.DEV && (
          <p
            className="mt-1 text-[9px] text-center font-mono"
            style={{ color: "oklch(0.545 0.020 60 / 0.30)" }}
          >
            Supply: {options[0]?.provider ?? "?"} · {devMode}
            {traceId ? ` · trace: ${traceId.slice(0, 8)}` : ""}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Image placeholder ────────────────────────────────────────────────────────
//
// Priority: experience image_url -> CATEGORY_VISUALS[category] -> icon gradient
// Each level falls through to the next on onError.

function ImagePlaceholder({
  category,
  imageUrl,
}: {
  category: string;
  imageUrl?: string | null;
}) {
  const palette   = PLACEHOLDER_PALETTE[category] ?? { emoji: "", from: "oklch(0.985 0.010 70)", to: "oklch(0.978 0.008 60)" };
  const catVisual = CATEGORY_VISUALS[category] ?? null;
  const FallbackIcon = CATEGORY_LUCIDE_ICONS[category] ?? MapPin;

  // Initial source: experience URL -> curated category image -> null (icon gradient)
  const [activeSrc, setActiveSrc] = useState<string | null>(
    () => imageUrl || catVisual?.image || null,
  );

  const handleError = () => {
    if (activeSrc === imageUrl && imageUrl && catVisual?.image) {
      setActiveSrc(catVisual.image); // step down: try curated category image
    } else {
      setActiveSrc(null);            // step down: icon gradient fallback
    }
  };

  if (activeSrc) {
    return (
      <img
        src={activeSrc}
        alt=""
        className="shrink-0 w-16 h-16 sm:w-[4.5rem] sm:h-[4.5rem] rounded-xl object-cover"
        style={{ border: "1px solid oklch(0.880 0.024 72 / 0.28)", flexShrink: 0 }}
        loading="lazy"
        onError={handleError}
      />
    );
  }

  // Icon gradient fallback: stable across platforms, no network dependency.
  return (
    <div
      className="shrink-0 w-16 h-16 sm:w-[4.5rem] sm:h-[4.5rem] rounded-xl flex items-center justify-center select-none"
      style={{
        background: `linear-gradient(145deg, ${palette.from} 0%, ${palette.to} 100%)`,
        border:     "1px solid oklch(0.880 0.024 72 / 0.30)",
        flexShrink: 0,
      }}
      aria-hidden
    >
      <FallbackIcon size={24} aria-hidden style={{ color: CATEGORY_META[category]?.color ?? "oklch(0.50 0.06 200)" }} />
    </div>
  );
}

// ─── Teaser section ───────────────────────────────────────────────────────────

function TeaserSection({ onSelect }: { onSelect: (q: string) => void }) {
  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 h-px" style={{ background: "oklch(0.880 0.024 72 / 0.32)" }} />
        <p className="text-[10px] uppercase tracking-[0.20em] font-bold shrink-0"
          style={{ color: "oklch(0.545 0.020 60 / 0.38)" }}>
          Destinos populares
        </p>
        <div className="flex-1 h-px" style={{ background: "oklch(0.880 0.024 72 / 0.32)" }} />
      </div>

      {/* Horizontal scroll row */}
      <div
        className="flex gap-3 overflow-x-auto pb-3 snap-x snap-mandatory"
        style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}
      >
        {TEASER_QUERIES.map((item, i) => (
          <motion.button
            key={item.query}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.40, delay: 0.05 * i, ease: [0.22, 1, 0.36, 1] }}
            type="button"
            onClick={() => { track("teaser_clicked", { city: item.city, query: item.query }); onSelect(item.query); }}
            className="snap-start shrink-0 flex flex-col rounded-2xl overflow-hidden text-left group
                       transition-all duration-200 active:scale-[0.97] hover:scale-[1.02]"
            style={{ width: 152, boxShadow: "0 2px 16px oklch(0 0 0 / 0.10)" }}
          >
            {/* Image / gradient top — photo is optional (see popularDestinations.ts
                module doc): cities with no already-vetted catalog photo render
                gradient-only, the same honest fallback CityCard uses on Home. */}
            <div className="relative overflow-hidden" style={{ height: 108, background: item.bg }}>
              {item.photo && (
                <img
                  src={item.photo}
                  alt={item.city}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-75 transition-opacity duration-300"
                  onError={e => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                />
              )}
              {/* Gradient overlay for text legibility */}
              <div className="absolute inset-0"
                style={{ background: "linear-gradient(180deg, transparent 30%, oklch(0 0 0 / 0.55) 100%)" }} />
              {/* Flag — no day-count label; this is a marketplace teaser, not
                  a trip-length promise (Experiences Popular Destinations
                  Marketplace Alignment V1). */}
              <div className="absolute bottom-2 left-2.5 flex items-center">
                <FlagImg flag={item.flag} size={16} className="rounded-sm shrink-0 select-none" />
              </div>
            </div>

            {/* Text body */}
            <div className="flex-1 px-3 py-2.5"
              style={{ background: "oklch(0.994 0.006 70)", borderTop: "1px solid oklch(0.880 0.024 72 / 0.30)" }}>
              <p className="text-[13px] font-bold leading-tight mb-0.5"
                style={{ color: "oklch(0.22 0.015 60)" }}>
                {item.city}
              </p>
              <p className="text-[10.5px] leading-snug"
                style={{ color: "oklch(0.48 0.018 60 / 0.65)" }}>
                {item.hook}
              </p>
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MULTI-CITY RENDER V1
// ═══════════════════════════════════════════════════════════════════════════════
//
// These components are ONLY rendered when sequence.multi_city_sequence?.mode
// === "multi_city".  Single-city flow is completely unaffected.
//
// Architecture:
//   MultiCityTimeline          — orchestrator, owns expand/collapse state
//     JourneyHero              — editorial route header
//     JourneyProgress          — city breadcrumb row
//     per city:
//       CityChapter            — chapter header + collapsible toggle
//       CitySummaryBlock       — derived themes from experiences
//       DayCard (existing)     — reused unchanged
//     between cities:
//       TransitionCard         — editorial narrative bridge
// ═══════════════════════════════════════════════════════════════════════════════

// ─── City accent palette ──────────────────────────────────────────────────────
// Maps IATA → oklch accent color used for chapter borders, progress dots, etc.

const CITY_ACCENT_MAP: Record<string, string> = {
  TYO: "oklch(0.68 0.18 285)", KYO: "oklch(0.62 0.15 358)", OSA: "oklch(0.70 0.16 52)",
  HKN: "oklch(0.56 0.12 162)", KNZ: "oklch(0.58 0.12 32)",
  LIS: "oklch(0.68 0.13 32)",  OPO: "oklch(0.64 0.13 28)",  CBM: "oklch(0.58 0.12 270)",
  FAO: "oklch(0.60 0.14 195)", DOV: "oklch(0.56 0.13 148)",
  MAD: "oklch(0.62 0.19 27)",  SEV: "oklch(0.64 0.16 45)",  GRA: "oklch(0.56 0.14 148)",
  COR: "oklch(0.58 0.14 44)",  AGP: "oklch(0.58 0.12 206)", CDZ: "oklch(0.56 0.12 200)",
  TOL: "oklch(0.60 0.15 36)",
  AMS: "oklch(0.54 0.10 195)", BGE: "oklch(0.60 0.12 265)",
  ROM: "oklch(0.65 0.14 42)",  FLR: "oklch(0.68 0.14 52)",  VCE: "oklch(0.62 0.16 204)",
  NAP: "oklch(0.62 0.16 36)",  MXP: "oklch(0.52 0.08 235)",
  CDG: "oklch(0.55 0.11 238)", BCN: "oklch(0.72 0.14 50)",  MEX: "oklch(0.72 0.16 52)",
};

// Fallback palette cycling for unknown cities
const FALLBACK_ACCENTS = [
  "oklch(0.62 0.14 280)", "oklch(0.60 0.14 32)", "oklch(0.56 0.12 162)",
  "oklch(0.60 0.14 52)",  "oklch(0.58 0.12 200)",
];

function getCityAccent(iata: string, idx: number): string {
  return CITY_ACCENT_MAP[iata] ?? FALLBACK_ACCENTS[idx % FALLBACK_ACCENTS.length];
}

// ─── Journey Hero ─────────────────────────────────────────────────────────────

function JourneyHero({
  sequence,
}: {
  sequence: ExperienceSequence;
}) {
  const mc = sequence.multi_city_sequence!;
  const cityNames = mc.cities.map(c => c.city_name);

  // Extract route label from summary — "Japón Clásico: 12 días por..." → "JAPÓN CLÁSICO"
  const colonIdx  = mc.summary.indexOf(":");
  const routeLabel =
    colonIdx > 0 && colonIdx < 60
      ? mc.summary.slice(0, colonIdx).toUpperCase()
      : cityNames.join(" · ").toUpperCase();

  const accentA = getCityAccent(mc.cities[0]?.city_iata ?? "", 0);
  const accentB = getCityAccent(mc.cities[1]?.city_iata ?? "", 1);

  return (
    <motion.div
      {...fadeUp(0.06, 0.55)}
      className="relative overflow-hidden rounded-3xl mb-6"
      style={{
        background:  `linear-gradient(145deg, oklch(0.978 0.014 70) 0%, oklch(0.970 0.010 58) 100%)`,
        border:      "1px solid oklch(0.880 0.024 72 / 0.50)",
        boxShadow:   `0 4px 32px ${accentA}18`,
      }}
    >
      {/* Background accent orbs */}
      <div
        className="absolute -top-20 -right-20 w-72 h-72 rounded-full pointer-events-none"
        style={{ background: `radial-gradient(circle, ${accentA}18 0%, transparent 70%)` }}
        aria-hidden
      />
      <div
        className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full pointer-events-none"
        style={{ background: `radial-gradient(circle, ${accentB}14 0%, transparent 70%)` }}
        aria-hidden
      />

      <div className="relative z-10 px-6 sm:px-8 py-7 sm:py-9">
        {/* Label row: RHEA badge + tone chip */}
        <div className="flex items-center gap-3 mb-3">
          <p
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.30em] font-bold"
            style={{ color: accentA + "bb" }}
          >
            <Sparkles size={11} aria-hidden />
            Sift Trip Multi-City Journey
          </p>
          {/* Journey tone chip — subtle, only when tone is available */}
          {sequence.journey_tone && sequence.journey_tone.tone_id !== "classic" && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
              style={{
                background:  accentA + "18",
                color:       accentA + "cc",
                border:      `1px solid ${accentA}30`,
                letterSpacing: "0.04em",
              }}
            >
              {sequence.journey_tone.hero_label}
            </span>
          )}
        </div>

        {/* Route name */}
        <h2
          className="font-serif font-black leading-none tracking-tight mb-1"
          style={{
            fontSize:      "clamp(1.85rem, 6vw, 3rem)",
            color:         "var(--color-heading-navy)",
            letterSpacing: "-0.02em",
          }}
        >
          {routeLabel}
        </h2>

        {/* Hero tagline — from tone or multi_city_seq */}
        {(mc.hero_tagline ?? sequence.journey_tone?.hero_tagline) && (
          <p
            className="text-xs sm:text-sm italic mb-2"
            style={{ color: "oklch(0.42 0.016 58 / 0.55)" }}
          >
            {mc.hero_tagline ?? sequence.journey_tone?.hero_tagline}
          </p>
        )}

        {/* Meta: days · cities */}
        <p
          className="text-sm font-semibold mb-4"
          style={{ color: "oklch(0.545 0.020 60 / 0.60)" }}
        >
          {mc.total_days} días · {mc.cities.length} ciudades
        </p>

        {/* City chain */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {mc.cities.map((seg, idx) => {
            const flag = getCityFlag(seg.city_iata);
            return (
              <span key={seg.city_iata} className="flex items-center gap-2">
                <span
                  className="font-serif font-black text-base sm:text-lg"
                  style={{ color: getCityAccent(seg.city_iata, idx) }}
                >
                  {flag && <span className="mr-1 text-sm not-italic font-normal select-none" aria-hidden>{flag}</span>}
                  {seg.city_name}
                </span>
                {idx < mc.cities.length - 1 && (
                  <span
                    className="text-xs font-light select-none"
                    style={{ color: "oklch(0.545 0.020 60 / 0.35)" }}
                    aria-hidden
                  >
                    ——
                  </span>
                )}
              </span>
            );
          })}
        </div>

        {/* why_this_route — editorial rationale */}
        {mc.why_this_route && (
          <div
            className="mt-3 pl-3 py-2"
            style={{ borderLeft: `2px solid ${accentA}55` }}
          >
            <p
              className="text-xs sm:text-[13px] leading-relaxed italic"
              style={{ color: "oklch(0.38 0.012 55 / 0.62)" }}
            >
              {mc.why_this_route}
            </p>
          </div>
        )}

        {/* route_arc — journey arc narrative (Route Design Engine V2) */}
        {mc.route_arc && (
          <div
            className="mt-4 rounded-xl px-4 py-3"
            style={{
              background: `linear-gradient(135deg, ${accentA}08 0%, ${accentB}06 100%)`,
              border:     `1px solid ${accentA}1e`,
            }}
          >
            <p
              className="text-[10px] uppercase tracking-[0.22em] font-bold mb-1.5"
              style={{ color: accentA + "99" }}
            >
              ✦ El recorrido
            </p>
            <p
              className="font-serif text-sm sm:text-[15px] leading-relaxed"
              style={{
                color:       "oklch(0.28 0.014 55 / 0.72)",
                fontStyle:   "italic",
                maxWidth:    "540px",
              }}
            >
              {mc.route_arc.length > 220
                ? mc.route_arc.slice(0, 218) + "…"
                : mc.route_arc}
            </p>
          </div>
        )}

        {/* Evidence Block — Evidence UI V1
            Only rendered when backend returns evidence_reasoning.
            Graceful empty: no block if field is absent / null. */}
        {mc.evidence_reasoning && (
          <EvidenceBlock
            reasoning={mc.evidence_reasoning}
            accentA={accentA}
            accentB={accentB}
          />
        )}
      </div>
    </motion.div>
  );
}

// ─── Evidence Block ───────────────────────────────────────────────────────────
//
// Renders the evidence_reasoning sentence from the backend, plus client-side
// signal chips parsed from the Spanish label list, and a source attribution line.
//
// Design principles:
//   - Premium editorial — not a dashboard, not a stats panel
//   - Subtle — sits below route_arc without competing with it
//   - Graceful empty — caller guards with {mc.evidence_reasoning && <EvidenceBlock …/>}
//   - Mobile-first expand — text is clamped to 2 lines on small screens,
//     "Ver más" appears if it overflows; desktop always shows full text

/** Spanish label -> display chip (matches runtime.py _ES dict output) */
const _EVIDENCE_LABEL_MAP: Record<string, { icon: LucideIcon; label: string }> = {
  "gastronomia":          { icon: Utensils,    label: "Gastronomía"    },
  "romanticismo":         { icon: Heart,       label: "Romance"         },
  "vino":                 { icon: Sparkles,    label: "Vino"            },
  "cultura":              { icon: Landmark,    label: "Cultura"         },
  "vida nocturna":        { icon: Moon,        label: "Vida nocturna"   },
  "paisaje":              { icon: Leaf,        label: "Naturaleza"      },
  "bienestar":            { icon: Heart,       label: "Bienestar"       },
  "arte":                 { icon: Palette,     label: "Arte"            },
  "arquitectura":         { icon: Building2,   label: "Arquitectura"    },
  "patrimonio":           { icon: Landmark,    label: "Patrimonio"      },
  "musica":               { icon: Music,       label: "Música"          },
  "rincones autenticos":  { icon: Sparkles,    label: "Rincones"        },
  "playas":               { icon: Sun,         label: "Playas"          },
  "moda y diseno":        { icon: ShoppingBag, label: "Moda"            },
  "cultura pop":          { icon: Sparkles,    label: "Cultura Pop"     },
};

type EvidenceChip = { key: string; icon: LucideIcon; label: string };

/**
 * Parse evidence_reasoning string into signal chips.
 *
 * Input:  "Las senales mas fuertes encontradas fueron: gastronomia, romanticismo y vino."
 * Output: [{ key: "gastronomia", icon: Utensils, label: "Gastronomía" }, ...]
 */
function parseEvidenceChips(reasoning: string): EvidenceChip[] {
  const match = reasoning.match(/fueron:\s*(.+?)\.?\s*$/i);
  if (!match) return [];

  // Split on ", " and " y " — handles 1-, 2-, and 3-item Spanish lists
  const tokens = match[1]
    .split(/,\s*|\s+y\s+/)
    .map(s => s.trim().toLowerCase())
    .filter(Boolean);

  return tokens
    .map(tok => {
      const chip = _EVIDENCE_LABEL_MAP[tok];
      return chip ? { key: tok, ...chip } : null;
    })
    .filter((c): c is EvidenceChip => c !== null);
}

function EvidenceBlock({
  reasoning,
  accentA,
  accentB: _accentB,   // reserved for future gradient use
}: {
  reasoning: string;
  accentA:   string;
  accentB:   string;
}) {
  const [expanded,  setExpanded]  = useState(false);
  const [overflows, setOverflows] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  // Detect whether the clamped paragraph actually overflows
  // (only relevant on mobile where line-clamp-2 is applied)
  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    // Temporarily remove clamp to measure true height
    el.style.webkitLineClamp = "none";
    const full    = el.scrollHeight;
    el.style.webkitLineClamp = "";
    const clamped = el.clientHeight;
    setOverflows(full > clamped + 2);
  }, [reasoning]);

  const chips = parseEvidenceChips(reasoning);

  return (
    <div
      className="mt-4 rounded-xl px-4 py-3.5"
      style={{
        background: "oklch(0.992 0.006 70 / 0.72)",
        border:     "1px solid oklch(0.880 0.024 72 / 0.38)",
      }}
    >
      {/* ── Header: evidence icon + title ────────────────────────────────── */}
      <div className="flex items-center gap-1.5 mb-2">
        {/* Concentric-circle evidence icon — minimal, not technical */}
        <svg
          width="12" height="12" viewBox="0 0 12 12" fill="none"
          aria-hidden
          style={{ color: accentA + "99", flexShrink: 0 }}
        >
          <circle cx="6" cy="6" r="5"   stroke="currentColor" strokeWidth="1.0" />
          <circle cx="6" cy="6" r="2.5" stroke="currentColor" strokeWidth="0.8" />
          <circle cx="6" cy="6" r="0.9" fill="currentColor" />
        </svg>
        <p
          className="text-[10px] uppercase tracking-[0.20em] font-bold"
          style={{ color: accentA + "88" }}
        >
          Por qué esta selección tiene sentido
        </p>
      </div>

      {/* ── Body text ─────────────────────────────────────────────────────── */}
      {/* Desktop: always full text.  Mobile: clamp-2 + optional expand. */}
      <p
        ref={textRef}
        className={[
          "text-xs sm:text-[13px] leading-relaxed",
          "sm:overflow-visible sm:[display:block]",  // sm+ always unclamped
          !expanded ? "line-clamp-2 sm:line-clamp-none" : "",
        ].join(" ")}
        style={{ color: "oklch(0.38 0.012 55 / 0.68)" }}
      >
        {reasoning}
      </p>

      {/* "Ver más" toggle — mobile only, only when text actually overflows */}
      {overflows && !expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="mt-1 text-[10px] font-semibold sm:hidden transition-opacity hover:opacity-70"
          style={{ color: accentA + "99" }}
        >
          Ver más ↓
        </button>
      )}

      {/* ── Signal chips ──────────────────────────────────────────────────── */}
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2.5" role="list" aria-label="Señales detectadas">
          {chips.map(chip => {
            const EvidenceIcon = chip.icon;
            return (
              <span
                key={chip.key}
                role="listitem"
                className="inline-flex items-center gap-1 text-[10px] font-semibold px-2.5 py-1 rounded-full"
                style={{
                  color:      accentA + "cc",
                  background: accentA + "0d",
                  border:     `1px solid ${accentA}28`,
                }}
              >
                <EvidenceIcon size={10} aria-hidden />
                {chip.label}
              </span>
            );
          })}
        </div>
      )}

      {/* ── Source attribution ─────────────────────────────────────────────── */}
      <p
        className="mt-2.5 text-[10px]"
        style={{ color: "oklch(0.545 0.020 60 / 0.36)" }}
      >
        Señales combinadas de conocimiento Sift Trip + fuentes abiertas
      </p>
    </div>
  );
}


// ─── Journey Progress ─────────────────────────────────────────────────────────

function JourneyProgress({
  cities,
  activeIata,
}: {
  cities:     CityExperienceSegment[];
  activeIata: string | null;
}) {
  return (
    <motion.div
      {...fadeUp(0.10, 0.45)}
      className="mb-7 px-1"
      role="navigation"
      aria-label="Progreso del viaje"
    >
      <div className="flex items-center gap-0 overflow-x-auto pb-1 scrollbar-none">
        {cities.map((seg, idx) => {
          const accent  = getCityAccent(seg.city_iata, idx);
          const isActive = activeIata === seg.city_iata;
          const isPast   = activeIata
            ? cities.findIndex(c => c.city_iata === activeIata) > idx
            : false;
          return (
            <div key={seg.city_iata} className="flex items-center shrink-0">
              {/* City pill */}
              <div className="flex flex-col items-center gap-1">
                {/* Dot */}
                <div
                  className="w-2 h-2 rounded-full transition-all duration-300"
                  style={{
                    background: isActive || isPast ? accent : `${accent}44`,
                    boxShadow:  isActive ? `0 0 0 3px ${accent}22` : "none",
                    transform:  isActive ? "scale(1.3)" : "scale(1)",
                  }}
                />
                {/* Name */}
                <span
                  className="text-[10px] font-semibold whitespace-nowrap px-1"
                  style={{
                    color:      isActive ? accent : isPast ? `${accent}88` : "oklch(0.545 0.020 60 / 0.45)",
                    fontWeight: isActive ? 700 : 500,
                  }}
                >
                  {seg.city_name}
                </span>
                {/* Days sub-label */}
                <span
                  className="text-[9px]"
                  style={{ color: "oklch(0.545 0.020 60 / 0.30)" }}
                >
                  {seg.days}d
                </span>
              </div>

              {/* Connector line between cities */}
              {idx < cities.length - 1 && (
                <div
                  className="flex-1 h-px mx-2 shrink-0"
                  style={{
                    width:      "28px",
                    background: isPast ? accent + "66" : "oklch(0.880 0.024 72 / 0.35)",
                    transition: "background 0.3s ease",
                  }}
                  aria-hidden
                />
              )}
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}

// ─── City Chapter Header ──────────────────────────────────────────────────────

function CityChapter({
  segment,
  chapterIndex,
  isExpanded,
  onToggle,
}: {
  segment:      CityExperienceSegment;
  chapterIndex: number;
  isExpanded:   boolean;
  onToggle:     () => void;
}) {
  const accent   = getCityAccent(segment.city_iata, chapterIndex);
  const meta     = CITY_HERO_META[segment.city_iata];
  const visual   = CITY_VISUALS[segment.city_iata] ?? null;
  const chapterN = String(chapterIndex + 1).padStart(2, "0");
  const [imgOk, setImgOk] = useState(false);

  return (
    <div
      className="relative overflow-hidden rounded-2xl mb-4"
      style={{
        background: meta?.gradient ??
          `linear-gradient(145deg, oklch(0.982 0.014 70) 0%, oklch(0.972 0.010 58) 100%)`,
        border:     `1px solid ${accent}28`,
        boxShadow:  `0 2px 20px ${accent}10`,
      }}
    >
      {/* Photo background (thumbnail) — only when available */}
      {visual && (
        <>
          <img
            src={visual.thumbnailImage}
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-center"
            style={{ opacity: imgOk ? 0.18 : 0, transition: "opacity 0.7s ease" }}
            loading="lazy"
            onLoad={() => setImgOk(true)}
            onError={() => setImgOk(false)}
            aria-hidden
          />
          {imgOk && (
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: `linear-gradient(135deg, ${accent}12 0%, oklch(0.988 0.008 70 / 0.70) 100%)`,
              }}
              aria-hidden
            />
          )}
        </>
      )}

      {/* Ambient orb */}
      <div
        className="absolute -top-10 -right-10 w-40 h-40 rounded-full pointer-events-none"
        style={{ background: `radial-gradient(circle, ${accent}16 0%, transparent 70%)` }}
        aria-hidden
      />

      <button
        type="button"
        onClick={onToggle}
        className="relative z-10 w-full text-left px-5 sm:px-7 py-5 sm:py-6"
        aria-expanded={isExpanded}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4 min-w-0">
            {/* Chapter number */}
            <div
              className="shrink-0 font-serif font-black leading-none select-none"
              style={{
                fontSize:   "clamp(2rem, 7vw, 3.5rem)",
                color:      `${accent}30`,
                lineHeight: 1,
                marginTop:  "2px",
              }}
              aria-hidden
            >
              {chapterN}
            </div>

            {/* City info */}
            <div className="min-w-0">
              <p
                className="text-[10px] uppercase tracking-[0.28em] font-bold mb-1"
                style={{ color: `${accent}aa` }}
              >
                {segment.country}
              </p>
              <h3
                className="font-serif font-black leading-none tracking-tight"
                style={{
                  fontSize:      "clamp(1.5rem, 5.5vw, 2.5rem)",
                  color:         "var(--color-heading-navy)",
                  letterSpacing: "-0.02em",
                }}
              >
                {meta?.heroName ?? segment.city_name.toUpperCase()}
              </h3>
              <p
                className="mt-1 text-xs font-semibold"
                style={{ color: `${accent}99` }}
              >
                {segment.days} {segment.days === 1 ? "día" : "días"}
                {segment.city_type === "stopover_city" && (
                  <span style={{ color: "oklch(0.545 0.020 60 / 0.45)" }}> · escala</span>
                )}
              </p>
            </div>
          </div>

          {/* Collapse toggle */}
          <div
            className="shrink-0 mt-1 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300"
            style={{
              background: `${accent}10`,
              border:     `1px solid ${accent}28`,
              color:      accent,
              transform:  isExpanded ? "rotate(180deg)" : "rotate(0deg)",
            }}
            aria-hidden
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>

        {/* Accent divider */}
        <div
          className="mt-4 h-px"
          style={{
            background: `linear-gradient(to right, ${accent}60 0%, ${accent}18 60%, transparent 100%)`,
          }}
          aria-hidden
        />
      </button>
    </div>
  );
}

// ─── City Summary Block ───────────────────────────────────────────────────────
// Derives the top themes from a city segment's experiences.

function CitySummaryBlock({
  segment,
  accent,
}: {
  segment: CityExperienceSegment;
  accent:  string;
}) {
  // Tally category occurrences across all days in this segment
  const counts: Record<string, number> = {};
  segment.experiences.forEach(day => {
    day.experiences.forEach(exp => {
      counts[exp.category] = (counts[exp.category] ?? 0) + 1;
    });
  });

  // Sort by frequency, take top 4
  const topCategories = Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4)
    .map(([cat]) => cat);

  if (topCategories.length === 0) return null;

  return (
    <div className="mb-4 px-1">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className="text-[9px] uppercase tracking-[0.20em] font-bold shrink-0"
          style={{ color: `${accent}80` }}
        >
          En esta ciudad
        </span>
        <div className="flex-1 h-px" style={{ background: `${accent}22` }} aria-hidden />
        {topCategories.map(cat => {
          const meta = CATEGORY_META[cat];
          const CatIc = CATEGORY_LUCIDE_ICONS[cat] ?? Sparkles;
          return (
            <span
              key={cat}
              className="inline-flex items-center gap-1 text-[10px] font-semibold px-2.5 py-1 rounded-full"
              style={{
                color:      meta?.color ?? accent,
                background: `${(meta?.color ?? accent)}10`,
                border:     `1px solid ${(meta?.color ?? accent)}28`,
              }}
            >
              <CatIc size={10} aria-hidden />
              {meta?.label ?? cat}
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ─── Transition Card ──────────────────────────────────────────────────────────

function TransitionCard({
  transition,
  fromAccent,
  toAccent,
}: {
  transition: TransitionNarrative;
  fromAccent: string;
  toAccent:   string;
}) {
  if (!transition.title && !transition.intro) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className="relative overflow-hidden rounded-2xl my-6"
      style={{
        background: `linear-gradient(135deg,
          ${fromAccent}0a 0%,
          oklch(0.985 0.012 70 / 0.95) 35%,
          oklch(0.985 0.012 70 / 0.95) 65%,
          ${toAccent}0a 100%)`,
        border:    `1px solid oklch(0.880 0.024 72 / 0.42)`,
        boxShadow: "0 2px 20px oklch(0.40 0.012 50 / 0.06)",
      }}
    >
      {/* Decorative large quote mark */}
      <span
        className="absolute top-0 left-4 font-serif font-black select-none pointer-events-none leading-none"
        style={{
          fontSize: "7rem",
          color:    "oklch(0.880 0.024 72 / 0.22)",
          lineHeight: 0.8,
        }}
        aria-hidden
      >
        "
      </span>

      {/* Left accent strip */}
      <div
        className="absolute left-0 top-0 bottom-0 w-[3px] rounded-l-2xl"
        style={{
          background: `linear-gradient(to bottom, ${fromAccent}80 0%, ${toAccent}80 100%)`,
        }}
        aria-hidden
      />

      <div className="relative z-10 px-6 sm:px-8 py-6 sm:py-7 pl-7 sm:pl-9">

        {/* City route label */}
        <div className="flex items-center gap-2 mb-4">
          <span
            className="text-[11px] font-bold"
            style={{ color: fromAccent }}
          >
            {transition.from_city}
          </span>
          <svg width="16" height="8" viewBox="0 0 16 8" fill="none" aria-hidden>
            <path d="M0 4h14M11 1l3 3-3 3" stroke={`${toAccent}88`} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span
            className="text-[11px] font-bold"
            style={{ color: toAccent }}
          >
            {transition.to_city}
          </span>
        </div>

        {/* Title */}
        {transition.title && (
          <h4
            className="font-serif font-black leading-snug tracking-tight mb-1"
            style={{
              fontSize:      "clamp(1.1rem, 4vw, 1.6rem)",
              color:         "var(--color-heading-navy)",
              letterSpacing: "-0.015em",
            }}
          >
            {transition.title}
          </h4>
        )}

        {/* Subtitle */}
        {transition.subtitle && (
          <p
            className="text-xs sm:text-[13px] font-medium italic mb-4"
            style={{ color: "oklch(0.545 0.020 60 / 0.62)" }}
          >
            {transition.subtitle}
          </p>
        )}

        {/* Intro */}
        {transition.intro && (
          <p
            className="text-xs sm:text-[13px] leading-relaxed mb-3"
            style={{ color: "oklch(0.28 0.010 55 / 0.70)" }}
          >
            {transition.intro}
          </p>
        )}

        {/* Momento */}
        {transition.moment && (
          <div
            className="mt-3 pl-3 pt-0.5"
            style={{ borderLeft: `2px solid ${toAccent}50` }}
          >
            <p
              className="text-[10px] uppercase tracking-[0.18em] font-bold mb-1"
              style={{ color: toAccent + "aa" }}
            >
              ✦ Momento del paso
            </p>
            <p
              className="text-[11px] sm:text-xs leading-relaxed italic"
              style={{ color: "oklch(0.28 0.010 55 / 0.55)" }}
            >
              {transition.moment}
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ─── Multi-City Timeline ──────────────────────────────────────────────────────
// Main orchestrator. Renders the full multi-city journey in order:
//   JourneyHero → JourneyProgress → (CityChapter + days + TransitionCard) × N

function MultiCityTimeline({ sequence }: { sequence: ExperienceSequence }) {
  const mc = sequence.multi_city_sequence!;

  // All cities expanded by default
  const [expandedCities, setExpandedCities] = useState<Set<string>>(
    () => new Set(mc.cities.map(c => c.city_iata)),
  );

  // Track which city is "active" for JourneyProgress
  // For V1, just mark the first city that is still visible (simplest: track first one)
  const [activeIata, setActiveIata] = useState<string | null>(
    mc.cities[0]?.city_iata ?? null,
  );

  // Chapter refs for intersection tracking
  const chapterRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  useEffect(() => {
    if (!("IntersectionObserver" in window)) return;
    const obs = new IntersectionObserver(
      entries => {
        // Set active to the last visible chapter (topmost in viewport)
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveIata(entry.target.getAttribute("data-iata"));
          }
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -60% 0px" },
    );
    chapterRefs.current.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, [mc.cities]);

  const toggleCity = (iata: string) =>
    setExpandedCities(prev => {
      const next = new Set(prev);
      if (next.has(iata)) { next.delete(iata); } else { next.add(iata); }
      return next;
    });

  return (
    <div>
      {/* Journey Hero */}
      <JourneyHero sequence={sequence} />

      {/* Journey Progress */}
      <JourneyProgress cities={mc.cities} activeIata={activeIata} />

      {/* City chapters */}
      {mc.cities.map((segment, idx) => {
        const isExpanded = expandedCities.has(segment.city_iata);
        const accent     = getCityAccent(segment.city_iata, idx);

        // Transition narrative AFTER this city (idx = 0 → transition between cities[0] and cities[1])
        const transition = mc.transition_narratives[idx] as TransitionNarrative | undefined;
        const nextAccent = idx < mc.cities.length - 1
          ? getCityAccent(mc.cities[idx + 1].city_iata, idx + 1)
          : accent;

        return (
          <div
            key={segment.city_iata}
            ref={el => {
              if (el) chapterRefs.current.set(segment.city_iata, el);
              else    chapterRefs.current.delete(segment.city_iata);
            }}
            data-iata={segment.city_iata}
          >
            {/* Chapter header */}
            <motion.div
              {...fadeUp(0.04 * idx, 0.50)}
            >
              <CityChapter
                segment={segment}
                chapterIndex={idx}
                isExpanded={isExpanded}
                onToggle={() => toggleCity(segment.city_iata)}
              />
            </motion.div>

            {/* Chapter body — collapsible */}
            <AnimatePresence initial={false}>
              {isExpanded && (
                <motion.div
                  key="chapter-body"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{   opacity: 0, height: 0 }}
                  transition={{ duration: 0.40, ease: [0.22, 1, 0.36, 1] }}
                  style={{ overflow: "hidden" }}
                >
                  {/* Theme summary */}
                  <CitySummaryBlock segment={segment} accent={accent} />

                  {/* Day timeline for this city */}
                  <div className="relative mb-4">
                    {/* Vertical accent line */}
                    <div
                      className="absolute left-[20px] top-8 bottom-8 w-px pointer-events-none"
                      style={{
                        background: `linear-gradient(to bottom,
                          ${accent}00,
                          ${accent}35 12%,
                          ${accent}35 88%,
                          ${accent}00)`,
                      }}
                      aria-hidden
                    />

                    {segment.experiences.map((day, di) => (
                      <motion.div
                        key={day.day_number}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 0.45,
                          delay:    0.05 + di * 0.06,
                          ease:     [0.22, 1, 0.36, 1],
                        }}
                        className="mb-5"
                      >
                        <DayCard day={day} isExpanded={day.day_number % 2 === 0} cityDisplay={segment.city_name} />
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Transition card — between this city and the next */}
            {transition && idx < mc.cities.length - 1 && (
              <TransitionCard
                transition={transition}
                fromAccent={accent}
                toAccent={nextAccent}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── RecentSearchStrip — max 3 dismissible chips ─────────────────────────────

function RecentSearchStrip({
  searches,
  onSelect,
  onRemove,
}: {
  searches: string[];
  onSelect: (q: string) => void;
  onRemove: (q: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2" role="list" aria-label="Búsquedas recientes">
      {searches.slice(0, 3).map(q => (
        <div
          key={q}
          role="listitem"
          className="inline-flex items-center rounded-xl overflow-hidden"
          style={{
            background:  "oklch(0.994 0.008 70 / 0.92)",
            border:      "1px solid oklch(0.880 0.024 72 / 0.42)",
            boxShadow:   "0 1px 4px oklch(0 0 0 / 0.04)",
          }}
        >
          <button
            type="button"
            onClick={() => onSelect(q)}
            className="flex items-center gap-1.5 pl-2.5 pr-2 py-1.5 text-left transition-colors"
            style={{ maxWidth: 220 }}
          >
            <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden>
              <circle cx="5" cy="5" r="3.5" stroke="oklch(0.545 0.020 60 / 0.40)" strokeWidth="1.3" />
              <path d="M8 8l2.2 2.2" stroke="oklch(0.545 0.020 60 / 0.40)" strokeWidth="1.3" strokeLinecap="round" />
            </svg>
            <span className="text-[11px] font-medium truncate" style={{ color: "oklch(0.28 0.016 55)" }}>
              {q}
            </span>
          </button>
          <button
            type="button"
            onClick={() => onRemove(q)}
            aria-label={`Eliminar: ${q}`}
            className="px-2 py-1.5 transition-opacity hover:opacity-60 border-l"
            style={{ borderColor: "oklch(0.880 0.024 72 / 0.32)" }}
          >
            <svg width="9" height="9" viewBox="0 0 9 9" fill="none" aria-hidden>
              <path d="M1.5 1.5l6 6M7.5 1.5l-6 6" stroke="oklch(0.545 0.020 60 / 0.38)" strokeWidth="1.3" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      ))}
    </div>
  );
}

// ─── Profile insight card — compact single-row hint ──────────────────────────

function ProfileInsightCard({
  insight,
  onAccept,
  onDismiss,
}: {
  insight:   ProfileInsight;
  onAccept:  () => void;
  onDismiss: () => void;
}) {
  return (
    <div
      className="flex items-center gap-2.5 rounded-xl px-3 py-2"
      style={{
        background: "#E8194B12",
        border:     "1px solid #E8194B38",
      }}
    >
      <span className="shrink-0 text-[10px] font-bold" style={{ color: "#E8194BB2" }} aria-hidden>
        ✦
      </span>
      <p className="flex-1 min-w-0 text-[11px] leading-snug truncate" style={{ color: "oklch(0.35 0.015 55 / 0.75)" }}>
        {insight.text}
      </p>
      <button
        type="button"
        onClick={onAccept}
        className="shrink-0 text-[10px] font-bold px-2 py-1 rounded-lg transition-opacity hover:opacity-80"
        style={{
          color:      "#E8194B",
          background: "#E8194B1F",
          border:     "1px solid #E8194B47",
        }}
      >
        Aplicar
      </button>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Cerrar sugerencia"
        className="shrink-0 text-xs transition-opacity hover:opacity-60"
        style={{ color: "oklch(0.545 0.020 60 / 0.38)" }}
      >
        ×
      </button>
    </div>
  );
}
